import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Banknote, ChevronRight, ChevronLeft, ChevronDown, ChevronUp, Download, List, CheckCircle, Layers, ArrowRightLeft, History, AlertCircle, TrendingUp, Clock, Wallet, Calendar, HelpCircle, Check, CircleCheck, RefreshCw, Loader2 } from 'lucide-react';
import { Tooltip } from 'react-tooltip';
import DetailItem from '../../Common/components/common/DetailItem';
import DetailGrid from '../../Common/components/common/DetailGrid';
import { useToast } from '../../Common/context/ToastContext';
import { useTooltip } from '../../Common/context/TooltipContext';
import { ADMIN_CP_PAYOUT_TENANT_ENABLED, ADMIN_GASYSTEM_EXTERNAL_SETTLEMENT_ENABLED } from '../../Common/config/menuConfig';
import { downloadBlob } from '../../Common/api/client';
import axios from 'axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const normalizedSettlementStatus = (s) => String(s?.status || '').toUpperCase();

// 입금 실패 여부 (status가 TRANSFER_FAILED이면 실패건 → 선택/입금 불가)
const isTransferFailed = (s) =>
  normalizedSettlementStatus(s) === 'TRANSFER_FAILED';

// phPay 지급대행 요청이 등록되어 결과를 기다리는 상태
const isPayoutTransferring = (s) =>
  ADMIN_CP_PAYOUT_TENANT_ENABLED && normalizedSettlementStatus(s) === 'TRANSFERRING';

const isKgInicisPayoutSettlement = (s) =>
  ADMIN_CP_PAYOUT_TENANT_ENABLED &&
  String(s?.pgProvider || '').trim().toUpperCase() === 'KG_INICIS';

// phPay는 READY만 지급 요청 가능. ntoss는 기존 정책(실패건만 제외)을 유지한다.
const isPayoutSelectable = (s) =>
  ADMIN_CP_PAYOUT_TENANT_ENABLED
    ? normalizedSettlementStatus(s) === 'READY'
    : !isTransferFailed(s);

const formatPayoutAcceptedAt = (value) => {
  if (!value) return '';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY.MM.DD. HH:mm:ss') : String(value);
};

const getPayoutRequestDetail = (s) => [
  String(s?.payoutStatus || '').toUpperCase() === 'ACCEPTED'
    ? '요청 접수 완료'
    : s?.payoutStatus,
  s?.payoutProviderCode,
  formatPayoutAcceptedAt(s?.payoutAcceptedAt),
].filter(Boolean).join(' · ');

// phPay 입금 처리 내역은 정산 상태를 1차 기준으로 표시한다.
// payout_status는 TRANSFERRING 내에서 접수/확인 중을 구분하는 보조 정보로만 사용한다.
const getDepositProcessStatus = (item) => {
  const status = String(item?.status || '').trim().toUpperCase();
  const payoutStatus = String(item?.payoutStatus || item?.payout_status || '').trim().toUpperCase();

  if (item?.externalSettlementMode) {
    const externalStatusMap = {
      PENDING: { label: '정산대기', badgeClass: 'border-slate-200 bg-slate-50 text-slate-700', iconClass: 'text-slate-500' },
      RECEIVE_PENDING: { label: '정산 확인 중', badgeClass: 'border-blue-200 bg-blue-50 text-blue-700', iconClass: 'text-blue-500' },
      RECEIVE_FAILED: { label: '정산 확인 실패', badgeClass: 'border-red-200 bg-red-50 text-red-700', iconClass: 'text-red-500' },
      RECEIVED: { label: '정산 확인 완료', badgeClass: 'border-emerald-200 bg-emerald-50 text-emerald-700', iconClass: 'text-emerald-500' },
      READY: { label: '입금대기', detailLabel: '지급예정일 자동 완료', badgeClass: 'border-amber-200 bg-amber-50 text-amber-700', iconClass: 'text-amber-500' },
      TRANSFERRING: { label: '외부 정산 확인 중', badgeClass: 'border-blue-200 bg-blue-50 text-blue-700', iconClass: 'text-blue-500' },
      TRANSFER_FAILED: { label: '외부 정산 확인 필요', badgeClass: 'border-red-200 bg-red-50 text-red-700', iconClass: 'text-red-500' },
      TRANSFERRED: { label: '정산완료', detailLabel: '외부 정산 일정에 따라 완료', badgeClass: 'border-emerald-200 bg-emerald-50 text-emerald-700', iconClass: 'text-emerald-500' },
      MANUAL_REQUIRED: { label: '관리자 확인 필요', badgeClass: 'border-orange-200 bg-orange-50 text-orange-700', iconClass: 'text-orange-500' },
    };
    const externalMeta = externalStatusMap[status];
    if (externalMeta) return { key: status, ...externalMeta };
  }

  if (status === 'TRANSFERRED') {
    return {
      key: 'TRANSFERRED',
      label: '입금 완료',
      badgeClass: 'border-green-200 bg-green-50 text-green-700',
      iconClass: 'text-green-500',
    };
  }
  if (status === 'TRANSFER_FAILED') {
    return {
      key: 'TRANSFER_FAILED',
      label: '입금 실패',
      badgeClass: 'border-red-200 bg-red-50 text-red-700',
      iconClass: 'text-red-500',
    };
  }
  if (status === 'TRANSFERRING' && payoutStatus === 'ACCEPTED') {
    return {
      key: 'TRANSFERRING_ACCEPTED',
      label: '입금 대기',
      detailLabel: '지급 요청 접수',
      badgeClass: 'border-amber-200 bg-amber-50 text-amber-700',
      iconClass: 'text-amber-500',
    };
  }
  if (status === 'TRANSFERRING') {
    return {
      key: 'TRANSFERRING_CONFIRMING',
      label: '결과 확인 중',
      detailLabel: ['CONFIRMING', 'UNKNOWN'].includes(payoutStatus) ? '지급 결과 확인 중' : '',
      badgeClass: 'border-blue-200 bg-blue-50 text-blue-700',
      iconClass: 'text-blue-500',
    };
  }

  return {
    key: status || 'UNKNOWN',
    label: status || '상태 확인 필요',
    badgeClass: 'border-slate-200 bg-slate-50 text-slate-600',
    iconClass: 'text-slate-400',
  };
};

const getDepositStatusTitle = (item) => [
  getDepositProcessStatus(item).detailLabel,
  item?.payoutProviderCode,
  item?.payoutProviderMessage,
].filter(Boolean).join(' · ');

const getDepositStatusSummaries = (items = []) => {
  const summaries = new Map();
  items.forEach((item) => {
    const meta = getDepositProcessStatus(item);
    const current = summaries.get(meta.key);
    summaries.set(meta.key, current ? { ...current, count: current.count + 1 } : { ...meta, count: 1 });
  });
  return Array.from(summaries.values());
};

const DepositStatusBadge = ({ item, count, showDetail = false }) => {
  const meta = getDepositProcessStatus(item);
  const title = getDepositStatusTitle(item);
  return (
    <span className="inline-flex min-w-0 flex-col items-start gap-0.5" title={title || undefined}>
      <span className={`inline-flex whitespace-nowrap rounded-full border px-2 py-0.5 text-[10px] font-bold ${meta.badgeClass}`}>
        {meta.label}{count ? ` ${count}건` : ''}
      </span>
      {showDetail && meta.detailLabel && (
        <span className="max-w-full truncate text-[9px] font-medium text-slate-500">{meta.detailLabel}</span>
      )}
      {showDetail && item?.payoutProviderCode && (
        <span className="max-w-full truncate text-[9px] text-slate-400">{item.payoutProviderCode}</span>
      )}
      {item?.externalSettlementMode && String(item?.transferRef || '').toUpperCase() === 'EXTERNAL_SCHEDULED' && (
        <span className="inline-flex whitespace-nowrap rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[9px] font-bold text-emerald-700">외부 정산 완료</span>
      )}
    </span>
  );
};

const formatPayoutAmount = (value) => {
  const raw = String(value ?? '').trim().replace(/,/g, '');
  if (/^-?\d+$/.test(raw)) {
    try {
      return window.BigInt(raw).toLocaleString('ko-KR');
    } catch (_) {
      // BigInt 변환 실패 시 원문을 사용한다.
    }
  }
  return raw || '0';
};

const sumPayoutSettlementAmounts = (rows = []) => {
  const rawAmounts = rows.map((row) => String(
    row?.settleAmountRaw
    ?? row?.settleAmountNum
    ?? row?.settleAmount
    ?? 0,
  ).replace(/,/g, '').trim());
  if (rawAmounts.every((amount) => /^-?\d+$/.test(amount))) {
    try {
      return rawAmounts.reduce((sum, amount) => sum + window.BigInt(amount), 0n).toLocaleString('ko-KR');
    } catch (_) {
      // Number 합산으로 대체한다.
    }
  }
  return rows.reduce((sum, row) => (
    sum + Number(row?.settleAmountNum ?? (String(row?.settleAmount || '0').replace(/,/g, '') || 0))
  ), 0).toLocaleString('ko-KR');
};

const formatPayoutBatchDate = (value) => {
  const raw = String(value || '').trim();
  if (/^\d{8}$/.test(raw)) return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
  const dateOnly = raw.match(/^\d{4}-\d{2}-\d{2}/)?.[0];
  return dateOnly || raw.split(/[T\s]/)[0] || '-';
};

const getPayoutBatchStatus = (value) => {
  const status = String(value || '').trim().toUpperCase();
  const map = {
    REQUESTED: { label: '지급 신청 중', className: 'border-slate-200 bg-slate-50 text-slate-700' },
    ACCEPTED: { label: '지급 신청 접수', className: 'border-amber-200 bg-amber-50 text-amber-700' },
    CONFIRMING: { label: '송금결과 확인 중', className: 'border-blue-200 bg-blue-50 text-blue-700' },
    SUCCESS: { label: '입금 완료', className: 'border-green-200 bg-green-50 text-green-700' },
    FAILED: { label: '지급 신청 실패', className: 'border-red-200 bg-red-50 text-red-700' },
    UNKNOWN: { label: '처리 결과 확인 필요', className: 'border-orange-200 bg-orange-50 text-orange-700' },
    CANCELED: { label: '지급 신청 취소', className: 'border-slate-200 bg-slate-100 text-slate-600' },
  };
  return { status, ...(map[status] || { label: status || '상태 확인 필요', className: 'border-slate-200 bg-slate-50 text-slate-600' }) };
};

const normalizePayoutBatch = (batch, index = 0) => ({
  batchId: batch?.payout_batch_id ?? batch?.batch_id ?? batch?.batchId ?? batch?.id ?? `PB-${index}`,
  cpId: batch?.cp_id ?? batch?.cpId ?? '',
  cpName: batch?.cp_name ?? batch?.cpName ?? batch?.mall_name ?? batch?.mallName ?? '',
  idMall: batch?.id_mall ?? batch?.idMall ?? '',
  payDate: batch?.pay_date ?? batch?.payDate ?? '',
  totalAmount: String(batch?.total_amount ?? batch?.amount ?? batch?.totalAmount ?? '0'),
  status: batch?.status ?? '',
  providerCode: batch?.provider_code ?? batch?.providerCode ?? '',
  providerMessage: batch?.provider_message ?? batch?.providerMessage ?? batch?.message ?? '',
  items: (Array.isArray(batch?.items) ? batch.items : []).map((item, itemIndex) => ({
    settlementId: item?.settlement_id ?? item?.settlementId ?? item?.id ?? `ITEM-${itemIndex}`,
    amount: String(item?.amount ?? '0'),
    paymentDate:
      item?.payment_date
      ?? item?.paymentDate
      ?? item?.approved_date
      ?? item?.approvedDate
      ?? item?.settlement_date
      ?? item?.settlementDate
      ?? '',
    itemStatus: item?.item_status ?? item?.itemStatus ?? '',
    settlementStatus: item?.settlement_status ?? item?.settlementStatus ?? '',
  })),
});

const canCheckPayoutBatchResult = (status) =>
  ['ACCEPTED', 'CONFIRMING', 'UNKNOWN'].includes(String(status || '').toUpperCase());

const PayoutBatchHistory = ({
  batches,
  expandedIds,
  onToggle,
  checkingIds,
  onCheckResult,
  settlementPaymentDates,
}) => {
  if (!batches.length) return null;
  return (
    <div className="mb-4 max-h-[280px] overflow-y-auto rounded-xl border-2 border-amber-300 bg-amber-50/40 shadow-sm custom-scrollbar">
      <div className="flex items-center justify-between border-b border-amber-200 bg-amber-100 px-4 py-3">
        <div>
          <p className="text-[13px] font-black text-amber-950">KG이니시스 지급 처리 중·확인 필요</p>
          <p className="text-[10px] text-slate-500">처리 중이거나 결과 확인이 필요한 합산 지급만 표시합니다.</p>
        </div>
        <span className="rounded-full bg-amber-600 px-2.5 py-1 text-[11px] font-black text-white">{batches.length}건</span>
      </div>
      <div className="divide-y divide-amber-100">
        {batches.map((batch) => {
          const key = String(batch.batchId);
          const expanded = expandedIds.has(`payout-batch-${key}`);
          const statusMeta = getPayoutBatchStatus(batch.status);
          const checking = checkingIds.has(key);
          return (
            <div key={key} className="border-l-4 border-amber-400 bg-white">
              <div className="flex items-center gap-3 px-4 py-3">
                <button
                  type="button"
                  onClick={() => onToggle(`payout-batch-${key}`)}
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded border border-slate-200 text-slate-500 hover:bg-slate-50"
                  aria-label={expanded ? '구성 정산 접기' : '구성 정산 펼치기'}
                >
                  {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="rounded bg-amber-500 px-1.5 py-0.5 text-[9px] font-black text-white">합산 지급</span>
                    <span className="truncate text-[12px] font-bold text-slate-800">{batch.cpName || batch.idMall || '-'}</span>
                    {batch.idMall && batch.cpName && <span className="text-[10px] text-slate-400">{batch.idMall}</span>}
                    <span className="rounded-md bg-amber-50 px-2 py-0.5 text-[11px] font-bold tabular-nums text-amber-800">
                      지급일 {formatPayoutBatchDate(batch.payDate)}
                    </span>
                  </div>
                  {['FAILED', 'UNKNOWN'].includes(statusMeta.status) && (batch.providerCode || batch.providerMessage) && (
                    <p className="mt-0.5 truncate text-[10px] text-red-500" title={batch.providerMessage || undefined}>
                      {[batch.providerCode, batch.providerMessage].filter(Boolean).join(' · ')}
                    </p>
                  )}
                </div>
                <span className="shrink-0 text-[15px] font-black tabular-nums text-amber-900">
                  {formatPayoutAmount(batch.totalAmount)}원
                </span>
                <span className={`shrink-0 rounded-full border px-2 py-1 text-[10px] font-bold ${statusMeta.className}`}>
                  {statusMeta.label}
                </span>
                {canCheckPayoutBatchResult(batch.status) && (
                  <button
                    type="button"
                    disabled={checking}
                    onClick={() => onCheckResult(batch.batchId)}
                    className="inline-flex h-7 shrink-0 items-center gap-1 rounded border border-blue-200 bg-blue-50 px-2 text-[10px] font-bold text-blue-700 hover:bg-blue-100 disabled:cursor-wait disabled:opacity-60"
                  >
                    <RefreshCw size={11} className={checking ? 'animate-spin' : ''} />
                    결과확인
                  </button>
                )}
              </div>
              {expanded && (
                <div className="border-t border-slate-100 bg-slate-50/70 px-10 py-2">
                  <div className="space-y-1.5">
                    {batch.items.map((item) => {
                      const itemMeta = getDepositProcessStatus({ status: item.settlementStatus });
                      const rawPaymentDate =
                        item.paymentDate || settlementPaymentDates.get(String(item.settlementId)) || '';
                      const paymentDate = rawPaymentDate
                        ? formatPayoutBatchDate(String(rawPaymentDate).split('.').join('-'))
                        : '';
                      return (
                        <div key={String(item.settlementId)} className="flex items-center gap-3 text-[11px]">
                          <span className="min-w-[190px] font-semibold text-slate-700">
                            {paymentDate && <strong className="text-slate-900">{paymentDate} 결제</strong>}
                            <span className={paymentDate ? 'ml-2 text-slate-400' : ''}>정산 #{item.settlementId}</span>
                          </span>
                          <span className="flex-1 text-right font-bold tabular-nums text-slate-800">{formatPayoutAmount(item.amount)}원</span>
                          <span className={`min-w-[70px] text-right font-semibold ${itemMeta.iconClass}`}>{itemMeta.label}</span>
                        </div>
                      );
                    })}
                    {batch.items.length === 0 && (
                      <p className="py-2 text-center text-[11px] text-slate-400">구성 정산 정보가 없습니다.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const formatPayoutDeadline = (value) => {
  if (!value) return '';
  const parsed = dayjs(value);
  return parsed.isValid() ? `${parsed.utcOffset(9).format('YYYY.MM.DD. HH:mm')} KST` : String(value);
};

const PayoutDeadlineNotice = () => {
  if (!ADMIN_CP_PAYOUT_TENANT_ENABLED) return null;
  return (
    <div className="mb-3 flex shrink-0 items-center gap-3 rounded-lg border-2 border-orange-300 border-l-[6px] border-l-orange-500 bg-orange-50 px-4 py-3 text-orange-950 shadow-[0_4px_12px_rgba(234,88,12,0.10)]">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-orange-500 text-white shadow-sm">
        <AlertCircle size={19} strokeWidth={2.5} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <strong className="text-[13px] font-black">중요 · 지급 신청 마감 안내</strong>
          <span className="rounded-full border border-orange-300 bg-white px-2 py-0.5 text-[10px] font-black text-orange-700">
            전날 15:00 KST 마감
          </span>
        </div>
        <p className="mt-0.5 text-[11px] font-semibold leading-relaxed text-orange-800">
          정산예정일에 지급하려면 전날 오후 3시까지 신청을 완료해 주세요.
          <strong className="ml-1 text-red-600">마감 이후 신청 시 예정일 지급이 보장되지 않습니다.</strong>
        </p>
      </div>
    </div>
  );
};

const PayoutDeadlineLine = ({ settlement }) => {
  if (!ADMIN_CP_PAYOUT_TENANT_ENABLED || !settlement?.payoutRequestDeadline) return null;
  const passed = settlement.payoutDeadlinePassed === true;
  return (
    <div className={`mt-1 flex items-center justify-between gap-2 text-[10px] ${passed ? 'text-red-500' : 'text-amber-700'}`}>
      <span className="shrink-0 font-semibold">{passed ? '신청 마감 경과' : '신청 마감'}</span>
      <span className="truncate text-right font-medium">{formatPayoutDeadline(settlement.payoutRequestDeadline)}</span>
    </div>
  );
};

const ExternalSettlementLine = ({ settlement }) => {
  if (!settlement?.externalSettlementMode) return null;
  const status = normalizedSettlementStatus(settlement);
  const message = status === 'READY'
    ? '지급예정일에 외부 정산 완료 상태로 자동 변경됩니다. 이 시스템에서는 별도의 지급지시를 실행하지 않습니다.'
    : status === 'TRANSFERRED'
      ? '외부 정산 일정에 따라 완료 처리된 내역입니다.'
      : '';
  return (
    <div className="mt-1.5 rounded-md border border-blue-100 bg-blue-50/70 px-2.5 py-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <DepositStatusBadge item={settlement} />
        <span className="text-[10px] font-semibold text-blue-700">지급예정일 {settlement.transferDate || settlement.settleDate || '-'}</span>
      </div>
      {message && <p className="mt-1 text-[10px] leading-4 text-blue-700">{message}</p>}
    </div>
  );
};

// ========== 관리자 정산 (D+2) ==========
/** 입금완료내역 목업 전용 모드: true면 API 미사용, 목업만 표시. false로 변경 시 API 연동 */
const USE_DEPOSIT_MOCK_ONLY = false;

/** 0: 기존 3단 레이아웃, 1: 정산예정·입금예정 각각 예정내역/완료내역 탭 + 허브 UI(반경 4px) */
// TEST 값만 바꾸면 env 재시작 없이 화면 확인 가능(빌드에는 null로 두세요).
const TEST_SETTLEMENT_HUB_UI = null; // 0 or 1
const SETTLEMENT_HUB_UI_V1 =
  TEST_SETTLEMENT_HUB_UI != null
    ? Number(TEST_SETTLEMENT_HUB_UI) === 1
    : process.env.REACT_APP_SETTLEMENT_HUB_UI === '1';
// const SETTLEMENT_HUB_UI_V1 = true;

// 목업 데이터 (USE_DEPOSIT_MOCK_ONLY true일 때만 사용, API 연동 시 주석 처리)
// const MOCK_DEPOSITS_DATA = [
//   { id: 'mock-1', merchant: '테스트약국A', approveDate: '2026.02.03.', approveAmount: '100,000,000', approveAmountNum: 100000000, settleDate: '2026.02.05.', settleAmount: '98,000,000', settleAmountNum: 98000000, depositDate: '2026.02.07.', depositAmount: '98,000,000', depositAmountNum: 98000000, feeAmount: '2,000,000', feeAmountNum: 2000000, type: 'deposit' },
//   { id: 'mock-2', merchant: '테스트약국B', approveDate: '2026.02.04.', approveAmount: '250,000,000', approveAmountNum: 250000000, settleDate: '2026.02.06.', settleAmount: '245,000,000', settleAmountNum: 245000000, depositDate: '2026.02.08.', depositAmount: '245,000,000', depositAmountNum: 245000000, feeAmount: '5,000,000', feeAmountNum: 5000000, type: 'deposit' },
//   { id: 'mock-3', merchant: '해피약국', approveDate: '2026.02.05.', approveAmount: '500,000,000', approveAmountNum: 500000000, settleDate: '2026.02.07.', settleAmount: '490,000,000', settleAmountNum: 490000000, depositDate: '2026.02.09.', depositAmount: '490,000,000', depositAmountNum: 490000000, feeAmount: '10,000,000', feeAmountNum: 10000000, type: 'deposit' },
// ];

function Settlement() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { showAllOpen } = useTooltip();
  const [hoveredTooltipId, setHoveredTooltipId] = useState(null);
  useEffect(() => {
    if (!showAllOpen) setHoveredTooltipId(null);
  }, [showAllOpen]);
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;

  // API 값 배수 (테스트용: 1000000 = 백만 배, 원래대로 되돌리려면 1로 변경)
  const AMOUNT_MULTIPLIER = 1;

  const formatToday = () => {
    const d = new Date();
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(
      2,
      '0',
    )}.${String(d.getDate()).padStart(2, '0')}.`;
  };

  const formatDate = (d) => `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}.`;

  // 기본 기간: 당월 1일 ~ 오늘
  const getDefaultMonthRange = () => {
    const today = new Date();
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    return {
      start: formatDate(monthStart),
      end: formatToday(),
    };
  };

  const [pendingSettlements, setPendingSettlements] = useState([]); // 정산예정 (PENDING)
  const [settlements, setSettlements] = useState([]); // 정산확정 (READY)
  const [deposits, setDeposits] = useState([]); // 입금완료 (TRANSFERRED)
  const [payoutBatches, setPayoutBatches] = useState([]); // phPay KG이니시스 합산 지급 배치
  const [checkingPayoutBatchIds, setCheckingPayoutBatchIds] = useState(() => new Set());
  const [payoutSubmitting, setPayoutSubmitting] = useState(false);
  const [payoutBatchResult, setPayoutBatchResult] = useState(null);
  const [settlementCapability, setSettlementCapability] = useState(null);
  const [settlementCapabilityLoading, setSettlementCapabilityLoading] = useState(ADMIN_GASYSTEM_EXTERNAL_SETTLEMENT_ENABLED);
  const [settlementCapabilityError, setSettlementCapabilityError] = useState('');
  const [settlementCapabilityReloadToken, setSettlementCapabilityReloadToken] = useState(0);
  const [summary, setSummary] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null); // { type, date, ...group, items }
  const [selectedMerchant, setSelectedMerchant] = useState(null); // 상세내역에서 선택한 가맹점 1건
  const [selectedIds, setSelectedIds] = useState([]); // 확정/입금용 체크된 ID 목록
  const [selectedSettlementIds, setSelectedSettlementIds] = useState([]); // 확정 모달용 체크된 ID 목록
  const [isSettlementModalOpen, setIsSettlementModalOpen] = useState(false); // 확정 모달
  const [isDepositModalOpen, setIsDepositModalOpen] = useState(false); // 입금 모달
  const [settlementOtpToken, setSettlementOtpToken] = useState(''); // 확정 모달 OTP 토큰
  const [depositOtpToken, setDepositOtpToken] = useState(''); // 입금 모달 OTP 토큰
  const [expandedId, setExpandedId] = useState(null); // '날짜-settlement' | '날짜-deposit' 드롭다운 열린 카드
  const [expandedRow, setExpandedRow] = useState(null); // 입금완료 테이블 행 확장용
  const [searchKeyword, setSearchKeyword] = useState('');
  const [reloadToken, setReloadToken] = useState(0); // 입금 후 재조회 트리거
  const [confirmSettlementOpen, setConfirmSettlementOpen] = useState(false); // 정산 확정 안내 모달
  const [confirmDepositOpen, setConfirmDepositOpen] = useState(false); // 입금 승인 안내 모달
  // 기본 기간: 당월 1일 ~ 오늘
  const defaultRange = getDefaultMonthRange();
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const [depositMonthPickerOpen, setDepositMonthPickerOpen] = useState(false);
  const [depositExcelDownloading, setDepositExcelDownloading] = useState(false);
  const depositMonthPickerRef = useRef(null);
  const [pendingPanelTab, setPendingPanelTab] = useState('scheduled');
  const [transferPanelTab, setTransferPanelTab] = useState('scheduled');
  const [pendingHistYear, setPendingHistYear] = useState(() => String(new Date().getFullYear()));
  const [pendingHistMonth, setPendingHistMonth] = useState(() => String(new Date().getMonth() + 1).padStart(2, '0'));
  const [transferHistYear, setTransferHistYear] = useState(() => String(new Date().getFullYear()));
  const [transferHistMonth, setTransferHistMonth] = useState(() => String(new Date().getMonth() + 1).padStart(2, '0'));
  const [hubExpandedIds, setHubExpandedIds] = useState(() => new Set());
  const pendingHistPickerRef = useRef(null);
  const transferHistPickerRef = useRef(null);
  const [pendingHistPickerOpen, setPendingHistPickerOpen] = useState(false);
  const [transferHistPickerOpen, setTransferHistPickerOpen] = useState(false);

  const capabilityPayoutExecutionEnabled = settlementCapability?.payoutExecutionEnabled
    ?? settlementCapability?.payout_execution_enabled;
  const capabilityExternalAutoCompleteEnabled = settlementCapability?.externalSettlementAutoCompleteEnabled
    ?? settlementCapability?.external_settlement_auto_complete_enabled;
  const capabilityCompletionMode = settlementCapability?.settlementCompletionMode
    ?? settlementCapability?.settlement_completion_mode;
  const externalScheduledCapability =
    capabilityPayoutExecutionEnabled === false
    && capabilityExternalAutoCompleteEnabled === true
    && String(capabilityCompletionMode || '').toUpperCase() === 'EXTERNAL_SCHEDULED';
  // GASYSTEM 빌드는 capability가 누락되어도 지급 실행을 절대 열지 않는다.
  const externalSettlementMode = ADMIN_GASYSTEM_EXTERNAL_SETTLEMENT_ENABLED || externalScheduledCapability;

  const dateToApi = (s) => (s ? s.replace(/\./g, '-') : '');

  // 문자열 금액 → 숫자
  const parseAmount = (s) => Number(String(s).replace(/,/g, '')) || 0;
  const getSettleFromApprove = (approveNum) => approveNum; // API에서 정산금액을 내려주므로 기본은 그대로 사용
  const getSettleFormatted = (approveAmountStr) => getSettleFromApprove(parseAmount(approveAmountStr)).toLocaleString();

  // CSV 유틸 (엑셀 다운로드용)
  const buildCsv = (settlementData, depositData) => {
    const escapeCell = (value) => {
      if (value === null || value === undefined) return '';
      return String(value).replace(/"/g, '""');
    };

    // 정산확정 + 입금완료 합치기
    const allRows = [
      ...settlementData.map((row) => ({
        type: '정산확정',
        date: row.settleDate,
        merchant: row.merchant,
        approveDate: row.approveDate,
        approveAmount: row.approveAmountNum,
        cancelAmount: row.cancelAmountNum ?? 0,
        settleAmount: row.settleAmountNum,
        depositAmount: '',
        depositDate: '',
      })),
      ...depositData.map((row) => ({
        type: '입금내역',
        date: row.depositDate,
        merchant: row.merchant,
        approveDate: row.approveDate,
        approveAmount: row.approveAmountNum,
        cancelAmount: row.cancelAmountNum ?? 0,
        settleAmount: row.settleAmountNum,
        depositAmount: row.depositAmountNum ?? 0,
        depositDate: row.depositDate,
      })),
    ];

    const columns = [
      { key: 'type', label: '구분' },
      { key: 'date', label: '정산일/입금일' },
      { key: 'merchant', label: '가맹점명' },
      { key: 'approveDate', label: '승인일' },
      { key: 'approveAmount', label: '승인금액' },
      { key: 'cancelAmount', label: '취소금액' },
      { key: 'settleAmount', label: '정산금액' },
      { key: 'depositAmount', label: '입금금액' },
      { key: 'depositDate', label: '입금일' },
    ];

    const header = columns.map((c) => `"${c.label}"`).join(',');
    const lines = allRows.map((row) => {
      return columns
        .map((c) => {
          let v = row[c.key];
          if (c.key === 'approveAmount' || c.key === 'cancelAmount' || c.key === 'settleAmount' || c.key === 'depositAmount') {
            v = Number(v || 0);
          }
          return `"${escapeCell(v ?? '')}"`;
        })
        .join(',');
    });

    // UTF-8 BOM 추가해서 윈도우 엑셀에서 한글 안 깨지도록
    return '\uFEFF' + header + '\r\n' + lines.join('\r\n');
  };

  // 입금완료내역 엑셀 (시트1: 입금일자별 합계, 시트2: 전체 세부건)
  const downloadDepositExcel = async () => {
    if (depositExcelDownloading) return;
    if (groupedDepositsByDate.length === 0) {
      showToast(`다운로드할 ${ADMIN_CP_PAYOUT_TENANT_ENABLED ? '입금 처리' : '입금완료'} 내역이 없습니다.`, 'error');
      return;
    }

    if (ADMIN_CP_PAYOUT_TENANT_ENABLED) {
      const dateFrom = String(startDate || '').replace(/\.$/, '').replace(/\./g, '-');
      const dateTo = String(endDate || '').replace(/\.$/, '').replace(/\./g, '-');
      setDepositExcelDownloading(true);
      try {
        await downloadBlob({
          url: '/api/admin/cp-settlement/export',
          params: {
            dateFrom,
            dateTo,
            dateBasis: 'PAYMENT_DATE',
            status: 'TRANSFERRING,TRANSFERRED,TRANSFER_FAILED',
          },
          fallbackFilename: `정산내역_${dateFrom.replace(/-/g, '')}_${dateTo.replace(/-/g, '')}.xlsx`,
        });
        showToast('정산내역 엑셀 다운로드가 시작되었습니다.');
      } catch (error) {
        showToast(error?.message || '정산내역 엑셀 다운로드에 실패했습니다.', 'error');
      } finally {
        setDepositExcelDownloading(false);
      }
      return;
    }

    try {
      const XLSX = await import('xlsx');

      const summaryRows = groupedDepositsByDate.map((group) => ({
        [ADMIN_CP_PAYOUT_TENANT_ENABLED ? '처리기준일' : '입금일']: group.depositDate,
        ...(ADMIN_CP_PAYOUT_TENANT_ENABLED
          ? { 처리상태: getDepositStatusSummaries(group.items).map((s) => `${s.label} ${s.count}건`).join(', ') }
          : {}),
        건수: group.count,
        승인금액: group.approveAmountNum ?? 0,
        수수료: group.feeAmountNum ?? 0,
        정산금액: group.settleAmountNum ?? 0,
        본사수익: group.adminFeeAmountNum ?? 0,
      }));

      const detailRows = groupedDepositsByDate.flatMap((group) =>
        group.items.map((item) => ({
          [ADMIN_CP_PAYOUT_TENANT_ENABLED ? '처리기준일' : '입금일']: group.depositDate,
          ...(ADMIN_CP_PAYOUT_TENANT_ENABLED
            ? {
                상태: getDepositProcessStatus(item).label,
                지급상태: item.payoutStatus ?? '',
                응답코드: item.payoutProviderCode ?? '',
                응답메시지: item.payoutProviderMessage ?? '',
              }
            : {}),
          정산일: item.settleDate ?? '',
          가맹점명: item.merchant ?? '',
          결제일자: item.approveDate ?? '',
          승인금액: item.approveAmountNum ?? 0,
          수수료: item.feeAmountNum ?? 0,
          수수료율: item.feeRate ?? '',
          정산금액: item.settleAmountNum ?? 0,
          본사수익: item.adminFeeAmountNum ?? 0,
        }))
      );

      const wb = XLSX.utils.book_new();
      const wsSummary = XLSX.utils.json_to_sheet(summaryRows);
      const wsDetail = XLSX.utils.json_to_sheet(detailRows);

      XLSX.utils.book_append_sheet(wb, wsSummary, '입금일자별 합계');
      XLSX.utils.book_append_sheet(wb, wsDetail, '세부 내역');

      const safeStart = startDate.replace(/\./g, '');
      const safeEnd = endDate.replace(/\./g, '');
      const filename = `${ADMIN_CP_PAYOUT_TENANT_ENABLED ? '입금처리내역' : '입금완료내역'}_${safeStart}_${safeEnd}.xlsx`;

      XLSX.writeFile(wb, filename);
      showToast('엑셀 다운로드가 시작되었습니다.');
    } catch (e) {
      console.error(e);
      showToast('엑셀 파일을 생성하는 중 오류가 발생했습니다.', 'error');
    }
  };

  const formatApiDateToDot = (dateStr) => {
    if (!dateStr) return '';
    if (dateStr.includes('.')) {
      return dateStr.endsWith('.') ? dateStr : dateStr + '.';
    }
    const datePart = String(dateStr).split('T')[0]?.split(' ')[0] || '';
    const parts = datePart.split('-');
    if (parts.length === 3) {
      return `${parts[0]}.${parts[1]}.${parts[2]}.`;
    }
    return dateStr;
  };

  // row별 amounts 계산 (API rows 기준)
  // 승인금액: gross_amount - cancel_amount
  // 수수료: fee_amount - canceled_fee_refund_amount
  // 정산금액: net_amount
  // 입금금액: transfer_amount
  // 본사수익: admin_fee_amount
  const getRowAmounts = (row, cg) => {
    const grossAmount = Number(row.gross_amount ?? row.grossAmount ?? 0) * AMOUNT_MULTIPLIER;
    const cancelAmount = Number(row.cancel_amount ?? row.cancelAmount ?? 0) * AMOUNT_MULTIPLIER;
    const feeAmountRaw = Number(row.fee_amount ?? row.feeAmount ?? 0) * AMOUNT_MULTIPLIER;
    const canceledFeeRefund = Number(row.canceled_fee_refund_amount ?? row.canceledFeeRefundAmount ?? 0) * AMOUNT_MULTIPLIER;
    const feeAmount = feeAmountRaw - canceledFeeRefund;
    const netAmount = Number(row.net_amount ?? row.netAmount ?? 0) * AMOUNT_MULTIPLIER;
    const transferAmount = Number(row.transfer_amount ?? row.transferAmount ?? 0) * AMOUNT_MULTIPLIER;
    const adminFeeAmount = Number(row.admin_fee_amount ?? row.adminFeeAmount ?? 0) * AMOUNT_MULTIPLIER;
    const approveAmount = grossAmount - cancelAmount;
    const feeRate = Number(cg?.feeRate ?? cg?.fee_rate ?? 0);
    return {
      paymentAmount: approveAmount,
      cancelAmount,
      feeAmount,
      transferAmount,
      netAmount,
      adminFeeAmount,
      feeRate,
    };
  };

  // 월별 입금 API(data.days) 응답 → day별 그룹 구조로 변환한다.
  // 통합 API 페이지 경계에서 같은 날짜가 나뉘어도 한 날짜 그룹으로 합친다.
  const parseDepositDaysFromApi = (data) => {
    const days = Array.isArray(data?.days) ? data.days : [];
    const groupedByDate = new Map();

    days.forEach((day) => {
      const dateStr = day.date || '';
      const dateKey = formatApiDateToDot(dateStr) || '-';
      if (!groupedByDate.has(dateKey)) {
        groupedByDate.set(dateKey, {
          key: dateKey,
          depositDate: dateKey,
          count: 0,
          approveAmountNum: 0,
          feeAmountNum: 0,
          settleAmountNum: 0,
          depositAmountNum: 0,
          adminFeeAmountNum: 0,
          items: [],
        });
      }
      const group = groupedByDate.get(dateKey);
      const cpGroups = Array.isArray(day.cpGroups) ? day.cpGroups : [];

      cpGroups.forEach((cg) => {
        const rows = Array.isArray(cg.rows) ? cg.rows : [];
        rows.forEach((row) => {
          const amt = getRowAmounts(row, cg);
          const settlementDate = row.settlement_date ?? row.settlementDate ?? '';
          const transferDate = row.transfer_date ?? row.transferDate ?? '';
          const approveDateVal = formatApiDateToDot(settlementDate) || dateKey;
          const settleDateVal = formatApiDateToDot(transferDate) || dateKey;
          const transferStatus = row.transfer_status ?? row.transferStatus ?? '';
          const status = row.status
            ?? row.settlement_status
            ?? row.settlementStatus
            ?? (String(transferStatus).toUpperCase() === 'SUCCESS' ? 'TRANSFERRED' : '');

          group.count += 1;
          group.approveAmountNum += amt.paymentAmount;
          group.feeAmountNum += amt.feeAmount;
          group.settleAmountNum += amt.netAmount;
          group.depositAmountNum += amt.transferAmount;
          group.adminFeeAmountNum += amt.adminFeeAmount;

          group.items.push({
            id: row.settlement_id ?? row.settlementId ?? row.id ?? `${cg.cp_id ?? ''}-${group.items.length}`,
            merchant: row.cp_name ?? cg.cp_name ?? cg.cpName ?? '',
            paymentDate: formatApiDateToDot(settlementDate),
            approveAmountNum: amt.paymentAmount,
            approveAmount: amt.paymentAmount.toLocaleString('ko-KR'),
            feeAmountNum: amt.feeAmount,
            feeAmount: amt.feeAmount.toLocaleString('ko-KR'),
            feeRate: amt.feeRate,
            settleAmountNum: amt.netAmount,
            settleAmount: amt.netAmount.toLocaleString('ko-KR'),
            depositAmountNum: amt.transferAmount,
            depositAmount: amt.transferAmount.toLocaleString('ko-KR'),
            adminFeeAmountNum: amt.adminFeeAmount,
            adminFeeAmount: amt.adminFeeAmount.toLocaleString('ko-KR'),
            approveDate: approveDateVal,
            settleDate: settleDateVal,
            status,
            externalSettlementMode,
            transferRef: row.transfer_ref ?? row.transferRef ?? '',
            transferDate: settleDateVal,
            transferStatus,
            pgProvider: row.pg_provider ?? row.pgProvider ?? '',
            payoutStatus: row.payout_status ?? row.payoutStatus ?? '',
            payoutProviderCode: row.payout_provider_code ?? row.payoutProviderCode ?? '',
            payoutResultCode: row.payout_result_code ?? row.payoutResultCode ?? '',
            payoutProviderMessage: row.payout_provider_message ?? row.payoutProviderMessage ?? '',
            payoutAcceptedAt: row.payout_accepted_at ?? row.payoutAcceptedAt ?? '',
            payoutResultCheckedAt: row.payout_result_checked_at ?? row.payoutResultCheckedAt ?? '',
            payoutCompletedAt: row.payout_completed_at ?? row.payoutCompletedAt ?? '',
            requestedAt: row.requested_at ?? row.requestedAt ?? '',
            completedAt: row.completed_at ?? row.completedAt ?? '',
          });
        });
      });
    });

    return Array.from(groupedByDate.values());
  };

  const mapSettlementRow = (row, index) => {
    const id =
      row.id ??
      row.settlementId ??
      row.settlement_id ??
      `ST-${index}`;
    const merchant =
      row.cp_name ??
      row.cpName ??
      row.merchant ??
      row.name ??
      '';

    // AMOUNT_MULTIPLIER 적용 (테스트용 - 1로 변경하면 원래대로)
    const approveAmountNum = Number(
      row.approvedAmount ??
      row.approveAmount ??
      row.approved_amount ??
      row.gross_amount ??
      0,
    ) * AMOUNT_MULTIPLIER;
    // 입금예정 가맹점당 금액: net_amount 우선
    const rawNetAmountValue =
      row.net_amount ??
      row.settlementAmount ??
      row.settleAmount ??
      row.settlement_amount ??
      0;
    const rawNetAmount = Number(rawNetAmountValue);
    const settlementAmountNum = rawNetAmount * AMOUNT_MULTIPLIER;
    // 입금금액: last_transfer_amount(실제 입금액) 우선, 없으면 net_amount(예정 입금액)
    const transferredAmountNum = Number(
      row.last_transfer_amount ??
      row.lastTransferAmount ??
      row.transferredAmount ??
      row.transferAmount ??
      row.transfer_amount ??
      0,
    ) * AMOUNT_MULTIPLIER;
    const feeAmountNum = Number(
      row.fee_amount ??
      row.feeAmount ??
      row.fee_amt ??
      row.feeAmt ??
      row.fee ??
      0,
    ) * AMOUNT_MULTIPLIER;
    const cancelAmountNum = Number(
      row.cancel_amount ?? row.cancelAmount ?? row.cancelAmountNum ?? 0,
    ) * AMOUNT_MULTIPLIER;

    // 결제일(표시용): 승인일 정보가 따로 없으면 settlement_date 사용
    const approveDate = formatApiDateToDot(
      row.approved_date ??
      row.approval_date ??
      row.approve_date ??
      row.settlement_date
    );
    // 정산일/입금예정: transfer_date 우선, 없으면 settlement_date
    const settleDate = formatApiDateToDot(
      row.transfer_date ??
      row.transferDate ??
      row.settlement_date ??
      row.settle_date
    );
    // 입금일: transfer_date 또는 transfer 관련 필드
    const depositDate = formatApiDateToDot(
      row.transfer_date ??
      row.transferDate ??
      row.transferredAt ??
      row.transferred_at ??
      row.depositDate,
    );

    const status = row.status ?? row.settlementStatus ?? '';
    const statusUpper = String(status).toUpperCase();

    // status 기반으로 type 결정: 
    // - PENDING → 정산예정
    // - READY → 정산확정
    // - TRANSFERRING → 지급대행 요청 등록(입금 처리 중)
    // - TRANSFERRED → 입금완료
    // - TRANSFER_FAILED → 입금실패(다시 입금예정으로 보내기 위해 정산확정으로 취급)
    let type = 'settlement'; // 기본값
    if (statusUpper === 'TRANSFERRED') {
      type = 'deposit';
    } else if (statusUpper === 'PENDING') {
      type = 'pending';
    } else if (statusUpper === 'READY') {
      type = 'settlement';
    } else if (statusUpper === 'TRANSFERRING') {
      type = 'settlement';
    } else if (statusUpper === 'TRANSFER_FAILED') {
      type = 'settlement';
    } else {
      // 기존 로직 유지 (하위 호환성)
      const isDeposit =
        transferredAmountNum > 0 ||
        !!depositDate ||
        statusUpper.includes('PAID') ||
        statusUpper.includes('TRANSFER') ||
        statusUpper.includes('DEPOSIT');
      type = isDeposit ? 'deposit' : 'settlement';
    }

    return {
      id,
      name: merchant,
      approveDate,
      approveAmount: approveAmountNum.toLocaleString(),
      approveAmountNum,
      approveCount: row.approveCount ?? row.approve_count ?? row.count ?? 0,
      settleDate,
      settleAmount: settlementAmountNum.toLocaleString(),
      settleAmountNum: settlementAmountNum,
      settleAmountRaw: String(rawNetAmountValue ?? '0'),
      settleCount: row.settleCount ?? row.settle_count ?? 0,
      depositDate,
      depositAmount: (transferredAmountNum || settlementAmountNum || approveAmountNum).toLocaleString(),
      depositAmountNum: transferredAmountNum || settlementAmountNum || approveAmountNum,
      cancelAmount: cancelAmountNum.toLocaleString(),
      cancelAmountNum,
      feeAmount: feeAmountNum.toLocaleString(),
      feeAmountNum,
      merchant,
      cpId: row.cp_id ?? row.cpId ?? '',
      pgProvider: row.pg_provider ?? row.pgProvider ?? '',
      idMall: row.id_mall ?? row.idMall ?? '',
      status,
      externalSettlementMode,
      transferRef: row.transfer_ref ?? row.transferRef ?? '',
      transferDate: settleDate,
      payoutStatus: row.payout_status ?? row.payoutStatus ?? '',
      payoutProviderCode: row.payout_provider_code ?? row.payoutProviderCode ?? '',
      payoutAcceptedAt: row.payout_accepted_at ?? row.payoutAcceptedAt ?? '',
      payoutRequestDeadline: row.payout_request_deadline ?? row.payoutRequestDeadline ?? '',
      payoutDeadlinePassed: row.payout_deadline_passed === true || row.payoutDeadlinePassed === true,
      payoutPayDate: row.payout_pay_date ?? row.payoutPayDate ?? '',
      transferFailReason: row.last_transfer_fail_reason ?? row.lastTransferFailReason ?? '',
      txId: row.txId ?? row.settlementId ?? row.settlement_id ?? '',
      type,
    };
  };

  const setQuickPeriod = (type) => {
    const today = new Date();
    const formatDate = (d) => `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}.`;
    const parseDate = (s) => {
      const [y, m, d] = s.split('.').map(Number);
      return new Date(y, m - 1, d);
    };
    const currentStart = parseDate(startDate);
    const currentEnd = parseDate(endDate);
    let newStart, newEnd;
    switch (type) {
      case '당일': newStart = newEnd = today; break;
      case '전일':
        // 마지막날짜( endDate ) 기준 전일: 1/1~1/31 → 1/30~1/30, 다시 전일 → 1/29~1/29
        newEnd = new Date(currentEnd);
        newEnd.setDate(currentEnd.getDate() - 1);
        newStart = new Date(newEnd);
        break;
      case '당월': newStart = new Date(today.getFullYear(), today.getMonth(), 1); newEnd = today; break;
      case '전월':
        newStart = new Date(currentStart.getFullYear(), currentStart.getMonth() - 1, 1);
        newEnd = new Date(currentStart.getFullYear(), currentStart.getMonth(), 0);
        break;
      default: return;
    }
    setStartDate(formatDate(newStart));
    setEndDate(formatDate(newEnd));
  };

  // 입금완료 영역 전용: 월 단위 이전/다음 이동
  const changeDepositMonth = (delta) => {
    if (!startDate) return;
    const [y, m] = startDate.split('.').map(Number);
    if (!y || !m) return;
    const base = new Date(y, m - 1, 1);
    base.setMonth(base.getMonth() + delta);
    const newYear = base.getFullYear();
    const newMonth = base.getMonth() + 1;
    const lastDay = new Date(newYear, newMonth, 0).getDate();
    const mm = String(newMonth).padStart(2, '0');
    setStartDate(`${newYear}.${mm}.01.`);
    setEndDate(`${newYear}.${mm}.${String(lastDay).padStart(2, '0')}.`);
  };

  const handleDepositMonthSelect = (date) => {
    if (!date) return;
    const y = date.getFullYear();
    const m = date.getMonth() + 1;
    const lastDay = new Date(y, m, 0).getDate();
    const mm = String(m).padStart(2, '0');
    setStartDate(`${y}.${mm}.01.`);
    setEndDate(`${y}.${mm}.${String(lastDay).padStart(2, '0')}.`);
    setDepositMonthPickerOpen(false);
  };

  const changePendingHistMonth = (delta) => {
    const y = parseInt(pendingHistYear, 10);
    const mo = parseInt(pendingHistMonth, 10);
    if (!y || !mo) return;
    const base = new Date(y, mo - 1, 1);
    base.setMonth(base.getMonth() + delta);
    setPendingHistYear(String(base.getFullYear()));
    setPendingHistMonth(String(base.getMonth() + 1).padStart(2, '0'));
  };

  const changeTransferHistMonth = (delta) => {
    const y = parseInt(transferHistYear, 10);
    const mo = parseInt(transferHistMonth, 10);
    if (!y || !mo) return;
    const base = new Date(y, mo - 1, 1);
    base.setMonth(base.getMonth() + delta);
    setTransferHistYear(String(base.getFullYear()));
    setTransferHistMonth(String(base.getMonth() + 1).padStart(2, '0'));
  };

  const handlePendingHistMonthPick = (date) => {
    if (!date) return;
    setPendingHistYear(String(date.getFullYear()));
    setPendingHistMonth(String(date.getMonth() + 1).padStart(2, '0'));
    setPendingHistPickerOpen(false);
  };

  const handleTransferHistMonthPick = (date) => {
    if (!date) return;
    setTransferHistYear(String(date.getFullYear()));
    setTransferHistMonth(String(date.getMonth() + 1).padStart(2, '0'));
    setTransferHistPickerOpen(false);
  };

  const resetPendingHistToCurrentMonth = () => {
    const t = new Date();
    setPendingHistYear(String(t.getFullYear()));
    setPendingHistMonth(String(t.getMonth() + 1).padStart(2, '0'));
  };

  const resetTransferHistToCurrentMonth = () => {
    const t = new Date();
    setTransferHistYear(String(t.getFullYear()));
    setTransferHistMonth(String(t.getMonth() + 1).padStart(2, '0'));
  };

  useEffect(() => {
    const handler = (e) => {
      if (depositMonthPickerRef.current && !depositMonthPickerRef.current.contains(e.target)) {
        setDepositMonthPickerOpen(false);
      }
      if (pendingHistPickerRef.current && !pendingHistPickerRef.current.contains(e.target)) {
        setPendingHistPickerOpen(false);
      }
      if (transferHistPickerRef.current && !transferHistPickerRef.current.contains(e.target)) {
        setTransferHistPickerOpen(false);
      }
    };
    if (depositMonthPickerOpen || pendingHistPickerOpen || transferHistPickerOpen) {
      document.addEventListener('mousedown', handler);
    }
    return () => document.removeEventListener('mousedown', handler);
  }, [depositMonthPickerOpen, pendingHistPickerOpen, transferHistPickerOpen]);

  useEffect(() => {
    setPendingHistPickerOpen(false);
  }, [pendingPanelTab]);

  useEffect(() => {
    setTransferHistPickerOpen(false);
  }, [transferPanelTab]);

  // 입금완료 월단위 조회용 (YYYY-MM)
  const apiMonthFrom = useMemo(() => {
    if (!startDate || !startDate.trim()) return '';
    const m = startDate.replace(/\./g, '-').slice(0, 7);
    return m.match(/^\d{4}-\d{2}$/) ? m : '';
  }, [startDate]);
  const apiMonthTo = useMemo(() => {
    if (!endDate || !endDate.trim()) return '';
    const m = endDate.replace(/\./g, '-').slice(0, 7);
    return m.match(/^\d{4}-\d{2}$/) ? m : '';
  }, [endDate]);

  // GASYSTEM은 env 차단을 우선 적용하고 capability는 서버 정책 확인용으로 사용한다.
  useEffect(() => {
    if (!ADMIN_GASYSTEM_EXTERNAL_SETTLEMENT_ENABLED) {
      setSettlementCapability(null);
      setSettlementCapabilityLoading(false);
      setSettlementCapabilityError('');
      return undefined;
    }

    const baseUrl = process.env.REACT_APP_API || '';
    let cancelled = false;
    const fetchSettlementCapability = async () => {
      setSettlementCapabilityLoading(true);
      setSettlementCapabilityError('');
      try {
        const response = await axios.get(baseUrl + '/api/admin/cp-settlement/capabilities', {
          withCredentials: true,
        });
        if (cancelled) return;
        if (response?.data?.code !== 1000) {
          throw new Error(response?.data?.msg || '정산 기능 설정을 확인하지 못했습니다.');
        }
        setSettlementCapability(response.data.data || {});
      } catch (error) {
        if (cancelled) return;
        setSettlementCapability(null);
        setSettlementCapabilityError(error?.response?.data?.msg || error?.message || '정산 기능 설정을 확인하지 못했습니다.');
      } finally {
        if (!cancelled) setSettlementCapabilityLoading(false);
      }
    };

    fetchSettlementCapability();
    return () => { cancelled = true; };
  }, [settlementCapabilityReloadToken]);

  // 1) 정산예정 / 입금예정: 분리 API (기간 필터 없음, 전체 목록)
  useEffect(() => {
    const baseUrl = process.env.REACT_APP_API || '';
    let cancelled = false;

    const fetchScheduled = async () => {
      try {
        const [settlementsRes, transfersRes] = await Promise.all([
          axios.get(baseUrl + '/api/admin/cp-settlement/scheduled/settlements', {
            params: { status: 'PENDING' },
            withCredentials: true,
          }),
          axios.get(baseUrl + '/api/admin/cp-settlement/scheduled/transfers', {
            params: { status: '' },
            withCredentials: true,
          }),
        ]);

        if (cancelled) return;

        let pendingRows = [];
        let transferRows = [];
        if (settlementsRes?.data?.code === 1000) {
          const d = settlementsRes.data.data;
          pendingRows = Array.isArray(d?.rows) ? d.rows : Array.isArray(d) ? d : [];
        }
        if (transfersRes?.data?.code === 1000) {
          const d = transfersRes.data.data;
          transferRows = Array.isArray(d?.rows) ? d.rows : Array.isArray(d) ? d : [];
        }

        const nextPending = pendingRows.map((r, i) => mapSettlementRow({ ...r, status: 'PENDING' }, i));
        const nextSettlements = transferRows.map((r, i) => mapSettlementRow(r, i));
        setPendingSettlements(nextPending);
        setSettlements(nextSettlements);
      } catch (e) {
        if (!cancelled) {
          showToastRef.current(e?.response?.data?.msg || '정산예정/입금예정 데이터를 불러오는 중 오류가 발생했습니다.', 'error');
          setPendingSettlements([]);
          setSettlements([]);
        }
      }
    };

    fetchScheduled();
    return () => { cancelled = true; };
  }, [reloadToken]);

  // 2) 월별 입금 내역: phPay만 통합 처리 API, ntoss는 기존 완료 API를 사용한다.
  useEffect(() => {
    if (USE_DEPOSIT_MOCK_ONLY) return;
    if (!apiMonthFrom || !apiMonthTo) return;

    const baseUrl = process.env.REACT_APP_API || '';
    let cancelled = false;

    const fetchDeposits = async () => {
      try {
        const integratedHistoryEnabled = ADMIN_CP_PAYOUT_TENANT_ENABLED;
        const endpoint = integratedHistoryEnabled
          ? '/api/admin/cp-settlement/transfer-logs/monthly'
          : '/api/admin/cp-settlement/transfer-logs/completed-monthly';
        const limit = integratedHistoryEnabled ? 100 : 500;
        const requestPage = (page) => axios.get(baseUrl + endpoint, {
          params: {
            monthFrom: apiMonthFrom,
            monthTo: apiMonthTo,
            ...(integratedHistoryEnabled
              ? { status: 'TRANSFERRING,TRANSFERRED,TRANSFER_FAILED' }
              : {}),
            page,
            limit,
          },
          withCredentials: true,
        });

        const firstRes = await requestPage(1);
        if (cancelled) return;
        if (firstRes?.data?.code !== 1000) {
          throw new Error(firstRes?.data?.msg || '입금 내역 조회에 실패했습니다.');
        }

        const payloads = [firstRes.data.data || {}];
        if (integratedHistoryEnabled) {
          const pagination = payloads[0]?.pagination || {};
          const total = Number(pagination.total ?? payloads[0]?.total ?? 0);
          const totalPages = Number(
            pagination.totalPages
            ?? pagination.total_pages
            ?? pagination.pageCount
            ?? pagination.page_count
            ?? (total > 0 ? Math.ceil(total / limit) : 1),
          );

          for (let page = 2; page <= totalPages; page += 1) {
            const pageRes = await requestPage(page);
            if (cancelled) return;
            if (pageRes?.data?.code !== 1000) {
              throw new Error(pageRes?.data?.msg || '입금 내역 추가 조회에 실패했습니다.');
            }
            payloads.push(pageRes.data.data || {});
          }
        }

        const allDays = payloads.flatMap((payload) => (
          Array.isArray(payload?.days) ? payload.days : []
        ));
        setDeposits(parseDepositDaysFromApi({ days: allDays }));

        if (integratedHistoryEnabled) {
          const batchMap = new Map();
          payloads.forEach((payload) => {
            const rows = Array.isArray(payload?.payout_batches)
              ? payload.payout_batches
              : Array.isArray(payload?.payoutBatches)
                ? payload.payoutBatches
                : [];
            rows.forEach((batch, index) => {
              const normalized = normalizePayoutBatch(batch, index);
              batchMap.set(String(normalized.batchId), normalized);
            });
          });
          setPayoutBatches(Array.from(batchMap.values()));
        } else {
          setPayoutBatches([]);
        }
        setSummary(null);
      } catch (e) {
        if (!cancelled) {
          const fallback = ADMIN_CP_PAYOUT_TENANT_ENABLED
            ? '입금 처리 내역을 불러오는 중 오류가 발생했습니다.'
            : '입금완료 데이터를 불러오는 중 오류가 발생했습니다.';
          showToastRef.current(e?.response?.data?.msg || e?.message || fallback, 'error');
          setDeposits([]);
          setPayoutBatches([]);
          setSummary(null);
        }
      }
    };

    fetchDeposits();
    return () => { cancelled = true; };
  }, [apiMonthFrom, apiMonthTo, reloadToken]);

  // 날짜별 그룹
  const getDateGroups = (list, isSettlement) => {
    const key = isSettlement ? 'settleDate' : 'depositDate';
    const grouped = {};
    list.forEach((item) => {
      const d = item[key];
      if (!grouped[d]) grouped[d] = { totalApproveAmount: 0, totalSettleAmount: 0, totalDepositAmount: 0, totalCancelAmount: 0, totalFeeAmount: 0, count: 0, items: [] };
      grouped[d].items.push(item);
      grouped[d].count += 1;
      const approveNum = item.approveAmountNum ?? parseAmount(item.approveAmount);
      const settleNum = item.settleAmountNum ?? getSettleFromApprove(approveNum);
      const depositNum = item.depositAmountNum ?? settleNum;
      const cancelNum = item.cancelAmountNum ?? 0;
      const feeNum = item.feeAmountNum ?? 0;
      grouped[d].totalApproveAmount += approveNum;
      grouped[d].totalSettleAmount += settleNum;
      grouped[d].totalDepositAmount += depositNum;
      grouped[d].totalCancelAmount += cancelNum;
      grouped[d].totalFeeAmount += feeNum;
    });
    Object.keys(grouped).forEach((k) => {
      grouped[k].totalApproveFormatted = grouped[k].totalApproveAmount.toLocaleString();
      grouped[k].totalFormatted = grouped[k].totalSettleAmount.toLocaleString();
      grouped[k].totalDepositFormatted = grouped[k].totalDepositAmount.toLocaleString();
      grouped[k].totalCancelFormatted = grouped[k].totalCancelAmount.toLocaleString();
      grouped[k].totalFeeFormatted = grouped[k].totalFeeAmount.toLocaleString();
    });
    return grouped;
  };

  // 기간/검색 필터 적용한 정산 목록
  // 정산예정: 전체 목록 (기간 필터 없음), 검색어만 적용
  const filteredPendingSettlements = useMemo(() => {
    return pendingSettlements.filter((s) => {
      const matchSearch = !searchKeyword.trim() || s.merchant?.toLowerCase().includes(searchKeyword.toLowerCase()) || (s.txId && String(s.txId).toLowerCase().includes(searchKeyword.toLowerCase()));
      return matchSearch;
    });
  }, [pendingSettlements, searchKeyword]);

  // 입금예정: 전체 목록 (기간 필터 없음), 검색어만 적용
  const filteredSettlements = useMemo(() => {
    return settlements.filter((s) => {
      const matchSearch = !searchKeyword.trim() || s.merchant?.toLowerCase().includes(searchKeyword.toLowerCase()) || (s.txId && String(s.txId).toLowerCase().includes(searchKeyword.toLowerCase()));
      return matchSearch;
    });
  }, [settlements, searchKeyword]);

  // 입금완료: API에서 이미 월별 조회됨, 검색어만 적용 (day 그룹 단위, items 중 가맹점명 매칭)
  const filteredDeposits = useMemo(() => {
    if (!searchKeyword.trim()) return deposits;
    const kw = searchKeyword.toLowerCase();
    return deposits.filter((group) =>
      group.items?.some((item) => item.merchant?.toLowerCase().includes(kw))
    );
  }, [deposits, searchKeyword]);

  // 정산예정 데이터 로드 시 전체선택 (데이터 없으면 초기화)
  useEffect(() => {
    if (filteredPendingSettlements.length > 0) {
      setSelectedSettlementIds(filteredPendingSettlements.map((s) => s.id));
    } else {
      setSelectedSettlementIds([]);
    }
  }, [filteredPendingSettlements]);

  // 입금예정 데이터 로드 시 전체선택 (외부 정산 모드에서는 지급 선택 자체를 비활성화)
  useEffect(() => {
    if (externalSettlementMode || filteredSettlements.length === 0) {
      setSelectedIds([]);
      return;
    }
    const selectableIds = filteredSettlements
      .filter(isPayoutSelectable)
      .map((s) => s.id);
    setSelectedIds(selectableIds);
  }, [externalSettlementMode, filteredSettlements]);

  const parseDateStr = (s) => {
    if (!s) return new Date(0);
    const [y, m, d] = String(s).split('.').map(Number);
    const year = y >= 100 ? y : 2000 + (y || 0);
    return new Date(year, (m || 1) - 1, d || 1);
  };

  // 통계: 입금완료 월별 데이터 합산 (관리자매출, 승인금액, 정산금액, 입금금액)
  const stats = useMemo(() => {
    if (summary) {
      const revenue = Number(summary.revenue || 0);
      const approved = Number(summary.approvedAmount || 0);
      const settle = Number(summary.settlementAmount || 0);
      const transfer = Number(summary.transferredAmount || 0);
      return {
        salesFormatted: revenue.toLocaleString(),
        approveFormatted: approved.toLocaleString(),
        settleFormatted: settle.toLocaleString(),
        depositFormatted: transfer.toLocaleString(),
      };
    }

    // 입금완료 월별 합산 (filteredDeposits = day별 그룹)
    let totalApprove = 0;
    let totalSettle = 0;
    let totalDepositAmount = 0;
    let totalFee = 0;
    let totalAdminFee = 0;
    filteredDeposits.forEach((group) => {
      totalApprove += Number(group.approveAmountNum ?? 0);
      totalSettle += Number(group.settleAmountNum ?? 0);
      totalDepositAmount += Number(group.depositAmountNum ?? 0);
      totalFee += Number(group.feeAmountNum ?? 0);
      totalAdminFee += Number(group.adminFeeAmountNum ?? 0);
    });
      return {
        salesFormatted: totalFee.toLocaleString(),
        approveFormatted: totalApprove.toLocaleString(),
        settleFormatted: totalSettle.toLocaleString(),
        depositFormatted: totalDepositAmount.toLocaleString(),
        adminFeeFormatted: totalAdminFee.toLocaleString(),
      };
  }, [summary, filteredDeposits]);

  // 입금완료 통계판넬용 기간 표시 (26.02.01~26.02.28 형식)
  const depositPeriodLabel = useMemo(() => {
    const s = (startDate || '').replace(/\.$/, '').trim();
    const e = (endDate || '').replace(/\.$/, '').trim();
    if (!s || !e) return '-';
    const shortYear = (str) => (str.length >= 8 ? str.slice(2) : str);
    return `${shortYear(s)}~${shortYear(e)}`;
  }, [startDate, endDate]);

  const formatDateHeader = (dateStr) => {
    const [, month, day] = (dateStr || '').split('.');
    return `${parseInt(month, 10) || 0}/${parseInt(day, 10) || 0}`;
  };

  // 오늘 업무 - 정산예정 (첫 날짜 그룹 또는 전체 합산)
  const pendingSettlementToday = useMemo(() => {
    const dates = getDateGroups(filteredPendingSettlements, true);
    const sorted = Object.keys(dates).sort((a, b) => parseDateStr(b) - parseDateStr(a));
    const first = sorted[0];
    if (!first || !dates[first]) return null;
    const g = dates[first];
    return {
      date: formatDateHeader(first),
      dateKey: first,
      count: g.count,
      totalExpected: `${g.totalFormatted}원`,
      group: g,
    };
  }, [filteredPendingSettlements]);

  // 오늘 업무 - 입금예정 요약 (정산확정 READY 건들 전체 합산, 목업 제거)
  const pendingDepositToday = useMemo(() => {
    if (!filteredSettlements.length) return null;

    const count = filteredSettlements.length;
    const totalDepositAmount = filteredSettlements.reduce((sum, item) => {
      const num =
        item.depositAmountNum ??
        item.settleAmountNum ??
        getSettleFromApprove(parseAmount(item.approveAmount));
      return sum + Number(num || 0);
    }, 0);

    const names = filteredSettlements.map((i) => i.merchant).filter(Boolean);
    const desc =
      names.length <= 1 ? (names[0] || '') : `${names[0]} 외 ${names.length - 1}건`;

    return {
      targetCount: count,
      totalAmount: `${totalDepositAmount.toLocaleString()}원`,
      description: desc,
      group: { items: filteredSettlements },
      dateKey: '',
      isMock: false,
    };
  }, [filteredSettlements]);

  const sortedPendingSettleDates = () => {
    const grouped = getDateGroups(filteredPendingSettlements, true);
    return Object.keys(grouped).sort((a, b) => parseDateStr(b) - parseDateStr(a));
  };

  const sortedSettleDates = () => {
    const grouped = getDateGroups(filteredSettlements, true);
    return Object.keys(grouped).sort((a, b) => parseDateStr(b) - parseDateStr(a));
  };

  const sortedDepositDates = () => {
    const grouped = getDateGroups(filteredDeposits, false);
    return Object.keys(grouped).sort((a, b) => parseDateStr(b) - parseDateStr(a));
  };

  const handleToggleDateCard = (group, e) => {
    e.stopPropagation();
    const ids = group.items.map((i) => i.id);
    const allSelected = ids.every((id) => selectedIds.includes(id));
    if (allSelected) setSelectedIds(selectedIds.filter((id) => !ids.includes(id)));
    else setSelectedIds([...new Set([...selectedIds, ...ids])]);
  };

  const handleToggleMerchant = (id, e) => {
    e.stopPropagation();
    if (selectedIds.includes(id)) setSelectedIds(selectedIds.filter((itemId) => itemId !== id));
    else setSelectedIds([...selectedIds, id]);
  };

  const toggleExpand = (expandKey, e) => {
    e.stopPropagation();
    setExpandedId(expandedId === expandKey ? null : expandKey);
  };

  // 카드 타입별 금액 라벨: 정산예정 | 정산확정 | 입금완료
  const getAmountLabels = (cardType) => {
    if (cardType === 'pending') return { settle: '정산예정금액' };
    if (cardType === 'settlement') return { settle: '정산금액' };
    return { settle: '정산금액', deposit: '입금금액' };
  };

  // 하루치 정산금액 카드 1장 (날짜별). 정산확정 입금금액 = 체크한 것만 합산(기본 0)
  const renderDateCard = (dateKey, group, cardType) => {
    const isSettlement = cardType === 'pending' || cardType === 'settlement';
    const labels = getAmountLabels(cardType);
    const expandKey = `${dateKey}-${cardType}`;
    const isExpanded = expandedId === expandKey;
    const isSelected = selectedItem?.date === dateKey && selectedItem?.type === cardType;
    const cardPayload = { type: cardType, date: dateKey, ...group };

    // 정산예정·정산확정: 승인금액, 취소금액, 정산예정금액/정산금액 (3칸) | 입금완료: + 입금금액 (4칸)
    return (
      <div
        key={expandKey}
        className={`rounded border overflow-hidden shadow-md transition-all ${isSelected ? 'border-[var(--brand-primary)] shadow-lg' : 'border-[#e5e8eb] hover:shadow-lg hover:border-[#d0d0d0]'}`}
      >
        <div
          onClick={() => { setSelectedItem(cardPayload); setSelectedMerchant(null); }}
          className="bg-white px-5 py-3 cursor-pointer transition-all"
        >
          <div className="flex items-center gap-4">
            {!isSettlement && (
              <div className="w-5 h-5 shrink-0 flex items-center justify-center" aria-hidden>
                <CheckCircle size={20} className="text-[var(--brand-primary)]" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-[20px] font-bold text-[#191f28] truncate">{formatDateHeader(dateKey)}</h3>
                <ChevronRight size={18} className="text-[#adb5bd] shrink-0" />
              </div>
            </div>
            <div className="bg-[#f9fafb] rounded-xl px-3 py-1 w-[140px] shrink-0">
              <div className="text-[12px] text-[#8b95a1] font-bold mb-0.5">승인금액</div>
              <div className="text-[15px] text-[#191f28] text-right">{group.totalApproveFormatted}원</div>
            </div>
            <div className="bg-[#f9fafb] rounded-xl px-3 py-1 w-[140px] shrink-0">
              <div className="text-[12px] text-[#8b95a1] font-bold mb-0.5">취소금액</div>
              <div className="text-[15px] text-[#4e5968] text-right">{group.totalCancelFormatted}원</div>
            </div>
            <div className="bg-[#f9fafb] rounded-xl px-3 py-1 w-[140px] shrink-0">
              <div className="text-[12px] text-[#8b95a1] font-bold mb-0.5">{labels.settle}</div>
              <div className="text-[15px] text-[var(--brand-primary)] text-right">{group.totalFormatted}원</div>
            </div>
            {cardType === 'deposit' && (
              <div className="bg-[#f9fafb] rounded-xl px-3 py-1 w-[140px] shrink-0">
                <div className="text-[12px] text-[#8b95a1] font-bold mb-0.5">{labels.deposit}</div>
                <div className="text-[15px] text-[var(--brand-primary)] text-right">{group.totalDepositFormatted}원</div>
              </div>
            )}
            <button
              type="button"
              onClick={(e) => toggleExpand(expandKey, e)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg shrink-0 transition-colors ${isExpanded ? 'bg-[var(--brand-primary-soft)] text-[var(--brand-primary)]' : 'hover:bg-[#f2f4f6] text-[#4e5968]'}`}
              title={isExpanded ? (cardType === 'pending' ? '정산예정 접기' : cardType === 'settlement' ? '정산확정 접기' : '입금내역 접기') : (cardType === 'pending' ? '정산예정 보기' : cardType === 'settlement' ? '정산확정 보기' : '입금내역 보기')}
            >
              <List size={18} />
              <span className="text-[13px] font-bold whitespace-nowrap">{cardType === 'pending' ? '정산예정' : cardType === 'settlement' ? '정산확정' : '입금내역'}</span>
              {isExpanded ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
            </button>
          </div>
        </div>
        {isExpanded && group.items.length > 0 && (
          <div className="bg-[#f9fafb] border-t border-[#e5e8eb] px-4 py-3">
            <div className="text-[12px] font-bold text-[#8b95a1] mb-2">{cardType === 'pending' ? '정산예정' : cardType === 'settlement' ? '정산확정' : '입금내역'} ({group.items.length}건)</div>
            <div className="overflow-x-auto custom-scrollbar">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-[#8b95a1] font-bold border-b border-[#e5e8eb]">
                    {isSettlement && <th className="w-10 py-2 pr-1 text-left">선택</th>}
                    <th className="text-left py-2 pr-3">가맹점명</th>
                    <th className="text-left py-2 pr-3">승인일</th>
                    <th className="text-right py-2 pr-3">승인금액</th>
                    <th className="text-right py-2 pr-3">취소금액</th>
                    <th className="text-right py-2 pr-3">{labels.settle}</th>
                    {cardType === 'deposit' && <th className="text-right py-2 pr-3">{labels.deposit}</th>}
                    {cardType === 'deposit' && <th className="text-left py-2">입금일</th>}
                  </tr>
                </thead>
                <tbody>
                  {!group.items?.length ? (
                    <tr>
                      <td colSpan={20} className="py-8 text-center text-[15px] font-medium text-[#8b95a1]">
                        데이터가 없습니다.
                      </td>
                    </tr>
                  ) : (
                    group.items.map((item) => (
                      <tr key={item.id} className="border-b border-[#f2f4f6]">
                        {isSettlement && (
                          <td className="py-2 pr-1" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              className="w-4 h-4 rounded"
                              checked={selectedIds.includes(item.id)}
                              onChange={(e) => handleToggleMerchant(item.id, e)}
                            />
                          </td>
                        )}
                        <td className="py-2 pr-3 font-medium text-[#191f28]">{item.merchant}</td>
                        <td className="py-2 pr-3 text-[#4e5968] whitespace-nowrap">{item.approveDate}</td>
                        <td className="py-2 pr-3 text-right text-[#191f28]">{item.approveAmount}원</td>
                        <td className="py-2 pr-3 text-right text-[#4e5968]">{item.cancelAmount}원</td>
                        <td className="py-2 pr-3 text-right text-[var(--brand-primary)]">{item.settleAmount}원</td>
                        {cardType === 'deposit' && <td className="py-2 pr-3 text-right text-[var(--brand-primary)]">{item.depositAmount}원</td>}
                        {cardType === 'deposit' && <td className="py-2 text-[#4e5968]">{item.depositDate}</td>}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  const toggleRow = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const allPendingSelected =
    filteredPendingSettlements.length > 0 &&
    filteredPendingSettlements.every((s) => selectedSettlementIds.includes(s.id));

  // 입금완료내역: day별 그룹 (API data.days → 이미 그룹 구조)
  const groupedDepositsByDate = useMemo(() => {
    return [...filteredDeposits].sort((a, b) => parseDateStr(b.depositDate) - parseDateStr(a.depositDate));
  }, [filteredDeposits]);

  const hasRealDepositRows = filteredSettlements.length > 0;
  const depositModalRows = filteredSettlements;
  const selectableDepositRows = externalSettlementMode ? [] : filteredSettlements.filter(isPayoutSelectable);
  const allDepositSelected =
    selectableDepositRows.length > 0 &&
    selectableDepositRows.every((s) => selectedIds.includes(s.id));

  const selectedPayoutRows = selectableDepositRows.filter((s) => selectedIds.includes(s.id));
  const kgInicisPayoutRows = selectableDepositRows.filter(isKgInicisPayoutSettlement);
  const selectedKgInicisRows = kgInicisPayoutRows.filter((s) => selectedIds.includes(s.id));
  const kgBatchSelectionStarted = selectedKgInicisRows.length > 0;
  const kgBatchSelectionComplete =
    kgInicisPayoutRows.length > 0 &&
    kgInicisPayoutRows.every((s) => selectedIds.includes(s.id));
  const payoutActionEnabled =
    !externalSettlementMode &&
    selectedPayoutRows.length > 0 &&
    (!kgBatchSelectionStarted || kgBatchSelectionComplete) &&
    !payoutSubmitting;
  const selectedPayoutAmount = sumPayoutSettlementAmounts(selectedPayoutRows);
  const selectedPayoutDates = [...new Set(
    selectedPayoutRows
      .map((row) => formatPayoutBatchDate(row.payoutPayDate || String(row.settleDate || '').replace(/\./g, '-')))
      .filter((value) => value && value !== '-'),
  )];
  const selectedPayoutDateLabel = selectedPayoutDates.length === 0
    ? '-'
    : selectedPayoutDates.length === 1
      ? selectedPayoutDates[0]
      : `${selectedPayoutDates[0]} 외 ${selectedPayoutDates.length - 1}개 지급일`;

  const activePayoutBatches = useMemo(() => {
    const activeStatuses = new Set(['REQUESTED', 'ACCEPTED', 'CONFIRMING', 'UNKNOWN', 'FAILED']);
    return payoutBatches.filter((batch) => activeStatuses.has(String(batch.status || '').toUpperCase()));
  }, [payoutBatches]);

  const incompletePayoutBatchSettlementIds = useMemo(() => {
    const ids = new Set();
    payoutBatches.forEach((batch) => {
      if (String(batch.status || '').toUpperCase() === 'SUCCESS') return;
      batch.items.forEach((item) => ids.add(String(item.settlementId)));
    });
    return ids;
  }, [payoutBatches]);

  const completedPayoutBatchSettlementIds = useMemo(() => {
    const ids = new Set();
    payoutBatches.forEach((batch) => {
      if (String(batch.status || '').toUpperCase() !== 'SUCCESS') return;
      batch.items.forEach((item) => ids.add(String(item.settlementId)));
    });
    return ids;
  }, [payoutBatches]);

  const payoutBatchSettlementPaymentDates = useMemo(() => {
    const dates = new Map();
    groupedDepositsByDate.forEach((group) => {
      group.items.forEach((item) => {
        const paymentDate = item.paymentDate || '';
        if (paymentDate) dates.set(String(item.id), paymentDate);
      });
    });
    return dates;
  }, [groupedDepositsByDate]);

  const standaloneDepositGroups = useMemo(() => {
    if (!ADMIN_CP_PAYOUT_TENANT_ENABLED || incompletePayoutBatchSettlementIds.size === 0) {
      return groupedDepositsByDate;
    }
    return groupedDepositsByDate.map((group) => {
      const items = group.items.filter((item) => !incompletePayoutBatchSettlementIds.has(String(item.id)));
      return {
        ...group,
        items,
        count: items.length,
        approveAmountNum: items.reduce((sum, item) => sum + Number(item.approveAmountNum || 0), 0),
        feeAmountNum: items.reduce((sum, item) => sum + Number(item.feeAmountNum || 0), 0),
        settleAmountNum: items.reduce((sum, item) => sum + Number(item.settleAmountNum || 0), 0),
        depositAmountNum: items.reduce((sum, item) => sum + Number(item.depositAmountNum || 0), 0),
        adminFeeAmountNum: items.reduce((sum, item) => sum + Number(item.adminFeeAmountNum || 0), 0),
      };
    }).filter((group) => group.items.length > 0);
  }, [groupedDepositsByDate, incompletePayoutBatchSettlementIds]);

  const handlePayoutBatchResultCheck = async (batchId) => {
    if (externalSettlementMode) {
      showToast('GASYSTEM에서는 지급대행을 실행하지 않습니다. 지급예정일에 외부 정산 완료 상태로 자동 변경됩니다.', 'error');
      return;
    }
    const key = String(batchId);
    if (checkingPayoutBatchIds.has(key)) return;
    setCheckingPayoutBatchIds((prev) => new Set(prev).add(key));
    try {
      const baseUrl = process.env.REACT_APP_API || '';
      const res = await axios.post(
        baseUrl + `/api/admin/cp-settlement/payout-batches/${batchId}/result`,
        {},
        { withCredentials: true },
      );
      if (res?.data?.code !== 1000) {
        throw new Error(res?.data?.msg || '송금결과 확인에 실패했습니다.');
      }

      const responseBatch = res?.data?.data?.batch;
      const nextStatus = String(
        responseBatch?.status
        ?? res?.data?.data?.status
        ?? '',
      ).toUpperCase();
      if (responseBatch) {
        const normalized = normalizePayoutBatch(responseBatch);
        setPayoutBatches((prev) => prev.map((batch) => (
          String(batch.batchId) === key ? { ...batch, ...normalized } : batch
        )));
      }

      if (nextStatus === 'SUCCESS') {
        showToast('송금 성공이 확인되었습니다. 연결된 정산을 입금 완료로 갱신합니다.');
      } else if (['CONFIRMING', 'UNKNOWN'].includes(nextStatus)) {
        showToast('아직 송금결과가 확정되지 않았습니다. 잠시 후 다시 확인해 주세요.');
      } else if (nextStatus === 'FAILED') {
        showToast(res?.data?.msg || '지급 신청 실패가 확인되었습니다.', 'error');
      } else {
        showToast(res?.data?.msg || '송금결과를 확인했습니다.');
      }
      setReloadToken((token) => token + 1);
    } catch (error) {
      const message = error?.response?.data?.msg || error?.message || '송금결과 확인 중 오류가 발생했습니다.';
      if (String(message).toUpperCase().includes('SC02')) {
        showToast('아직 조회 가능한 송금결과가 없습니다. 잠시 후 다시 확인해 주세요.');
        setReloadToken((token) => token + 1);
      } else {
        showToast(message, 'error');
      }
    } finally {
      setCheckingPayoutBatchIds((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const handlePayoutBatchRequest = async () => {
    if (externalSettlementMode) {
      showToast('GASYSTEM에서는 지급대행을 실행하지 않습니다. 지급예정일에 외부 정산 완료 상태로 자동 변경됩니다.', 'error');
      setConfirmDepositOpen(false);
      setIsDepositModalOpen(false);
      return;
    }
    if (payoutSubmitting) return;
    if (selectedPayoutRows.length === 0) {
      showToast('입금할 항목을 선택해주세요.', 'error');
      return;
    }
    if (kgBatchSelectionStarted && !kgBatchSelectionComplete) {
      const missingCount = kgInicisPayoutRows.length - selectedKgInicisRows.length;
      showToast(
        `같은 하위몰·지급일의 지급 가능 정산을 모두 선택해야 합니다. (누락 ${missingCount}건)`,
        'error',
      );
      return;
    }

    const isKgBatchRequest =
      ADMIN_CP_PAYOUT_TENANT_ENABLED && selectedKgInicisRows.length > 0;

    setPayoutSubmitting(true);
    try {
      const baseUrl = process.env.REACT_APP_API || '';
      const response = await axios.post(
        baseUrl + '/api/admin/cp-settlement/payout-batch',
        { ids: selectedPayoutRows.map((row) => row.id) },
        { withCredentials: true },
      );
      const body = response?.data || {};
      if (body.code !== 1000) {
        const error = new Error(body.msg || '지급 신청에 실패했습니다.');
        error.apiData = body.data || {};
        throw error;
      }

      const data = body.data || {};
      const resultRows = Array.isArray(data.rows) ? data.rows : [];
      const failedRows = resultRows.filter((row) => String(row?.status || '').toUpperCase() === 'FAILED');
      const failedCount = Number(data.failedCount ?? failedRows.length ?? 0);
      const acceptedCount = Number(data.acceptedCount ?? 0);
      const unknownCount = Number(data.unknownCount ?? 0);
      const successCount = Number(data.successCount ?? 0);
      if (isKgBatchRequest) {
        setPayoutBatchResult({
          message: body.msg || '',
          successCount,
          acceptedCount,
          unknownCount,
          failedCount,
          skippedCount: Number(data.skippedCount ?? 0),
          total: Number(data.total ?? resultRows.length),
          rows: resultRows,
        });

        if (failedCount > 0) {
          const detail = failedRows.map((row) => row?.message).filter(Boolean).join(' / ');
          showToast(detail || `지급 신청 ${failedCount}개 그룹이 실패했습니다.`, 'error');
        } else if (unknownCount > 0) {
          showToast('일부 지급 신청의 처리 결과를 확인해야 합니다.');
        } else if (acceptedCount > 0) {
          showToast(`KG이니시스 지급 신청 ${acceptedCount}건이 접수되었습니다.`);
        } else if (successCount > 0) {
          showToast(`입금 처리 ${successCount}건이 완료되었습니다.`);
        } else {
          showToast(body.msg || '지급 신청 결과를 확인해 주세요.');
        }
      } else {
        showToast('입금 승인 처리가 완료되었습니다.');
      }

      setConfirmDepositOpen(false);
      setIsDepositModalOpen(false);
      setSelectedIds([]);
      setDepositOtpToken('');
      setReloadToken((token) => token + 1);
    } catch (error) {
      const responseData = error?.response?.data || {};
      const apiData = responseData.data || error?.apiData || {};
      const payoutDisabled = Number(responseData.code) === 4003
        || apiData.payoutExecutionEnabled === false
        || apiData.payout_execution_enabled === false;
      if (payoutDisabled) {
        showToast('GASYSTEM에서는 지급대행을 실행하지 않습니다. 지급예정일에 외부 정산 완료 상태로 자동 변경됩니다.', 'error');
        setConfirmDepositOpen(false);
        setIsDepositModalOpen(false);
        return;
      }
      const missingIds = Array.isArray(apiData.missingSettlementIds) ? apiData.missingSettlementIds : [];
      const message = missingIds.length > 0
        ? `같은 하위몰·지급일의 지급 가능 정산을 모두 선택해야 합니다. (누락 ${missingIds.length}건)`
        : responseData.msg || error?.message || '지급 신청 처리 중 오류가 발생했습니다.';
      showToast(message, 'error');
    } finally {
      setPayoutSubmitting(false);
    }
  };

  useEffect(() => {
    if (!externalSettlementMode) return;
    setSelectedIds([]);
    setConfirmDepositOpen(false);
    setIsDepositModalOpen(false);
    setPayoutBatchResult(null);
  }, [externalSettlementMode]);

  const toggleHubExpand = (key) => {
    setHubExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const detailRowsForPendingItem = (s) => [
    { id: `${s.id}-ap`, title: '승인금액', price: s.approveAmountNum ?? parseAmount(s.approveAmount) },
    { id: `${s.id}-ca`, title: '취소금액', price: s.cancelAmountNum ?? 0 },
    { id: `${s.id}-st`, title: '정산예정금액', price: s.settleAmountNum ?? 0 },
  ];

  const detailRowsForTransferItem = (s) => {
    const settle = s.settleAmountNum ?? 0;
    const dep = s.depositAmountNum ?? settle;
    return [
      { id: `${s.id}-ap`, title: '승인금액', price: s.approveAmountNum ?? parseAmount(s.approveAmount) },
      { id: `${s.id}-ca`, title: '취소금액', price: s.cancelAmountNum ?? 0 },
      { id: `${s.id}-st`, title: '정산금액', price: settle },
      { id: `${s.id}-dp`, title: '입금예정액', price: dep },
    ];
  };

  const detailRowsForDepositHistoryItem = (item) => {
    const ap = item.approveAmountNum ?? parseAmount(item.approveAmount);
    const fee = item.feeAmountNum ?? parseAmount(item.feeAmount ?? '0');
    const st = item.settleAmountNum ?? parseAmount(item.settleAmount ?? '0');
    const adm = item.adminFeeAmountNum ?? parseAmount(item.adminFeeAmount ?? '0');
    const dep = item.depositAmountNum ?? parseAmount(item.depositAmount ?? '0');
    return [
      { id: `${item.id}-ap`, title: '승인금액', price: ap },
      { id: `${item.id}-fe`, title: '수수료', price: fee },
      { id: `${item.id}-st`, title: '정산금액', price: st },
      { id: `${item.id}-dp`, title: '입금금액', price: dep || st },
      { id: `${item.id}-ad`, title: '본사수익', price: adm },
    ];
  };

  const dotDateMatchesYm = (dotDate, y, m) => {
    if (!dotDate || !y || !m) return false;
    const parts = String(dotDate).replace(/\.$/, '').trim().split('.');
    if (parts.length < 2) return false;
    return String(parts[0]) === String(y) && String(parts[1]).padStart(2, '0') === String(m).padStart(2, '0');
  };

  const settlementsForPendingHistory = useMemo(
    () => settlements.filter((s) => dotDateMatchesYm(s.settleDate || s.approveDate, pendingHistYear, pendingHistMonth)),
    [settlements, pendingHistYear, pendingHistMonth],
  );

  const depositItemsForTransferHistory = useMemo(() => {
    const rows = [];
    deposits.forEach((g) => {
      (g.items || []).forEach((item) => {
        if (incompletePayoutBatchSettlementIds.has(String(item.id))) return;
        const d = g.depositDate || item.settleDate || item.approveDate;
        if (dotDateMatchesYm(d, transferHistYear, transferHistMonth)) {
          rows.push({ ...item, _groupDepositDate: g.depositDate });
        }
      });
    });
    return rows;
  }, [deposits, transferHistYear, transferHistMonth, incompletePayoutBatchSettlementIds]);

  const groupBySettleAndApprove = (list) => {
    const grouped = {};
    list.forEach((s) => {
      const sk = s.settleDate || '-';
      const pk = s.approveDate || '-';
      const key = `${sk}|||${pk}`;
      if (!grouped[key]) grouped[key] = { settleDate: sk, approveDate: pk, items: [] };
      grouped[key].items.push(s);
    });
    return Object.values(grouped).sort((a, b) => parseDateStr(b.settleDate) - parseDateStr(a.settleDate));
  };

  return (
    <div className="relative flex-1 flex flex-col min-h-0 overflow-hidden bg-white py-4 px-8 w-full">
      <Tooltip id="settlement-pending-tooltip" place="right" positionStrategy="absolute" className="max-w-[300px] text-[11px] z-[9999] whitespace-nowrap" isOpen={showAllOpen || hoveredTooltipId === 'settlement-pending'} setIsOpen={() => {}} />
      <Tooltip id="settlement-deposit-expected-tooltip" place="right" positionStrategy="absolute" className="max-w-[300px] text-[11px] z-[9999] whitespace-nowrap" isOpen={showAllOpen || hoveredTooltipId === 'settlement-deposit-expected'} setIsOpen={() => {}} />
      <Tooltip id="settlement-deposit-completed-tooltip" place="right" positionStrategy="absolute" className="max-w-[300px] text-[11px] z-[9999] whitespace-nowrap" isOpen={showAllOpen || hoveredTooltipId === 'settlement-deposit-completed'} setIsOpen={() => {}} />
      <Tooltip id="settlement-pending-list-tooltip" place="left" positionStrategy="absolute" className="max-w-[300px] text-[11px] z-[9999] whitespace-nowrap" isOpen={showAllOpen || hoveredTooltipId === 'settlement-pending-list'} setIsOpen={() => {}} />
      <Tooltip id="settlement-deposit-list-tooltip" place="left" positionStrategy="absolute" className="max-w-[300px] text-[11px] z-[9999] whitespace-nowrap" isOpen={showAllOpen || hoveredTooltipId === 'settlement-deposit-list'} setIsOpen={() => {}} />
      <Tooltip id="settlement-pending-total-tooltip" place="right" positionStrategy="absolute" className="max-w-[300px] text-[11px] z-[9999] whitespace-nowrap" isOpen={showAllOpen || hoveredTooltipId === 'settlement-pending-total'} setIsOpen={() => {}} />
      <Tooltip id="settlement-confirm-btn-tooltip" place="top" positionStrategy="absolute" className="max-w-[320px] text-[11px] z-[9999] whitespace-nowrap" isOpen={showAllOpen || hoveredTooltipId === 'settlement-confirm-btn'} setIsOpen={() => {}} />
      <Tooltip id="settlement-deposit-total-tooltip" place="right" positionStrategy="absolute" className="max-w-[300px] text-[11px] z-[9999] whitespace-nowrap" isOpen={showAllOpen || hoveredTooltipId === 'settlement-deposit-total'} setIsOpen={() => {}} />
      <Tooltip id="settlement-deposit-btn-tooltip" place="top" positionStrategy="absolute" className="max-w-[300px] text-[11px] z-[9999] whitespace-nowrap" isOpen={showAllOpen || hoveredTooltipId === 'settlement-deposit-btn'} setIsOpen={() => {}} />
      <Tooltip id="settlement-deposit-fail-tooltip" place="right" positionStrategy="absolute" className="max-w-[300px] text-[11px] z-[9999] whitespace-nowrap" isOpen={showAllOpen || hoveredTooltipId === 'settlement-deposit-fail'} setIsOpen={() => {}} />
      <Tooltip id="deposit-stat-period-tooltip" place="left" positionStrategy="absolute" className="max-w-[180px] text-[11px] z-[9999] whitespace-nowrap" isOpen={showAllOpen || hoveredTooltipId === 'deposit-stat-period'} setIsOpen={() => {}} />
      <Tooltip id="deposit-stat-approve-tooltip" place="left" positionStrategy="absolute" className="max-w-[180px] text-[11px] z-[9999] whitespace-nowrap" isOpen={showAllOpen || hoveredTooltipId === 'deposit-stat-approve'} setIsOpen={() => {}} />
      <Tooltip id="deposit-stat-fee-tooltip" place="left" positionStrategy="absolute" className="max-w-[180px] text-[11px] z-[9999] whitespace-nowrap" isOpen={showAllOpen || hoveredTooltipId === 'deposit-stat-fee'} setIsOpen={() => {}} />
      <Tooltip id="deposit-stat-settle-tooltip" place="left" positionStrategy="absolute" className="max-w-[180px] text-[11px] z-[9999] whitespace-nowrap" isOpen={showAllOpen || hoveredTooltipId === 'deposit-stat-settle'} setIsOpen={() => {}} />
      <Tooltip id="deposit-stat-deposit-tooltip" place="left" positionStrategy="absolute" className="max-w-[180px] text-[11px] z-[9999] whitespace-nowrap" isOpen={showAllOpen || hoveredTooltipId === 'deposit-stat-deposit'} setIsOpen={() => {}} />
      <Tooltip id="deposit-excel-download-tooltip" place="left" positionStrategy="absolute" className="max-w-[300px] text-[11px] z-[9999] whitespace-nowrap" isOpen={showAllOpen || hoveredTooltipId === 'deposit-excel-download'} setIsOpen={() => {}} />
      <Tooltip id="deposit-row-expand-tooltip" place="right" positionStrategy="absolute" className="max-w-[300px] text-[11px] z-[9999] whitespace-nowrap" isOpen={showAllOpen || hoveredTooltipId === 'deposit-row-expand'} setIsOpen={() => {}} />
      <PayoutDeadlineNotice />
      {externalSettlementMode && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-blue-900">
          <div className="min-w-0">
            <p className="text-[13px] font-bold">외부 정산 자동 완료</p>
            <p className="mt-0.5 text-[12px] leading-5">지급예정일에 외부 정산 완료 상태로 자동 변경됩니다. 이 시스템에서는 별도의 지급지시를 실행하지 않습니다.</p>
            {settlementCapabilityLoading && <p className="mt-1 text-[11px] text-blue-600">정산 기능 설정 확인 중...</p>}
            {settlementCapabilityError && <p className="mt-1 text-[11px] font-semibold text-amber-700">정산 기능 설정을 확인하지 못했습니다. 지급 기능은 안전하게 차단된 상태입니다.</p>}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {settlementCapabilityError && (
              <button type="button" onClick={() => setSettlementCapabilityReloadToken((token) => token + 1)} className="rounded-md border border-blue-200 bg-white px-3 py-2 text-[12px] font-bold text-blue-700 hover:bg-blue-100">설정 재조회</button>
            )}
            <button type="button" onClick={() => setReloadToken((token) => token + 1)} className="inline-flex items-center gap-1.5 rounded-md bg-blue-700 px-3 py-2 text-[12px] font-bold text-white hover:bg-blue-800"><RefreshCw size={14} />목록 새로고침</button>
          </div>
        </div>
      )}
      <div className="w-full flex gap-4 flex-1 min-h-0 overflow-hidden">
        {/* 정산예정 25% - 일자별 그룹핑 */}
        <div className="basis-1/5 min-w-0 flex flex-col min-h-0">
          {SETTLEMENT_HUB_UI_V1 ? (
          <div className="flex-1 min-h-0 bg-white border border-slate-200 rounded-[4px] overflow-hidden flex flex-col shadow-[0_8px_20px_rgba(15,23,42,0.04)]">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-2 shrink-0">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-9 h-9 rounded-[4px] bg-orange-50 flex items-center justify-center text-orange-500 shrink-0">
                  <Layers size={18} />
                </div>
                <div className="min-w-0">
                  <h3 className="text-[15px] font-bold text-slate-800 leading-tight flex items-center gap-1.5">
                    정산예정
                    <span
                      data-tooltip-id="settlement-pending-tooltip"
                      data-tooltip-html="각 가맹점의 결제금액을 확인해 확정 처리합니다.<br/>확정된 내역은 입금예정으로 이동합니다."
                      onMouseEnter={() => !showAllOpen && setHoveredTooltipId('settlement-pending')}
                      onMouseLeave={() => setHoveredTooltipId(null)}
                      className="inline-flex cursor-help text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      <HelpCircle size={14} strokeWidth={2} />
                    </span>
                  </h3>
                  <p className="text-[11px] text-slate-400 font-medium">승인건을 확정합니다.</p>
                </div>
              </div>
              <div className="flex bg-[#f2f4f6] p-0.5 rounded-[4px] border border-[#e5e8eb] shrink-0">
                <button
                  type="button"
                  onClick={() => setPendingPanelTab('scheduled')}
                  className={`px-2.5 py-1 text-[11px] font-bold rounded-[4px] transition-all ${pendingPanelTab === 'scheduled' ? 'bg-white text-orange-600 shadow-sm ring-1 ring-[#e5e8eb]' : 'text-[#8b95a1]'}`}
                >
                  예정내역
                </button>
                <button
                  type="button"
                  onClick={() => setPendingPanelTab('completed')}
                  className={`px-2.5 py-1 text-[11px] font-bold rounded-[4px] transition-all ${pendingPanelTab === 'completed' ? 'bg-white text-orange-600 shadow-sm ring-1 ring-[#e5e8eb]' : 'text-[#8b95a1]'}`}
                >
                  완료내역
                </button>
              </div>
            </div>
            {pendingPanelTab === 'scheduled' ? (
              <>
                <div
                  className="px-3 h-10 min-h-[40px] box-border flex items-center gap-2 border-b border-slate-100 shrink-0 cursor-pointer select-none"
                  style={{ backgroundColor: 'rgba(249, 249, 249, 0.5)' }}
                  onClick={() => {
                    if (allPendingSelected) setSelectedSettlementIds([]);
                    else setSelectedSettlementIds(filteredPendingSettlements.map((s) => s.id));
                  }}
                >
                  <div className={`w-4 h-4 rounded-[4px] border-2 flex items-center justify-center shrink-0 ${allPendingSelected ? 'bg-[#f96600] border-[#f96600]' : 'bg-white border-slate-300'}`}>
                    {allPendingSelected && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
                  </div>
                  <span className="text-[12px] text-slate-400">전체 선택</span>
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar p-3 space-y-4" style={{ backgroundColor: '#fdfdfd' }}>
                  {filteredPendingSettlements.length === 0 ? (
                    <div className="flex items-center justify-center h-32 text-[13px] text-slate-400">정산예정 내역이 없습니다.</div>
                  ) : (
                    groupBySettleAndApprove(filteredPendingSettlements).map((grp) => (
                      <div key={`${grp.settleDate}-${grp.approveDate}`} className="flex flex-col gap-2">
                        <div className="flex items-center gap-2 px-0.5">
                          <div className="w-1 h-4 bg-orange-400 rounded-full shrink-0" />
                          <div className="flex flex-wrap items-center gap-x-2 text-[12px]">
                            <span className="text-slate-500">정산일</span>
                            <span className="text-[13px] font-bold text-slate-700">{grp.settleDate}</span>
                            <span className="text-slate-300">|</span>
                            <span className="text-slate-500">결제일</span>
                            <span className="text-[13px] font-bold text-slate-700">{grp.approveDate}</span>
                          </div>
                          <div className="flex-1 h-px bg-slate-100 min-w-[8px]" />
                        </div>
                        <div className="rounded-[4px] border border-slate-200 bg-white overflow-hidden divide-y divide-slate-100">
                          {grp.items.map((s) => {
                            const isChecked = selectedSettlementIds.includes(s.id);
                            const expKey = `p-${s.id}`;
                            const isExp = hubExpandedIds.has(expKey);
                            const details = detailRowsForPendingItem(s);
                            return (
                              <div key={s.id} className="flex flex-col">
                                <div className={`px-3 py-2 flex items-center gap-2 ${isExp ? 'bg-slate-50/80' : ''} ${isChecked ? 'bg-orange-50/40' : ''}`}>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (isChecked) setSelectedSettlementIds(selectedSettlementIds.filter((id) => id !== s.id));
                                      else setSelectedSettlementIds([...selectedSettlementIds, s.id]);
                                    }}
                                    className={`w-4 h-4 rounded-[4px] border-2 flex items-center justify-center shrink-0 ${isChecked ? 'bg-[#f96600] border-[#f96600]' : 'bg-white border-slate-300'}`}
                                  >
                                    {isChecked && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
                                  </button>
                                  <div className="flex-1 min-w-0 flex items-center gap-1.5">
                                    <span className="text-[13px] text-slate-700 truncate">{s.merchant}</span>
                                    <span className="text-[9px] px-1.5 py-0.5 bg-orange-50 text-orange-600 rounded-[4px] font-bold border border-orange-100 shrink-0">예정</span>
                                  </div>
                                  <span className="text-[13px] font-bold text-slate-800 tabular-nums shrink-0">{s.settleAmount}원</span>
                                  <button
                                    type="button"
                                    onClick={() => toggleHubExpand(expKey)}
                                    className={`p-1.5 rounded-[4px] border border-slate-200 shrink-0 ${isExp ? 'bg-slate-200' : 'bg-white'}`}
                                  >
                                    {isExp ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                  </button>
                                </div>
                                {isExp && (
                                  <div className="bg-slate-50/90 border-t border-slate-100 px-3 py-2 pl-10">
                                    <div className="flex justify-between text-[10px] font-bold text-slate-400 uppercase tracking-tight border-b border-slate-100/80 pb-1.5 mb-1">
                                      <span>구분</span>
                                      <span className="pr-1">금액</span>
                                    </div>
                                    {details.map((d) => (
                                      <div key={d.id} className="flex justify-between text-[12px] py-1.5">
                                        <span className="text-slate-600">{d.title}</span>
                                        <span className="font-bold text-slate-800 tabular-nums">{Number(d.price).toLocaleString('ko-KR')}원</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div className="px-4 py-3 border-t border-slate-200 bg-white shrink-0">
                  <div className="flex items-center justify-end gap-6">
                    <div className="text-right">
                      <p className="text-[11px] text-slate-400">선택된 {selectedSettlementIds.length}건 합계</p>
                      <p className="text-[18px] font-black text-orange-500 tabular-nums flex items-center justify-end gap-0.5">
                        {filteredPendingSettlements.filter((s) => selectedSettlementIds.includes(s.id)).reduce((sum, s) => sum + Number((s.settleAmountNum ?? getSettleFromApprove(parseAmount(s.approveAmount))) || 0), 0).toLocaleString()}원
                        <span
                          data-tooltip-id="settlement-pending-total-tooltip"
                          data-tooltip-html="정산예정내역에서 선택한 건의 승인금액 합계입니다."
                          onMouseEnter={() => !showAllOpen && setHoveredTooltipId('settlement-pending-total')}
                          onMouseLeave={() => setHoveredTooltipId(null)}
                          className="inline-flex cursor-help text-slate-400 hover:text-slate-600 shrink-0"
                        >
                          <HelpCircle size={16} strokeWidth={2} />
                        </span>
                      </p>
                    </div>
                    <span
                      data-tooltip-id="settlement-confirm-btn-tooltip"
                      data-tooltip-html="선택한 내역을 정산 확정합니다.<br/>확정 시 입금 예정 내역으로(오타확인) 이동되며, 취소할 수 없습니다."
                      onMouseEnter={() => !showAllOpen && setHoveredTooltipId('settlement-confirm-btn')}
                      onMouseLeave={() => setHoveredTooltipId(null)}
                      className="inline-flex shrink-0 cursor-help"
                    >
                      <button
                        type="button"
                        disabled={!selectedSettlementIds.length}
                        onClick={() => { if (!selectedSettlementIds.length) { showToast('확정할 항목을 선택해주세요.', 'error'); return; } setConfirmSettlementOpen(true); }}
                        className={`px-5 py-2.5 rounded-lg text-[13px] font-bold shrink-0 ${selectedSettlementIds.length > 0 ? 'bg-orange-500 text-white hover:bg-orange-600' : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`}
                      >
                        정산 확정
                      </button>
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="px-3 h-10 min-h-[40px] box-border border-b border-slate-100 flex items-center gap-2 shrink-0 bg-white">
                  <div ref={pendingHistPickerRef} className="relative flex flex-1 min-w-0 items-center gap-2">
                    <div className="flex flex-1 min-w-0 h-9 items-center gap-0.5 bg-white px-1 rounded-[4px] border border-[#e5e8eb] hover:border-orange-400/35 transition-all">
                      <button
                        type="button"
                        onClick={() => changePendingHistMonth(-1)}
                        className="w-8 h-8 shrink-0 flex items-center justify-center rounded-[4px] hover:bg-[#e5e8eb] text-[#4e5968]"
                      >
                        <ChevronLeft size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setPendingHistPickerOpen((v) => !v)}
                        className="flex flex-1 min-w-0 items-center justify-center gap-2 px-2 h-8 rounded-[4px] hover:bg-[#e5e8eb] text-[#191f28]"
                      >
                        <Calendar size={16} className="text-orange-500 shrink-0" />
                        <span className="text-[14px] font-semibold tabular-nums truncate">
                          {pendingHistYear}-{pendingHistMonth}
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => changePendingHistMonth(1)}
                        className="w-8 h-8 shrink-0 flex items-center justify-center rounded-[4px] hover:bg-[#e5e8eb] text-[#4e5968]"
                      >
                        <ChevronRight size={14} />
                      </button>
                      {pendingHistPickerOpen && (
                        <div className="absolute top-full left-0 mt-1 z-[1200] bg-white rounded-[4px] shadow-xl border border-slate-200 overflow-hidden">
                          <DatePicker
                            selected={new Date(parseInt(pendingHistYear, 10), parseInt(pendingHistMonth, 10) - 1, 1)}
                            onChange={handlePendingHistMonthPick}
                            showMonthYearPicker
                            dateFormat="yyyy.MM"
                            renderMonthContent={(month) => String(month + 1).padStart(2, '0')}
                            inline
                            calendarClassName="deposit-month-picker"
                          />
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={resetPendingHistToCurrentMonth}
                      className="h-9 shrink-0 px-2.5 bg-white border border-[#e5e8eb] rounded-[4px] text-[12px] font-bold text-[#4e5968] hover:bg-[#f2f4f6] flex items-center leading-none"
                    >
                      당월
                    </button>
                  </div>
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar p-3 space-y-4" style={{ backgroundColor: '#fdfdfd' }}>
                  {settlementsForPendingHistory.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-32 text-slate-400 text-[13px] font-medium">조회된 내역이 없습니다.</div>
                  ) : (
                    groupBySettleAndApprove(settlementsForPendingHistory).map((grp) => (
                      <div key={`h-${grp.settleDate}-${grp.approveDate}`} className="flex flex-col gap-2">
                        <div className="flex items-center gap-2 px-0.5">
                          <div className="w-1 h-4 bg-orange-400 rounded-full shrink-0" />
                          <div className="flex flex-wrap items-center gap-x-2 text-[12px]">
                            <span className="text-slate-500">정산일</span>
                            <span className="text-[13px] font-bold text-slate-700">{grp.settleDate}</span>
                            <span className="text-slate-300">|</span>
                            <span className="text-slate-500">결제일</span>
                            <span className="text-[13px] font-bold text-slate-700">{grp.approveDate}</span>
                          </div>
                          <div className="flex-1 h-px bg-slate-100 min-w-[8px]" />
                        </div>
                        <div className="rounded-[4px] border border-slate-200 bg-white overflow-hidden divide-y divide-slate-100">
                          {grp.items.map((s) => {
                            const failed = isTransferFailed(s);
                            const transferring = isPayoutTransferring(s);
                            const expKey = `ph-${s.id}`;
                            const isExp = hubExpandedIds.has(expKey);
                            const details = detailRowsForTransferItem(s);
                            return (
                              <div key={s.id} className="flex flex-col">
                                <div className={`px-3 py-2 flex items-center gap-2 ${isExp ? 'bg-slate-50/80' : ''}`}>
                                  {failed ? (
                                    <div className="w-5 h-5 flex items-center justify-center bg-red-50 rounded-full shrink-0">
                                      <AlertCircle className="w-3.5 h-3.5 text-red-400" />
                                    </div>
                                  ) : transferring ? (
                                    <Clock className="w-5 h-5 text-blue-500 shrink-0" />
                                  ) : (
                                    <CircleCheck className="w-5 h-5 text-green-500 shrink-0" />
                                  )}
                                  <div className="flex-1 min-w-0 flex items-center gap-1.5">
                                    <span className="text-[13px] text-slate-700 truncate">{s.merchant}</span>
                                    {failed ? (
                                      <span className="text-[9px] px-1.5 py-0.5 bg-red-50 text-red-500 rounded-[4px] font-bold border border-red-100 shrink-0">실패</span>
                                    ) : transferring ? (
                                      <span className="text-[9px] px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded-[4px] font-bold border border-blue-100 shrink-0">지급 요청 등록</span>
                                    ) : (
                                      <span className="text-[9px] px-1.5 py-0.5 bg-green-50 text-green-600 rounded-[4px] font-bold border border-green-100 shrink-0">완료</span>
                                    )}
                                  </div>
                                  <span className="text-[13px] font-bold text-slate-800 tabular-nums shrink-0">{s.settleAmount}원</span>
                                  <button type="button" onClick={() => toggleHubExpand(expKey)} className={`p-1.5 rounded-[4px] border border-slate-200 shrink-0 ${isExp ? 'bg-slate-200' : 'bg-white'}`}>
                                    {isExp ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                  </button>
                                </div>
                                {isExp && (
                                  <div className="bg-slate-50/90 border-t border-slate-100 px-3 py-2 pl-10">
                                    {failed && (
                                      <div className="mb-2 p-2 rounded-[4px] bg-red-50/80 border border-red-100 flex gap-2">
                                        <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
                                        <div>
                                          <p className="text-[10px] font-bold text-red-400">실패 사유</p>
                                          <p className="text-[12px] text-red-600 font-semibold">{s.transferFailReason || '-'}</p>
                                        </div>
                                      </div>
                                    )}
                                    {transferring && (
                                      <div className="mb-2 rounded-[4px] border border-blue-100 bg-blue-50/80 p-2 text-[11px] font-semibold text-blue-600">
                                        {getPayoutRequestDetail(s) || '지급대행 요청이 등록되었습니다.'}
                                      </div>
                                    )}
                                    <div className="flex justify-between text-[10px] font-bold text-slate-400 uppercase tracking-tight border-b border-slate-100/80 pb-1.5 mb-1">
                                      <span>구분</span>
                                      <span className="pr-1">금액</span>
                                    </div>
                                    {details.map((d) => (
                                      <div key={d.id} className="flex justify-between text-[12px] py-1.5">
                                        <span className="text-slate-600">{d.title}</span>
                                        <span className="font-bold text-slate-800 tabular-nums">{Number(d.price).toLocaleString('ko-KR')}원</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div className="px-3 py-2.5 border-t border-slate-100 bg-slate-50 flex items-center justify-between shrink-0">
                  <p className="text-[11px] text-slate-500">
                    {ADMIN_CP_PAYOUT_TENANT_ENABLED ? '지급 요청·완료·실패 처리 내역' : '성공 및 실패 히스토리 조회 중'}
                  </p>
                  <button type="button" onClick={() => navigate('/admin/payments')} className="text-[11px] font-bold text-orange-600 hover:underline">보고서 보기</button>
                </div>
              </>
            )}
          </div>
          ) : (
          <div className="flex-1 min-h-0 bg-white border border-slate-200 rounded-[6px] overflow-hidden flex flex-col shadow-[0_8px_20px_rgba(15,23,42,0.04)]">
            {/* 헤더 */}
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-[4px] bg-orange-50 flex items-center justify-center text-orange-500 shrink-0">
                  <Layers size={18} />
                </div>
                <div>
                  <h3 className="text-[15px] font-bold text-slate-800 leading-tight flex items-center gap-1.5">
                    정산예정
                    <span
                      data-tooltip-id="settlement-pending-tooltip"
                      data-tooltip-html="각 가맹점의 결제금액을 확인해 확정 처리합니다.<br/>확정된 내역은 입금예정으로 이동합니다."
                      onMouseEnter={() => !showAllOpen && setHoveredTooltipId('settlement-pending')}
                      onMouseLeave={() => setHoveredTooltipId(null)}
                      className="inline-flex cursor-help text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      <HelpCircle size={14} strokeWidth={2} />
                    </span>
                  </h3>
                  <p className="text-[11px] text-slate-400 font-medium">승인건을 확정합니다.</p>
                </div>
              </div>
              {/* <div className="flex items-center gap-2">
                <span className="text-[12px] text-slate-400">전체선택</span>
                <input
                  type="checkbox"
                  checked={allPendingSelected}
                  onChange={(e) => {
                    if (e.target.checked) {
                      const allIds = filteredPendingSettlements.map((s) => s.id);
                      setSelectedSettlementIds(allIds);
                    } else {
                      setSelectedSettlementIds([]);
                    }
                  }}
                  className="w-5 h-5 border-[#fdba74] rounded focus:ring-[#fb923c] accent-[#f96600]"
                  style={{ accentColor: '#f96600' }}
                />
              </div> */}
            </div>

            {/* 일자별 그룹핑 목록 */}
            <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar p-3 space-y-3" style={{ backgroundColor: '#fdfdfd' }}>
              {filteredPendingSettlements.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <p className="text-[13px] text-slate-400">정산예정 내역이 없습니다.</p>
                </div>
              ) : (
                (() => {
                  const grouped = {};
                  filteredPendingSettlements.forEach((s) => {
                    const dateKey = s.approveDate || s.settleDate || '-';
                    if (!grouped[dateKey]) grouped[dateKey] = [];
                    grouped[dateKey].push(s);
                  });
                  const sortedDates = Object.keys(grouped).sort((a, b) => parseDateStr(b) - parseDateStr(a));
                  return sortedDates.map((dateKey) => {
                    const items = grouped[dateKey];
                    return (
                      <div key={dateKey} className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                        <div className="px-3 py-2 flex items-center justify-between border-b border-slate-200" style={{ backgroundColor: 'rgba(249, 249, 249, 0.5)' }}>
                          <div className="flex items-center gap-2">
                            <Calendar size={14} className="text-orange-400" />
                            <span className="text-[12px] text-slate-500">결제일:</span>
                            <span className="text-[13px] font-bold text-slate-700">{dateKey}</span>
                          </div>
                          <span
                            data-tooltip-id="settlement-pending-list-tooltip"
                            data-tooltip-html="해당 결제일의 가맹점별 승인금액입니다.<br/>체크박스를 클릭해 선택할 수 있습니다."
                            onMouseEnter={() => !showAllOpen && setHoveredTooltipId('settlement-pending-list')}
                            onMouseLeave={() => setHoveredTooltipId(null)}
                            className="inline-flex cursor-help text-slate-400 hover:text-slate-600 shrink-0"
                          >
                            <HelpCircle size={12} strokeWidth={2} />
                          </span>
                          {/* <div className="flex items-center gap-1">
                            <span className="text-[11px] text-slate-400">결제</span>
                            <span className="text-[12px] font-bold text-orange-500">{items.length}건</span>
                          </div> */}
                        </div>
                        <div className="divide-y divide-slate-100">
                          {items.map((s) => {
                            const isChecked = selectedSettlementIds.includes(s.id);
                            const expKey = `p-${s.id}`;
                            const isExp = hubExpandedIds.has(expKey);
                            if (SETTLEMENT_HUB_UI_V1) {
                              const details = detailRowsForPendingItem(s);
                              return (
                                <div key={s.id} className="flex flex-col bg-white">
                                  <div
                                    className={`px-3 py-2 flex items-center gap-2 transition-colors cursor-pointer ${isChecked ? 'bg-orange-50/40' : 'hover:bg-slate-50'}`}
                                    onClick={() => {
                                      if (isChecked) {
                                        setSelectedSettlementIds(selectedSettlementIds.filter((id) => id !== s.id));
                                      } else {
                                        setSelectedSettlementIds([...selectedSettlementIds, s.id]);
                                      }
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        if (isChecked) {
                                          setSelectedSettlementIds(selectedSettlementIds.filter((id) => id !== s.id));
                                        } else {
                                          setSelectedSettlementIds([...selectedSettlementIds, s.id]);
                                        }
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                      className="w-4 h-4 border-slate-300 rounded shrink-0 accent-orange-500"
                                      style={{ accentColor: '#f96600' }}
                                    />
                                    <div className="flex-1 min-w-0 flex items-center gap-1.5">
                                      <span className="text-[13px] text-slate-700 truncate">{s.merchant}</span>
                                      <span className="text-[9px] px-1.5 py-0.5 bg-orange-50 text-orange-600 rounded font-bold border border-orange-100 shrink-0">예정</span>
                                    </div>
                                    <span className="text-[13px] font-bold text-slate-800 tabular-nums shrink-0">{s.settleAmount}원</span>
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); toggleHubExpand(expKey); }}
                                      className={`p-1.5 rounded-md border border-slate-200 shrink-0 transition-colors ${isExp ? 'bg-slate-200 text-slate-700' : 'bg-white text-slate-400 hover:text-slate-600'}`}
                                      aria-label={isExp ? '상세 접기' : '상세 펼치기'}
                                    >
                                      {isExp ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                    </button>
                                  </div>
                                  {isExp && (
                                    <div className="bg-slate-50/90 border-t border-slate-100 px-3 py-2 pl-10">
                                      <div className="flex justify-between text-[10px] font-bold text-slate-400 uppercase tracking-tight border-b border-slate-100/80 pb-1.5 mb-1">
                                        <span>구분</span>
                                        <span className="pr-1">금액</span>
                                      </div>
                                      {details.map((d) => (
                                        <div key={d.id} className="flex justify-between text-[12px] py-1.5">
                                          <span className="text-slate-600">{d.title}</span>
                                          <span className="font-bold text-slate-800 tabular-nums">{Number(d.price).toLocaleString('ko-KR')}원</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            }
                            return (
                              <div
                                key={s.id}
                                className={`px-3 py-2 flex items-center gap-2 cursor-pointer transition-colors ${isChecked ? 'bg-orange-50/40' : 'bg-white hover:bg-slate-50'}`}
                                onClick={() => {
                                  if (isChecked) {
                                    setSelectedSettlementIds(selectedSettlementIds.filter((id) => id !== s.id));
                                  } else {
                                    setSelectedSettlementIds([...selectedSettlementIds, s.id]);
                                  }
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    if (isChecked) {
                                      setSelectedSettlementIds(selectedSettlementIds.filter((id) => id !== s.id));
                                    } else {
                                      setSelectedSettlementIds([...selectedSettlementIds, s.id]);
                                    }
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  className="w-4 h-4 border-slate-300 rounded shrink-0 accent-orange-500"
                                  style={{ accentColor: '#f96600' }}
                                />
                                <div className="flex-1 min-w-0 flex flex-col">
                                  <span className="text-[13px] text-slate-700 truncate">{s.merchant}</span>
                                  <span className="mt-0.5 flex items-center gap-1 text-[11px] tabular-nums">
                                    <span className="font-medium text-slate-500">정산예정일</span>
                                    <span className="font-bold text-orange-600">{s.settleDate || "-"}</span>
                                  </span>
                                </div>
                                <span className="text-[13px] font-bold text-slate-800 tabular-nums shrink-0">{s.settleAmount}원</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  });
                })()
              )}
            </div>

            {/* 하단 고정: 건수/금액 + 버튼 (우측 정렬, 텍스트·금액 블록과 버튼 사이 gap) */}
            <div className="px-4 py-3 border-t border-slate-200 bg-white shrink-0">
              <div className="flex items-center justify-end gap-6">
                <div className="text-right">
                  <p className="text-[11px] text-slate-400">선택된 {selectedSettlementIds.length}건 합계</p>
                  <p className="text-[18px] font-black text-orange-500 tabular-nums flex items-center justify-end gap-0.5">
                    {(() => {
                      const selected = filteredPendingSettlements.filter((s) => selectedSettlementIds.includes(s.id));
                      const total = selected.reduce((sum, s) => {
                        const num = s.settleAmountNum ?? getSettleFromApprove(parseAmount(s.approveAmount));
                        return sum + Number(num || 0);
                      }, 0);
                      return total.toLocaleString();
                    })()}원
                    <span
                      data-tooltip-id="settlement-pending-total-tooltip"
                      data-tooltip-html="정산예정내역에서 선택한 건의 승인금액 합계입니다."
                      onMouseEnter={() => !showAllOpen && setHoveredTooltipId('settlement-pending-total')}
                      onMouseLeave={() => setHoveredTooltipId(null)}
                      className="inline-flex cursor-help text-slate-400 hover:text-slate-600 shrink-0"
                    >
                      <HelpCircle size={16} strokeWidth={2} />
                    </span>
                  </p>
                </div>
                <span
                  data-tooltip-id="settlement-confirm-btn-tooltip"
                  data-tooltip-html="선택한 내역을 정산 확정합니다.<br/>확정 시 입금 예정 내역으로(오타확인) 이동되며, 취소할 수 없습니다."
                  onMouseEnter={() => !showAllOpen && setHoveredTooltipId('settlement-confirm-btn')}
                  onMouseLeave={() => setHoveredTooltipId(null)}
                  className="inline-flex shrink-0 cursor-help"
                >
                  <button
                    type="button"
                    disabled={!selectedSettlementIds.length}
                    onClick={() => {
                      if (!selectedSettlementIds.length) {
                        showToast('확정할 항목을 선택해주세요.', 'error');
                        return;
                      }
                      setConfirmSettlementOpen(true);
                    }}
                    className={`px-5 py-2.5 rounded-lg text-[13px] font-bold shrink-0 ${selectedSettlementIds.length > 0
                      ? 'bg-orange-500 text-white hover:bg-orange-600'
                      : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                    }`}
                  >
                    정산 확정
                  </button>
                </span>
              </div>
            </div>
          </div>
          )}
        </div>

        {/* 입금예정 25% - 일자별 그룹핑 */}
        <div className="basis-1/5 min-w-0 flex flex-col min-h-0">
          {SETTLEMENT_HUB_UI_V1 ? (
          <div className="flex-1 min-h-0 bg-white border border-slate-200 rounded-[4px] overflow-hidden flex flex-col shadow-[0_8px_20px_rgba(15,23,42,0.04)]">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-2 shrink-0">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-9 h-9 rounded-[4px] bg-[var(--brand-primary-soft)] flex items-center justify-center text-[var(--brand-primary)] shrink-0">
                  <ArrowRightLeft size={18} />
                </div>
                <div className="min-w-0">
                  <h3 className="text-[15px] font-bold text-slate-800 leading-tight flex items-center gap-1.5">
                    입금예정
                    <span
                      data-tooltip-id="settlement-deposit-expected-tooltip"
                      data-tooltip-html={externalSettlementMode
                        ? '지급예정일에 외부 정산 완료 상태로 자동 변경됩니다.<br/>이 시스템에서는 별도의 지급지시를 실행하지 않습니다.'
                        : ADMIN_CP_PAYOUT_TENANT_ENABLED
                          ? '정산 확정된 건의 지급대행을 신청합니다.<br/>신청 후 입금 처리 내역에서 상태를 확인할 수 있습니다.'
                          : '정산 확정된 건을 잔액 한도내에서 입금할 수 있습니다.<br/>입금 처리한 내역은 입금완료내역으로 이동합니다.'}
                      onMouseEnter={() => !showAllOpen && setHoveredTooltipId('settlement-deposit-expected')}
                      onMouseLeave={() => setHoveredTooltipId(null)}
                      className="inline-flex cursor-help text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      <HelpCircle size={14} strokeWidth={2} />
                    </span>
                  </h3>
                  <p className="text-[11px] text-slate-400 font-medium">{externalSettlementMode ? '지급예정일에 자동 완료됩니다.' : '정산확정건을 입금합니다.'}</p>
                </div>
              </div>
              <div className="flex bg-[#f2f4f6] p-0.5 rounded-[4px] border border-[#e5e8eb] shrink-0">
                <button
                  type="button"
                  onClick={() => setTransferPanelTab('scheduled')}
                  className={`px-2.5 py-1 text-[11px] font-bold rounded-[4px] transition-all ${transferPanelTab === 'scheduled' ? 'bg-white text-[var(--brand-primary)] shadow-sm ring-1 ring-[#e5e8eb]' : 'text-[#8b95a1]'}`}
                >
                  예정내역
                </button>
                <button
                  type="button"
                  onClick={() => setTransferPanelTab('completed')}
                  className={`px-2.5 py-1 text-[11px] font-bold rounded-[4px] transition-all ${transferPanelTab === 'completed' ? 'bg-white text-[var(--brand-primary)] shadow-sm ring-1 ring-[#e5e8eb]' : 'text-[#8b95a1]'}`}
                >
                  {externalSettlementMode ? '정산완료' : ADMIN_CP_PAYOUT_TENANT_ENABLED ? '처리내역' : '완료내역'}
                </button>
              </div>
            </div>
            {transferPanelTab === 'scheduled' ? (
              <>
                <div
                  className={`${externalSettlementMode ? 'hidden' : 'flex'} px-3 h-10 min-h-[40px] box-border items-center gap-2 border-b border-slate-100 shrink-0 cursor-pointer select-none`}
                  style={{ backgroundColor: 'rgba(249, 249, 249, 0.5)' }}
                  onClick={() => {
                    if (externalSettlementMode || !hasRealDepositRows) return;
                    if (allDepositSelected) setSelectedIds([]);
                    else setSelectedIds(selectableDepositRows.map((s) => s.id));
                  }}
                >
                  <div className={`w-4 h-4 rounded-[4px] border-2 flex items-center justify-center shrink-0 ${allDepositSelected ? 'bg-[var(--brand-primary)] border-[var(--brand-primary)]' : 'bg-white border-slate-300'} ${!hasRealDepositRows ? 'opacity-40' : ''}`}>
                    {allDepositSelected && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
                  </div>
                  <span className="text-[12px] font-medium text-slate-900">전체 선택</span>
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar p-3 space-y-4" style={{ backgroundColor: '#fdfdfd' }}>
                  {filteredSettlements.length === 0 ? (
                    <div className="flex items-center justify-center h-32 text-[13px] text-slate-400">입금예정 내역이 없습니다.</div>
                  ) : (
                    groupBySettleAndApprove(filteredSettlements).map((grp) => (
                      <div key={`tr-${grp.settleDate}-${grp.approveDate}`} className="flex flex-col gap-2">
                        <div className="flex items-center gap-2 px-0.5">
                          <div className="w-1 h-4 bg-[var(--brand-primary)] rounded-full shrink-0" />
                          <div className="flex flex-wrap items-center gap-x-2 text-[12px]">
                            <span className="text-slate-500">{externalSettlementMode ? '지급예정일' : '정산일'}</span>
                            <span className="text-[13px] font-bold text-slate-700">{grp.settleDate}</span>
                            <span className="text-slate-300">|</span>
                            <span className="text-slate-500">결제일</span>
                            <span className="text-[13px] font-bold text-slate-700">{grp.approveDate}</span>
                          </div>
                          <div className="flex-1 h-px bg-slate-100 min-w-[8px]" />
                        </div>
                        <div className="rounded-[4px] border border-slate-200 bg-white overflow-hidden divide-y divide-slate-100">
                          {grp.items.map((s) => {
                            const isChecked = selectedIds.includes(s.id);
                            const isFailed = isTransferFailed(s);
                            const isTransferring = isPayoutTransferring(s);
                            const isSelectable = !externalSettlementMode && isPayoutSelectable(s);
                            const expKey = `t-${s.id}`;
                            const isExp = hubExpandedIds.has(expKey);
                            const details = detailRowsForTransferItem(s);
                            return (
                              <div key={s.id} className={`flex flex-col ${!isSelectable ? 'opacity-90' : ''}`}>
                                <div className={`px-3 py-2 flex items-center gap-2 ${isExp ? 'bg-slate-50/80' : ''} ${isChecked && isSelectable ? 'bg-[var(--brand-primary-soft)]/40' : ''}`}>
                                  <button
                                    type="button"
                                    disabled={!hasRealDepositRows || !isSelectable}
                                    onClick={() => {
                                      if (!hasRealDepositRows || !isSelectable) return;
                                      if (isChecked) setSelectedIds(selectedIds.filter((id) => id !== s.id));
                                      else setSelectedIds([...selectedIds, s.id]);
                                    }}
                                    className={`w-4 h-4 rounded-[4px] border-2 flex items-center justify-center shrink-0 ${isChecked ? 'bg-[var(--brand-primary)] border-[var(--brand-primary)]' : 'bg-white border-slate-300'} ${!hasRealDepositRows || !isSelectable ? 'opacity-40 cursor-not-allowed' : ''}`}
                                  >
                                    {isChecked && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
                                  </button>
                                  <div className="flex-1 min-w-0 flex items-center gap-1.5">
                                    <span className="text-[13px] text-slate-700 truncate">{s.merchant}</span>
                                    {isFailed ? (
                                      <span className="text-[9px] px-1.5 py-0.5 bg-red-50 text-red-500 rounded-[4px] font-bold border border-red-100 shrink-0">실패</span>
                                    ) : isTransferring ? (
                                      <span className="text-[9px] px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded-[4px] font-bold border border-blue-100 shrink-0">지급 요청 등록</span>
                                    ) : (
                                      <span className="text-[9px] px-1.5 py-0.5 bg-[var(--brand-primary-soft)] text-[var(--brand-primary)] rounded-[4px] font-bold border border-[var(--brand-primary-border)] shrink-0">예정</span>
                                    )}
                                  </div>
                                  <span className="text-[13px] font-bold text-slate-800 tabular-nums shrink-0">{s.settleAmount}원</span>
                                  <button type="button" onClick={() => toggleHubExpand(expKey)} className={`p-1.5 rounded-[4px] border border-slate-200 shrink-0 ${isExp ? 'bg-slate-200' : 'bg-white'}`}>
                                    {isExp ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                  </button>
                                </div>
                                <div className="px-3 pb-2 pl-9">
                                  <PayoutDeadlineLine settlement={s} />
                                  <ExternalSettlementLine settlement={s} />
                                </div>
                                {isExp && (
                                  <div className="bg-slate-50/90 border-t border-slate-100 px-3 py-2 pl-10">
                                    {isFailed && (
                                      <div className="mb-2 p-2 rounded-[4px] bg-red-50/80 border border-red-100 flex gap-2">
                                        <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
                                        <div>
                                          <p className="text-[10px] font-bold text-red-400">실패 사유</p>
                                          <p className="text-[12px] text-red-600 font-semibold">{s.transferFailReason || '관리자문의'}</p>
                                        </div>
                                      </div>
                                    )}
                                    {isTransferring && (
                                      <div className="mb-2 rounded-[4px] border border-blue-100 bg-blue-50/80 p-2 text-[11px] font-semibold text-blue-600">
                                        {getPayoutRequestDetail(s) || '지급대행 요청이 등록되었습니다.'}
                                      </div>
                                    )}
                                    <div className="flex justify-between text-[10px] font-bold text-slate-400 uppercase tracking-tight border-b border-slate-100/80 pb-1.5 mb-1">
                                      <span>구분</span>
                                      <span className="pr-1">금액</span>
                                    </div>
                                    {details.map((d) => (
                                      <div key={d.id} className="flex justify-between text-[12px] py-1.5">
                                        <span className="text-slate-600">{d.title}</span>
                                        <span className="font-bold text-slate-800 tabular-nums">{Number(d.price).toLocaleString('ko-KR')}원</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div className={externalSettlementMode ? 'hidden' : 'px-4 py-3 border-t border-slate-200 bg-white shrink-0'}>
                  <div className="flex items-center justify-end gap-6">
                    <div className="text-right">
                      <p className="text-[11px] text-slate-400">
                    {ADMIN_CP_PAYOUT_TENANT_ENABLED && kgInicisPayoutRows.length > 0
                      ? `선택 정산: ${selectedPayoutRows.length}건`
                      : `선택된 ${selectedIds.length}건 합계`}
                  </p>
                      <p className="text-[18px] font-black text-[var(--brand-primary)] tabular-nums flex items-center justify-end gap-0.5">
                        {ADMIN_CP_PAYOUT_TENANT_ENABLED && kgInicisPayoutRows.length > 0
                          ? selectedPayoutAmount
                          : filteredSettlements.filter((s) => selectedIds.includes(s.id)).reduce((sum, s) => sum + Number((s.settleAmountNum ?? getSettleFromApprove(parseAmount(s.settleAmount || s.approveAmount))) || 0), 0).toLocaleString()}원
                        <span
                          data-tooltip-id="settlement-deposit-total-tooltip"
                          data-tooltip-html="입금예정내역에서 선택한 건의 입금예정금액 합계입니다."
                          onMouseEnter={() => !showAllOpen && setHoveredTooltipId('settlement-deposit-total')}
                          onMouseLeave={() => setHoveredTooltipId(null)}
                          className="inline-flex cursor-help text-slate-400 hover:text-slate-600 shrink-0"
                        >
                          <HelpCircle size={16} strokeWidth={2} />
                        </span>
                      </p>
                      {ADMIN_CP_PAYOUT_TENANT_ENABLED && kgInicisPayoutRows.length > 0 && (
                        <>
                          <p className="mt-0.5 text-[11px] font-semibold text-slate-500">지급예정일: {selectedPayoutDateLabel}</p>
                          {kgBatchSelectionStarted && !kgBatchSelectionComplete && (
                            <p className="mt-0.5 text-[10px] font-semibold text-red-500">지급 가능 정산을 모두 선택해야 신청할 수 있습니다.</p>
                          )}
                        </>
                      )}
                    </div>
                    <span
                      data-tooltip-id="settlement-deposit-btn-tooltip"
                      data-tooltip-html={ADMIN_CP_PAYOUT_TENANT_ENABLED
                        ? '선택한 내역의 지급대행을 신청합니다.<br/>신청 상태는 입금 처리 내역에서 확인할 수 있습니다.'
                        : '선택한 내역을 가맹점 계좌로 입금합니다.<br/>입금 시 입금 완료 내역으로 이동되며, 취소할 수 없습니다.'}
                      onMouseEnter={() => !showAllOpen && setHoveredTooltipId('settlement-deposit-btn')}
                      onMouseLeave={() => setHoveredTooltipId(null)}
                      className="inline-flex shrink-0 cursor-help"
                    >
                      <button
                        type="button"
                        disabled={ADMIN_CP_PAYOUT_TENANT_ENABLED && kgInicisPayoutRows.length > 0 ? !payoutActionEnabled : !selectedIds.length}
                        onClick={() => { if (!selectedIds.length) { showToast('입금할 항목을 선택해주세요.', 'error'); return; } setConfirmDepositOpen(true); }}
                        className={`px-5 py-2.5 rounded-lg text-[13px] font-bold shrink-0 ${(ADMIN_CP_PAYOUT_TENANT_ENABLED && kgInicisPayoutRows.length > 0 ? payoutActionEnabled : selectedIds.length > 0) ? 'bg-[var(--brand-primary)] text-white hover:bg-[var(--brand-primary-hover)]' : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`}
                      >
                        {ADMIN_CP_PAYOUT_TENANT_ENABLED && kgInicisPayoutRows.length > 0 ? '합계 지급 신청' : '입금 처리'}
                      </button>
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="px-3 h-10 min-h-[40px] box-border border-b border-slate-100 flex items-center gap-2 shrink-0 bg-white">
                  <div ref={transferHistPickerRef} className="relative flex flex-1 min-w-0 items-center gap-2">
                    <div className="flex flex-1 min-w-0 h-9 items-center gap-0.5 bg-white px-1 rounded-[4px] border border-[#e5e8eb] hover:border-[var(--brand-primary)]/30 transition-all">
                      <button
                        type="button"
                        onClick={() => changeTransferHistMonth(-1)}
                        className="w-8 h-8 shrink-0 flex items-center justify-center rounded-[4px] hover:bg-[#e5e8eb] text-[#4e5968]"
                      >
                        <ChevronLeft size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setTransferHistPickerOpen((v) => !v)}
                        className="flex flex-1 min-w-0 items-center justify-center gap-2 px-2 h-8 rounded-[4px] hover:bg-[#e5e8eb] text-[#191f28]"
                      >
                        <Calendar size={16} className="text-[var(--brand-primary)] shrink-0" />
                        <span className="text-[14px] font-semibold tabular-nums truncate">
                          {transferHistYear}-{transferHistMonth}
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => changeTransferHistMonth(1)}
                        className="w-8 h-8 shrink-0 flex items-center justify-center rounded-[4px] hover:bg-[#e5e8eb] text-[#4e5968]"
                      >
                        <ChevronRight size={14} />
                      </button>
                      {transferHistPickerOpen && (
                        <div className="absolute top-full left-0 mt-1 z-[1200] bg-white rounded-[4px] shadow-xl border border-slate-200 overflow-hidden">
                          <DatePicker
                            selected={new Date(parseInt(transferHistYear, 10), parseInt(transferHistMonth, 10) - 1, 1)}
                            onChange={handleTransferHistMonthPick}
                            showMonthYearPicker
                            dateFormat="yyyy.MM"
                            renderMonthContent={(month) => String(month + 1).padStart(2, '0')}
                            inline
                            calendarClassName="deposit-month-picker"
                          />
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={resetTransferHistToCurrentMonth}
                      className="h-9 shrink-0 px-2.5 bg-white border border-[#e5e8eb] rounded-[4px] text-[12px] font-bold text-[#4e5968] hover:bg-[#f2f4f6] flex items-center leading-none"
                    >
                      당월
                    </button>
                  </div>
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar p-3 space-y-4" style={{ backgroundColor: '#fdfdfd' }}>
                  {ADMIN_CP_PAYOUT_TENANT_ENABLED && (
                    <PayoutBatchHistory
                      batches={activePayoutBatches}
                      expandedIds={hubExpandedIds}
                      onToggle={toggleHubExpand}
                      checkingIds={checkingPayoutBatchIds}
                      onCheckResult={handlePayoutBatchResultCheck}
                      settlementPaymentDates={payoutBatchSettlementPaymentDates}
                    />
                  )}
                  {depositItemsForTransferHistory.length === 0 && activePayoutBatches.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-32 text-slate-400 text-[13px] font-medium">조회된 내역이 없습니다.</div>
                  ) : (
                    (() => {
                      const byKey = {};
                      depositItemsForTransferHistory.forEach((item) => {
                        const sk = item.settleDate || '-';
                        const pk = item.approveDate || '-';
                        const key = `${sk}|||${pk}`;
                        if (!byKey[key]) byKey[key] = { settleDate: sk, approveDate: pk, items: [] };
                        byKey[key].items.push(item);
                      });
                      return Object.values(byKey).sort((a, b) => parseDateStr(b.settleDate) - parseDateStr(a.settleDate));
                    })().map((grp) => (
                      <div key={`dh-${grp.settleDate}-${grp.approveDate}`} className="flex flex-col gap-2">
                        <div className="flex items-center gap-2 px-0.5">
                          <div className="w-1 h-4 bg-[var(--brand-primary)] rounded-full shrink-0" />
                          <div className="flex flex-wrap items-center gap-x-2 text-[12px]">
                            <span className="text-slate-500">{externalSettlementMode ? '지급예정일' : '정산일'}</span>
                            <span className="text-[13px] font-bold text-slate-700">{grp.settleDate}</span>
                            <span className="text-slate-300">|</span>
                            <span className="text-slate-500">결제일</span>
                            <span className="text-[13px] font-bold text-slate-700">{grp.approveDate}</span>
                          </div>
                          <div className="flex-1 h-px bg-slate-100 min-w-[8px]" />
                        </div>
                        <div className="rounded-[4px] border border-slate-200 bg-white overflow-hidden divide-y divide-slate-100">
                          {grp.items.map((item) => {
                            const expKey = `dh-${item.id}`;
                            const isExp = hubExpandedIds.has(expKey);
                            const details = detailRowsForDepositHistoryItem(item);
                            const amt = item.settleAmount || item.depositAmount || '0';
                            const statusMeta = getDepositProcessStatus(item);
                            return (
                              <div key={item.id} className="flex flex-col">
                                <div className={`px-3 py-2 flex items-center gap-2 ${isExp ? 'bg-slate-50/80' : ''}`}>
                                  {statusMeta.key === 'TRANSFERRED' ? (
                                    <CircleCheck className={`w-5 h-5 ${statusMeta.iconClass} shrink-0`} />
                                  ) : statusMeta.key === 'TRANSFER_FAILED' ? (
                                    <AlertCircle className={`w-5 h-5 ${statusMeta.iconClass} shrink-0`} />
                                  ) : (
                                    <Clock className={`w-5 h-5 ${statusMeta.iconClass} shrink-0`} />
                                  )}
                                  <div className="flex-1 min-w-0 flex items-center gap-1.5">
                                    <span className="text-[13px] text-slate-700 truncate">{item.merchant}</span>
                                    {completedPayoutBatchSettlementIds.has(String(item.id)) && (
                                      <span className="shrink-0 rounded bg-amber-50 px-1.5 py-0.5 text-[9px] font-black text-amber-700 ring-1 ring-inset ring-amber-200">합산 지급</span>
                                    )}
                                    {ADMIN_CP_PAYOUT_TENANT_ENABLED ? (
                                      <DepositStatusBadge item={item} />
                                    ) : (
                                      <span className="text-[9px] px-1.5 py-0.5 bg-green-50 text-green-600 rounded-[4px] font-bold border border-green-100 shrink-0">완료</span>
                                    )}
                                  </div>
                                  <span className="text-[13px] font-bold text-slate-800 tabular-nums shrink-0">{amt}원</span>
                                  <button type="button" onClick={() => toggleHubExpand(expKey)} className={`p-1.5 rounded-[4px] border border-slate-200 shrink-0 ${isExp ? 'bg-slate-200' : 'bg-white'}`}>
                                    {isExp ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                  </button>
                                </div>
                                {isExp && (
                                  <div className="bg-slate-50/90 border-t border-slate-100 px-3 py-2 pl-10">
                                    <div className="flex justify-between text-[10px] font-bold text-slate-400 uppercase tracking-tight border-b border-slate-100/80 pb-1.5 mb-1">
                                      <span>구분</span>
                                      <span className="pr-1">금액</span>
                                    </div>
                                    {details.map((d) => (
                                      <div key={d.id} className="flex justify-between text-[12px] py-1.5">
                                        <span className="text-slate-600">{d.title}</span>
                                        <span className="font-bold text-slate-800 tabular-nums">{Number(d.price).toLocaleString('ko-KR')}원</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div className="px-3 py-2.5 border-t border-slate-100 bg-slate-50 flex items-center justify-between shrink-0">
                  <p className="text-[11px] text-slate-500">
                    {ADMIN_CP_PAYOUT_TENANT_ENABLED ? '지급 요청·완료·실패 처리 내역' : '성공 및 실패 히스토리 조회 중'}
                  </p>
                  <button type="button" onClick={() => navigate('/admin/payments')} className="text-[11px] font-bold text-[var(--brand-primary)] hover:underline">보고서 보기</button>
                </div>
              </>
            )}
          </div>
          ) : (
          <div className="flex-1 min-h-0 bg-white border border-slate-200 rounded-[6px] overflow-hidden flex flex-col shadow-[0_8px_20px_rgba(15,23,42,0.04)]">
            {/* 헤더 */}
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-[4px] bg-[var(--brand-primary-soft)] flex items-center justify-center text-[var(--brand-primary)] shrink-0">
                  <ArrowRightLeft size={18} />
                </div>
                <div>
                  <h3 className="text-[15px] font-bold text-slate-800 leading-tight flex items-center gap-1.5">
                    입금예정
                    <span
                      data-tooltip-id="settlement-deposit-expected-tooltip"
                      data-tooltip-html={externalSettlementMode
                        ? '지급예정일에 외부 정산 완료 상태로 자동 변경됩니다.<br/>이 시스템에서는 별도의 지급지시를 실행하지 않습니다.'
                        : ADMIN_CP_PAYOUT_TENANT_ENABLED
                          ? '정산 확정된 건의 지급대행을 신청합니다.<br/>신청 후 입금 처리 내역에서 상태를 확인할 수 있습니다.'
                          : '정산 확정된 건을 잔액 한도내에서 입금할 수 있습니다.<br/>입금 처리한 내역은 입금완료내역으로 이동합니다.'}
                      onMouseEnter={() => !showAllOpen && setHoveredTooltipId('settlement-deposit-expected')}
                      onMouseLeave={() => setHoveredTooltipId(null)}
                      className="inline-flex cursor-help text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      <HelpCircle size={14} strokeWidth={2} />
                    </span>
                  </h3>
                  <p className="text-[11px] text-slate-400 font-medium">{externalSettlementMode ? '지급예정일에 자동 완료됩니다.' : '정산확정건을 입금합니다.'}</p>
                </div>
              </div>
              {ADMIN_CP_PAYOUT_TENANT_ENABLED && kgInicisPayoutRows.length > 0 && (
                <label className="flex cursor-pointer items-center gap-2">
                  <span className="text-[12px] font-semibold text-slate-600">전체 선택</span>
                  <input
                    type="checkbox"
                    checked={allDepositSelected}
                    onChange={(e) => setSelectedIds(e.target.checked ? selectableDepositRows.map((s) => s.id) : [])}
                    className="h-5 w-5 rounded border-[#cbd5f5] accent-[var(--brand-primary)]"
                    style={{ accentColor: 'var(--brand-primary)' }}
                  />
                </label>
              )}
            </div>


            {/* 일자별 그룹핑 목록 */}
            <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar p-3 space-y-3" style={{ backgroundColor: '#fdfdfd' }}>
              {filteredSettlements.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <p className="text-[13px] text-slate-400">입금예정 내역이 없습니다.</p>
                </div>
              ) : (
                (() => {
                  const grouped = {};
                  filteredSettlements.forEach((s) => {
                    const dateKey = s.settleDate || s.approveDate || '-';
                    if (!grouped[dateKey]) grouped[dateKey] = [];
                    grouped[dateKey].push(s);
                  });
                  const sortedDates = Object.keys(grouped).sort((a, b) => parseDateStr(b) - parseDateStr(a));
                  return sortedDates.map((dateKey) => {
                    const items = grouped[dateKey];
                    return (
                      <div key={dateKey} className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                        <div className="px-3 py-2 flex items-center justify-between border-b border-slate-200" style={{ backgroundColor: 'rgba(249, 249, 249, 0.5)' }}>
                          <div className="flex items-center gap-2">
                            <Calendar size={14} className="text-[var(--brand-primary)]" />
                            <span className="text-[12px] text-slate-500">{externalSettlementMode ? '지급예정일:' : '정산일:'}</span>
                            <span className="text-[13px] font-bold text-slate-700">{dateKey}</span>
                          </div>
                          <span
                            data-tooltip-id="settlement-deposit-list-tooltip"
                            data-tooltip-html={externalSettlementMode ? '지급예정일에 자동 완료될 가맹점별 정산금액입니다.' : '해당 정산일에 입금할 가맹점별 정산금액입니다.'}
                            onMouseEnter={() => !showAllOpen && setHoveredTooltipId('settlement-deposit-list')}
                            onMouseLeave={() => setHoveredTooltipId(null)}
                            className="inline-flex cursor-help text-slate-400 hover:text-slate-600 shrink-0"
                          >
                            <HelpCircle size={12} strokeWidth={2} />
                          </span>
                          {/* <div className="flex items-center gap-1">
                            <span className="text-[11px] text-slate-400">결제</span>
                            <span className="text-[12px] font-bold text-[var(--brand-primary)]">{items.length}건</span>
                          </div> */}
                        </div>
                        <div className="divide-y divide-slate-100">
                          {items.map((s) => {
                            const isTransferring = isPayoutTransferring(s);
                            const isSelectable = !externalSettlementMode && isPayoutSelectable(s);
                            const isChecked = selectedIds.includes(s.id);
                            const isFailed = isTransferFailed(s);
                            const expKey = `t-${s.id}`;
                            const isExp = hubExpandedIds.has(expKey);
                            if (SETTLEMENT_HUB_UI_V1) {
                              const details = detailRowsForTransferItem(s);
                              return (
                                <div key={s.id} className={`flex flex-col bg-white ${!isSelectable ? 'opacity-90' : ''}`}>
                                  <div
                                    className={`px-3 py-2 transition-colors ${!isSelectable ? 'cursor-default bg-slate-50' : isChecked ? 'bg-[var(--brand-primary-soft)]/40 cursor-pointer' : 'bg-white hover:bg-slate-50 cursor-pointer'}`}
                                    onClick={() => {
                                      if (!isSelectable || !hasRealDepositRows) return;
                                      if (isChecked) {
                                        setSelectedIds(selectedIds.filter((id) => id !== s.id));
                                      } else {
                                        setSelectedIds([...selectedIds, s.id]);
                                      }
                                    }}
                                  >
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        disabled={!hasRealDepositRows || !isSelectable}
                                        onChange={(e) => {
                                          if (!hasRealDepositRows || !isSelectable) return;
                                          e.stopPropagation();
                                          if (isChecked) {
                                            setSelectedIds(selectedIds.filter((id) => id !== s.id));
                                          } else {
                                            setSelectedIds([...selectedIds, s.id]);
                                          }
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        className="w-4 h-4 border-slate-300 rounded shrink-0 disabled:opacity-40 accent-[var(--brand-primary)]"
                                        style={{ accentColor: '#3b82f6' }}
                                      />
                                      <div className="flex-1 min-w-0 flex items-center gap-1.5">
                                        <span className="text-[13px] text-slate-700 truncate">{s.merchant}</span>
                                        {isFailed ? (
                                          <span className="text-[9px] px-1.5 py-0.5 bg-red-50 text-red-500 rounded font-bold border border-red-100 shrink-0">실패</span>
                                        ) : isTransferring ? (
                                          <span className="text-[9px] px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded font-bold border border-blue-100 shrink-0">지급 요청 등록</span>
                                        ) : (
                                          <span className="text-[9px] px-1.5 py-0.5 bg-[var(--brand-primary-soft)] text-[var(--brand-primary)] rounded font-bold border border-[var(--brand-primary-border)] shrink-0">예정</span>
                                        )}
                                      </div>
                                      <span className="text-[13px] font-bold text-slate-800 tabular-nums shrink-0">{s.settleAmount}원</span>
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); toggleHubExpand(expKey); }}
                                        className={`p-1.5 rounded-md border border-slate-200 shrink-0 transition-colors ${isExp ? 'bg-slate-200 text-slate-700' : 'bg-white text-slate-400 hover:text-slate-600'}`}
                                        aria-label={isExp ? '상세 접기' : '상세 펼치기'}
                                      >
                                        {isExp ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                      </button>
                                    </div>
                                  </div>
                                  <div className="px-3 pb-2 pl-9">
                                    <PayoutDeadlineLine settlement={s} />
                                  <ExternalSettlementLine settlement={s} />
                                  </div>
                                  {isExp && (
                                    <div className="bg-slate-50/90 border-t border-slate-100 px-3 py-2 pl-10">
                                      {isFailed && (
                                        <div className="mb-2 p-2.5 bg-red-50/80 border border-red-100 rounded-lg flex items-start gap-2">
                                          <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                                          <div>
                                            <p className="text-[10px] font-bold text-red-400 uppercase tracking-wide">실패 사유</p>
                                            <p className="text-[12px] text-red-600 font-semibold">{s.transferFailReason || '관리자문의'}</p>
                                          </div>
                                        </div>
                                      )}
                                      {isTransferring && (
                                        <div className="mb-2 rounded-lg border border-blue-100 bg-blue-50/80 p-2.5 text-[11px] font-semibold text-blue-600">
                                          {getPayoutRequestDetail(s) || '지급대행 요청이 등록되었습니다.'}
                                        </div>
                                      )}
                                      <div className="flex justify-between text-[10px] font-bold text-slate-400 uppercase tracking-tight border-b border-slate-100/80 pb-1.5 mb-1">
                                        <span>구분</span>
                                        <span className="pr-1">금액</span>
                                      </div>
                                      {details.map((d) => (
                                        <div key={d.id} className="flex justify-between text-[12px] py-1.5">
                                          <span className="text-slate-600">{d.title}</span>
                                          <span className="font-bold text-slate-800 tabular-nums">{Number(d.price).toLocaleString('ko-KR')}원</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            }
                            return (
                              <div
                                key={s.id}
                                className={`px-3 py-2 transition-colors ${!isSelectable ? 'cursor-not-allowed bg-slate-50 opacity-75' : isChecked ? 'bg-[var(--brand-primary-soft)]/40 cursor-pointer' : 'bg-white hover:bg-slate-50 cursor-pointer'}`}
                                onClick={() => {
                                  if (!isSelectable || !hasRealDepositRows) return;
                                  if (isChecked) {
                                    setSelectedIds(selectedIds.filter((id) => id !== s.id));
                                  } else {
                                    setSelectedIds([...selectedIds, s.id]);
                                  }
                                }}
                              >
                                <div className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    disabled={!hasRealDepositRows || !isSelectable}
                                    onChange={(e) => {
                                      if (!hasRealDepositRows || !isSelectable) return;
                                      e.stopPropagation();
                                      if (isChecked) {
                                        setSelectedIds(selectedIds.filter((id) => id !== s.id));
                                      } else {
                                        setSelectedIds([...selectedIds, s.id]);
                                      }
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    className="w-4 h-4 border-slate-300 rounded shrink-0 disabled:opacity-40 accent-[var(--brand-primary)]"
                                    style={{ accentColor: '#3b82f6' }}
                                  />
                                  <span className="flex-1 text-[13px] text-slate-700 truncate">{s.merchant}</span>
                                  <span className="text-[13px] font-bold text-slate-800 tabular-nums shrink-0">{s.settleAmount}원</span>
                                </div>
                                <PayoutDeadlineLine settlement={s} />
                                  <ExternalSettlementLine settlement={s} />
                                {isFailed && (
                                  <div className="mt-0.5 flex items-center justify-between text-[11px] text-red-500">
                                    <span className="font-semibold flex items-center gap-0.5">
                                      실패
                                      <span
                                        data-tooltip-id="settlement-deposit-fail-tooltip"
                                        data-tooltip-html="실패한 건은 시스템에서 입금 처리할 수 없습니다.<br/>중복출금 등 오류를 방지하기 위해 수동 입금이 필요합니다."
                                        onMouseEnter={(e) => { e.stopPropagation(); !showAllOpen && setHoveredTooltipId('settlement-deposit-fail'); }}
                                        onMouseLeave={(e) => { e.stopPropagation(); setHoveredTooltipId(null); }}
                                        className="inline-flex cursor-help text-red-500/80 hover:text-red-600 shrink-0"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <AlertCircle size={12} strokeWidth={2} />
                                      </span>
                                    </span>
                                    <span className="flex-1 ml-2 text-right truncate">{s.transferFailReason || '관리자문의'}</span>
                                  </div>
                                )}
                                {isTransferring && (
                                  <div className="mt-0.5 flex items-center justify-between gap-2 text-[11px] text-blue-600">
                                    <span className="shrink-0 font-semibold">지급 요청 등록</span>
                                    <span className="truncate text-right">{getPayoutRequestDetail(s) || '지급대행 요청이 등록되었습니다.'}</span>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  });
                })()
              )}
            </div>

            {/* 하단 고정: 건수/금액 + 버튼 (우측 정렬) */}
            <div className={externalSettlementMode ? 'hidden' : 'px-4 py-3 border-t border-slate-200 bg-white shrink-0'}>
              <div className="flex items-center justify-end gap-6">
                <div className="text-right">
                  <p className="text-[11px] text-slate-400">
                    {ADMIN_CP_PAYOUT_TENANT_ENABLED && kgInicisPayoutRows.length > 0
                      ? `선택 정산: ${selectedPayoutRows.length}건`
                      : `선택된 ${selectedIds.length}건 합계`}
                  </p>
                  <p className="text-[18px] font-black text-[var(--brand-primary)] tabular-nums flex items-center justify-end gap-0.5">
                    {ADMIN_CP_PAYOUT_TENANT_ENABLED && kgInicisPayoutRows.length > 0
                      ? selectedPayoutAmount
                      : (() => {
                          const selected = filteredSettlements.filter((s) => selectedIds.includes(s.id));
                          const total = selected.reduce((sum, s) => {
                            const num = s.settleAmountNum ?? getSettleFromApprove(parseAmount(s.settleAmount || s.approveAmount));
                            return sum + Number(num || 0);
                          }, 0);
                          return total.toLocaleString();
                        })()}원
                    <span
                      data-tooltip-id="settlement-deposit-total-tooltip"
                      data-tooltip-html="입금예정내역에서 선택한 건의 입금예정금액 합계입니다."
                      onMouseEnter={() => !showAllOpen && setHoveredTooltipId('settlement-deposit-total')}
                      onMouseLeave={() => setHoveredTooltipId(null)}
                      className="inline-flex cursor-help text-slate-400 hover:text-slate-600 shrink-0"
                    >
                      <HelpCircle size={16} strokeWidth={2} />
                    </span>
                  </p>
                  {ADMIN_CP_PAYOUT_TENANT_ENABLED && kgInicisPayoutRows.length > 0 && (
                    <>
                      <p className="mt-0.5 text-[11px] font-semibold text-slate-500">지급예정일: {selectedPayoutDateLabel}</p>
                      {kgBatchSelectionStarted && !kgBatchSelectionComplete && (
                        <p className="mt-0.5 text-[10px] font-semibold text-red-500">지급 가능 정산을 모두 선택해야 신청할 수 있습니다.</p>
                      )}
                    </>
                  )}
                </div>
                <span
                  data-tooltip-id="settlement-deposit-btn-tooltip"
                  data-tooltip-html={ADMIN_CP_PAYOUT_TENANT_ENABLED
                    ? '선택한 내역의 지급대행을 신청합니다.<br/>신청 상태는 입금 처리 내역에서 확인할 수 있습니다.'
                    : '선택한 내역을 가맹점 계좌로 입금합니다.<br/>입금 시 입금 완료 내역으로 이동되며, 취소할 수 없습니다.'}
                  onMouseEnter={() => !showAllOpen && setHoveredTooltipId('settlement-deposit-btn')}
                  onMouseLeave={() => setHoveredTooltipId(null)}
                  className="inline-flex shrink-0 cursor-help"
                >
                  <button
                    type="button"
                    disabled={ADMIN_CP_PAYOUT_TENANT_ENABLED && kgInicisPayoutRows.length > 0 ? !payoutActionEnabled : !selectedIds.length}
                    onClick={() => {
                      if (!selectedIds.length) {
                        showToast('입금할 항목을 선택해주세요.', 'error');
                        return;
                      }
                      setConfirmDepositOpen(true);
                    }}
                    className={`px-5 py-2.5 rounded-lg text-[13px] font-bold shrink-0 ${(ADMIN_CP_PAYOUT_TENANT_ENABLED && kgInicisPayoutRows.length > 0 ? payoutActionEnabled : selectedIds.length > 0)
                      ? 'bg-[var(--brand-primary)] text-white hover:bg-[var(--brand-primary-hover)]'
                      : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                      }`}
                  >
                    {ADMIN_CP_PAYOUT_TENANT_ENABLED && kgInicisPayoutRows.length > 0 ? '합계 지급 신청' : '입금 처리'}
                  </button>
                </span>
              </div>
            </div>
          </div>
          )}
        </div>

        {/* 입금완료 50% */}
        <div className="basis-3/5 min-w-0 flex flex-col min-h-0 overflow-hidden pt-0">
          <div className="flex-1 min-h-0 bg-white border border-slate-200 rounded-[6px] overflow-hidden flex flex-col shadow-[0_8px_20px_rgba(15,23,42,0.04)]">
            {/* 헤더 - 입금예정과 동일 구조 (border-b) */}
            <div className="px-4 py-3 border-b border-slate-200 flex flex-col md:flex-row md:items-center justify-between gap-4 shrink-0">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-[4px] bg-[var(--brand-primary-soft)] flex items-center justify-center text-[var(--brand-primary)] shrink-0">
                  <History size={18} />
                </div>
                <div>
                  <h3 className="text-[15px] font-bold text-slate-800 leading-tight flex items-center gap-1.5">
                    {externalSettlementMode ? '정산완료 내역' : ADMIN_CP_PAYOUT_TENANT_ENABLED ? '입금 처리 내역' : '입금 완료 내역'}
                    <span
                      data-tooltip-id="settlement-deposit-completed-tooltip"
                      data-tooltip-html={externalSettlementMode
                        ? '외부 정산 일정에 따라 완료 처리된 내역을 조회합니다.<br/>상태와 완료 사유를 함께 확인할 수 있습니다.'
                        : ADMIN_CP_PAYOUT_TENANT_ENABLED
                          ? '지급 요청 접수부터 입금 완료·실패까지 조회합니다.<br/>처리 기준일별 상태를 확인할 수 있습니다.'
                          : '가맹점 정산이 완료된 내역을 조회합니다.<br/>입금일 기준 가맹점별 내역을 확인할 수 있습니다.'}
                      onMouseEnter={() => !showAllOpen && setHoveredTooltipId('settlement-deposit-completed')}
                      onMouseLeave={() => setHoveredTooltipId(null)}
                      className="inline-flex cursor-help text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      <HelpCircle size={14} strokeWidth={2} />
                    </span>
                  </h3>
                  <p className="text-[11px] text-slate-400 font-medium leading-tight">
                    {externalSettlementMode ? '기간별 외부 정산 완료 내역을 조회합니다.' : '기간별 입금 처리 내역을 조회합니다.'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-wrap ml-auto">
                <div className="flex items-center gap-2">
                <div ref={depositMonthPickerRef} className="relative h-[36px] flex items-center gap-2 bg-white px-1 rounded-lg border border-[#e5e8eb] hover:border-[var(--brand-primary)]/30 transition-all">
                  <button
                    type="button"
                    onClick={() => changeDepositMonth(-1)}
                    className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-[#e5e8eb] text-[#4e5968] shrink-0"
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setDepositMonthPickerOpen((v) => !v)}
                    className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-[#e5e8eb] text-[#191f28]"
                  >
                    <Calendar size={16} className="text-[var(--brand-primary)] shrink-0" />
                    <span className="text-[14px] font-semibold tabular-nums">
                      {startDate.replace(/\./g, '-').slice(0, 7)}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => changeDepositMonth(1)}
                    className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-[#e5e8eb] text-[#4e5968] shrink-0"
                  >
                    <ChevronRight size={14} />
                  </button>
                  {depositMonthPickerOpen && (
                    <div className="absolute top-full left-0 mt-2 z-[1200] bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden">
                      <DatePicker
                        selected={startDate ? new Date(startDate.replace(/\./g, '-').slice(0, 7) + '-01') : new Date()}
                        onChange={handleDepositMonthSelect}
                        showMonthYearPicker
                        dateFormat="yyyy.MM"
                        renderMonthContent={(month) => String(month + 1).padStart(2, '0')}
                        inline
                        calendarClassName="deposit-month-picker"
                      />
                    </div>
                  )}
                </div>
                <div className="flex gap-1">
                  {['당월'].map((type) => (
                    <button
                      key={type}
                      data-tooltip-id="deposit-quick-period-tooltip"
                      data-tooltip-html={`현재 달의 ${ADMIN_CP_PAYOUT_TENANT_ENABLED ? '입금 처리' : '입금 완료'} 내역을 표시합니다.`}
                      onMouseEnter={() => setHoveredTooltipId('deposit-quick-period')}
                      onMouseLeave={() => setHoveredTooltipId(null)}
                      onClick={() => setQuickPeriod(type)}
                      className="px-2.5 py-1.5 bg-white border border-[#e5e8eb] rounded-lg text-[12px] font-bold text-[#4e5968] hover:bg-[#f2f4f6] leading-none h-[36px] flex items-center"
                    >
                      {type}
                    </button>
                  ))}
                </div>
                </div>
                <div className="h-4 w-px bg-gray-300 shrink-0" />
                <button
                  data-tooltip-id="deposit-excel-download-tooltip"
                  data-tooltip-html={`월별 ${ADMIN_CP_PAYOUT_TENANT_ENABLED ? '입금 처리' : '입금 완료'} 내역을 엑셀로 다운로드합니다.<br/>1번 시트: 일자별 합계, 2번 시트: 전체 세부 내역`}
                  onMouseEnter={() => setHoveredTooltipId('deposit-excel-download')}
                  onMouseLeave={() => setHoveredTooltipId(null)}
                  onClick={downloadDepositExcel}
                  disabled={depositExcelDownloading}
                  aria-busy={depositExcelDownloading}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#191f28] text-white rounded-lg font-bold text-[14px] hover:bg-black transition-colors shrink-0 h-[36px] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {depositExcelDownloading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                  {depositExcelDownloading ? '다운로드 중...' : '엑셀다운로드'}
                </button>
              </div>
            </div>

            {/* 콘텐츠 영역 */}
            <div className="flex-1 min-h-0 overflow-hidden flex flex-col p-3">
            {ADMIN_CP_PAYOUT_TENANT_ENABLED && (
              <PayoutBatchHistory
                batches={activePayoutBatches}
                expandedIds={hubExpandedIds}
                onToggle={toggleHubExpand}
                checkingIds={checkingPayoutBatchIds}
                onCheckResult={handlePayoutBatchResultCheck}
                settlementPaymentDates={payoutBatchSettlementPaymentDates}
              />
            )}
            {/* 입금완료 테이블 - thead 고정, tbody만 스크롤, 페이지 스크롤 없음, scrollbar-gutter으로 토글 시 좌우 이동 방지 */}
            <div className={`${standaloneDepositGroups.length === 0 && activePayoutBatches.length > 0 ? 'hidden' : ''} flex-1 min-h-[420px] overflow-y-auto overflow-x-hidden custom-scrollbar rounded-[4px] border border-slate-100 bg-white shadow-inner`} style={{ scrollbarGutter: 'stable' }}>
              <table className="w-full text-left">
                <thead className="bg-slate-50 sticky top-0 backdrop-blur-md z-10 border-b border-slate-100">
                  <tr>
                    <th className="w-10 px-3 py-2 text-center">
                      <span
                        data-tooltip-id="deposit-row-expand-tooltip"
                        data-tooltip-html={ADMIN_CP_PAYOUT_TENANT_ENABLED
                          ? '클릭하면 처리 기준일에 해당하는 세부내역을 확인할 수 있습니다.'
                          : '클릭하면 입금날짜에 해당하는 세부내역을 확인할 수 있습니다.'}
                        onMouseEnter={() => setHoveredTooltipId('deposit-row-expand')}
                        onMouseLeave={() => setHoveredTooltipId(null)}
                        className="inline-flex cursor-help text-slate-400 hover:text-slate-600 transition-colors"
                      >
                        <HelpCircle size={14} strokeWidth={2} />
                      </span>
                    </th>
                    <th className="min-w-[180px] px-3 py-2 text-[12px] font-bold text-slate-500 uppercase tracking-wider text-left">
                      {externalSettlementMode ? '정산완료일' : ADMIN_CP_PAYOUT_TENANT_ENABLED ? '처리 기준일' : '입금일자'}
                    </th>
                    {(ADMIN_CP_PAYOUT_TENANT_ENABLED || externalSettlementMode) && (
                      <th className="min-w-[150px] px-3 py-2 text-[12px] font-bold text-slate-500 uppercase tracking-wider text-left">상태</th>
                    )}
                    {/* <th className="w-[110px] px-3 py-2 text-[12px] font-bold text-slate-500 uppercase tracking-wider text-left">정산일자</th> */}
                    <th className="px-3 py-2 text-[12px] font-bold text-slate-500 uppercase tracking-wider text-right">건수</th>
                    <th className="px-3 py-2 text-[12px] font-bold text-slate-500 uppercase tracking-wider text-right min-w-[130px]">승인금액</th>
                    <th className="px-3 py-2 text-[12px] font-bold text-slate-500 uppercase tracking-wider text-right min-w-[120px]">수수료</th>
                    <th className="px-3 py-2 text-[12px] font-bold text-slate-500 uppercase tracking-wider text-right min-w-[130px]">정산금액</th>
                    <th className="px-3 py-2 text-[12px] font-bold text-slate-500 uppercase tracking-wider text-right min-w-[130px]">본사수익(준비중)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {standaloneDepositGroups.map((group) => (
                      <React.Fragment key={group.key}>
                        <tr
                          className={`transition-colors cursor-pointer ${expandedRow === group.key ? 'bg-[var(--brand-primary-soft-strong)]' : 'hover:bg-slate-50/50'}`}
                          onClick={() => toggleRow(group.key)}
                        >
                          <td
                            className={`px-3 py-2 text-center align-top border-l-4 ${expandedRow === group.key ? 'border-l-[var(--brand-primary)]' : 'border-l-transparent'}`}
                            data-tooltip-id="deposit-row-expand-tooltip"
                            data-tooltip-html={ADMIN_CP_PAYOUT_TENANT_ENABLED
                          ? '클릭하면 처리 기준일에 해당하는 세부내역을 확인할 수 있습니다.'
                          : '클릭하면 입금날짜에 해당하는 세부내역을 확인할 수 있습니다.'}
                            onMouseEnter={() => setHoveredTooltipId('deposit-row-expand')}
                            onMouseLeave={() => setHoveredTooltipId(null)}
                          >
                            {expandedRow === group.key ? <ChevronUp size={14} className="text-[var(--brand-primary)] mx-auto" /> : <ChevronDown size={14} className="text-slate-300 mx-auto" />}
                          </td>
                          <td className="px-3 py-2 text-[12px] text-slate-700 text-left tabular-nums">
                            <div className="flex items-center gap-2">
                              <span>{group.depositDate}</span>
                              {group.items.some((item) => completedPayoutBatchSettlementIds.has(String(item.id))) && (
                                <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[9px] font-black text-amber-700 ring-1 ring-inset ring-amber-200">합산 지급</span>
                              )}
                            </div>
                          </td>
                          {(ADMIN_CP_PAYOUT_TENANT_ENABLED || externalSettlementMode) && (
                            <td className="px-3 py-2 text-left">
                              <div className="flex flex-wrap gap-1">
                                {getDepositStatusSummaries(group.items).map((statusSummary) => (
                                  <DepositStatusBadge
                                    key={statusSummary.key}
                                    item={{
                                      status: statusSummary.key === 'TRANSFERRING_ACCEPTED' || statusSummary.key === 'TRANSFERRING_CONFIRMING' ? 'TRANSFERRING' : statusSummary.key,
                                      payoutStatus: statusSummary.key === 'TRANSFERRING_ACCEPTED' ? 'ACCEPTED' : '',
                                      externalSettlementMode,
                                      transferRef: group.items.some((item) => String(item.transferRef || '').toUpperCase() === 'EXTERNAL_SCHEDULED') ? 'EXTERNAL_SCHEDULED' : '',
                                    }}
                                    count={statusSummary.count}
                                  />
                                ))}
                              </div>
                            </td>
                          )}
                          {/* <td className="w-[110px] px-3 py-2 text-[12px] text-slate-700 text-left tabular-nums">{group.settleDate}</td> */}
                          <td className="px-3 py-2 text-[12px] text-slate-900 text-right tabular-nums">{group.count}건</td>
                          <td className="px-3 py-2 text-[12px] text-slate-800 text-right tabular-nums">{group.approveAmountNum.toLocaleString('ko-KR')}원</td>
                          <td className="px-3 py-2 text-[12px] text-red-500 text-right tabular-nums">{group.feeAmountNum.toLocaleString('ko-KR')}원</td>
                          <td className="px-3 py-2 text-[12px] text-slate-800 text-right tabular-nums">{group.settleAmountNum.toLocaleString('ko-KR')}원</td>
                          <td className="px-3 py-2 text-[12px] text-[var(--brand-primary-hover)] font-medium text-right tabular-nums">{group.adminFeeAmountNum.toLocaleString('ko-KR')}원</td>
                        </tr>
                        {expandedRow === group.key && (
                          <tr>
                            <td colSpan={(ADMIN_CP_PAYOUT_TENANT_ENABLED || externalSettlementMode) ? 8 : 7} className="px-1 py-1 pb-3 bg-transparent align-top border-l-4 border-l-[var(--brand-primary)]">
                              <div className="px-2.5 py-2 rounded-xl border-2 border-[var(--brand-primary-border)] bg-[var(--brand-primary-soft)]/30 shadow-sm ml-0">
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-2">
                                    <List size={14} className="text-[var(--brand-primary)] shrink-0" />
                                    <h4 className="text-xs font-semibold text-slate-800 uppercase tracking-wider">세부 입금 항목</h4>
                                  </div>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const dates = [...new Set(group.items.map((i) => (i.approveDate || '').replace(/\.$/, '')).filter(Boolean))].sort();
                                      const searchDate = dates.length === 0 ? '' : dates.length === 1 ? dates[0] : `${dates[0]}~${dates[dates.length - 1]}`;
                                      navigate('/admin/payments', {
                                        state: { searchDate }
                                      });
                                    }}
                                    className="flex items-center gap-1 text-[12px] font-medium text-[var(--brand-primary)] hover:text-[var(--brand-primary-hover)]"
                                  >
                                    결제내역으로 이동
                                    <ChevronRight size={14} />
                                  </button>
                                </div>
                                <div className="space-y-2">
                                  <div className={`grid ${(ADMIN_CP_PAYOUT_TENANT_ENABLED || externalSettlementMode) ? 'grid-cols-8' : 'grid-cols-7'} text-[12px] font-bold text-slate-400 uppercase px-2 pb-2 border-b border-slate-100 gap-2`}>
                                    <span>가맹점명</span>
                                    {(ADMIN_CP_PAYOUT_TENANT_ENABLED || externalSettlementMode) && <span className="text-left">상태</span>}
                                    <span className="text-left">결제일자</span>
                                    <span className="text-right">승인금액</span>
                                    <span className="text-right">수수료</span>
                                    <span className="text-right">수수료율</span>
                                    <span className="text-right">정산금액</span>
                                    <span className="text-right">본사수익(준비중)</span>
                                  </div>
                                  {group.items.map((item, idx) => {
                                    const approveNum = item.approveAmountNum ?? parseAmount(item.approveAmount);
                                    const feeNum = item.feeAmountNum ?? parseAmount(item.feeAmount ?? '0');
                                    const feeRateRaw = (item.feeRate != null && item.feeRate !== '' && Number(item.feeRate) >= 0)
                                      ? Number(item.feeRate)
                                      : (approveNum > 0 ? (feeNum / approveNum) * 100 : 0);
                                    const feeRate = String(feeRateRaw).replace(/\.?0+$/, '');
                                    return (
                                      <div key={item.id || idx} className={`grid ${(ADMIN_CP_PAYOUT_TENANT_ENABLED || externalSettlementMode) ? 'grid-cols-8' : 'grid-cols-7'} text-[12px] py-2 pt-0 px-2 rounded-[4px] gap-2`}>
                                        <span className="flex min-w-0 items-center gap-1.5 text-slate-700">
                                          <span className="truncate">{item.merchant}</span>
                                          {completedPayoutBatchSettlementIds.has(String(item.id)) && (
                                            <span className="shrink-0 rounded bg-amber-50 px-1.5 py-0.5 text-[9px] font-black text-amber-700 ring-1 ring-inset ring-amber-200">합산 지급</span>
                                          )}
                                        </span>
                                        {(ADMIN_CP_PAYOUT_TENANT_ENABLED || externalSettlementMode) && (
                                          <DepositStatusBadge item={item} showDetail />
                                        )}
                                        <span className="text-left text-slate-600 tabular-nums">{item.approveDate ?? '-'}</span>
                                        <span className="text-right text-slate-600 tabular-nums">{item.approveAmount ?? '0'}원</span>
                                        <span className="text-right text-red-500 tabular-nums">{item.feeAmount ?? '0'}원</span>
                                        <span className="text-right text-slate-500 tabular-nums">{feeRate}%</span>
                                        <span className="text-right text-slate-600 tabular-nums">{item.settleAmount ?? item.depositAmount ?? '0'}원</span>
                                        <span className="text-right text-[var(--brand-primary)] font-medium tabular-nums">{item.adminFeeAmount ?? '0'}원</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                </tbody>
              </table>
              {standaloneDepositGroups.length === 0 && activePayoutBatches.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                  <AlertCircle size={40} className="mb-2 opacity-20" />
                  <p className="text-sm font-medium">조회된 {externalSettlementMode ? '정산완료' : ADMIN_CP_PAYOUT_TENANT_ENABLED ? '입금 처리' : '입금 완료'} 내역이 없습니다.</p>
                </div>
              )}
            </div>
            </div>

            {/* 하단 고정 - 기간, 승인금액 합계, 수수료 합계, 정산금액 합계, 입금금액 합계 5칸 */}
            <div className="border-t border-slate-200 bg-white shrink-0">
              <div className="grid divide-x divide-slate-200" style={{ gridTemplateColumns: '1.2fr 1fr 1fr 1fr 1fr' }}>
                <div className="px-4 py-3 flex flex-col items-end justify-center">
                  <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                    기간
                    <span
                      data-tooltip-id="deposit-stat-period-tooltip"
                      data-tooltip-html="월별 조회내역입니다."
                      onMouseEnter={() => !showAllOpen && setHoveredTooltipId('deposit-stat-period')}
                      onMouseLeave={() => setHoveredTooltipId(null)}
                      className="inline-flex cursor-help text-slate-400 hover:text-slate-600"
                    >
                      <HelpCircle size={12} strokeWidth={2} />
                    </span>
                  </p>
                  <p className="text-[18px] font-black text-slate-800 tabular-nums">{depositPeriodLabel}</p>
                </div>
                <div className="px-4 py-3 flex flex-col items-end justify-center">
                  <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                    승인금액 합계
                    <span
                      data-tooltip-id="deposit-stat-approve-tooltip"
                      data-tooltip-html="월별 승인금액의 합계입니다."
                      onMouseEnter={() => !showAllOpen && setHoveredTooltipId('deposit-stat-approve')}
                      onMouseLeave={() => setHoveredTooltipId(null)}
                      className="inline-flex cursor-help text-slate-400 hover:text-slate-600"
                    >
                      <HelpCircle size={12} strokeWidth={2} />
                    </span>
                  </p>
                  <p className="text-[18px] font-black text-slate-900 tabular-nums">{stats.approveFormatted}원</p>
                </div>
                <div className="px-4 py-3 flex flex-col items-end justify-center">
                  <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                    수수료 합계
                    <span
                      data-tooltip-id="deposit-stat-fee-tooltip"
                      data-tooltip-html="월별 가맹점 수수료의 합계입니다."
                      onMouseEnter={() => !showAllOpen && setHoveredTooltipId('deposit-stat-fee')}
                      onMouseLeave={() => setHoveredTooltipId(null)}
                      className="inline-flex cursor-help text-slate-400 hover:text-slate-600"
                    >
                      <HelpCircle size={12} strokeWidth={2} />
                    </span>
                  </p>
                  <p className="text-[18px] font-black text-red-500 tabular-nums">{stats.salesFormatted}원</p>
                </div>
                <div className="px-4 py-3 flex flex-col items-end justify-center">
                  <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                    정산금액 합계
                    <span
                      data-tooltip-id="deposit-stat-settle-tooltip"
                      data-tooltip-html="월별 가맹점 정산금액의 합계입니다."
                      onMouseEnter={() => !showAllOpen && setHoveredTooltipId('deposit-stat-settle')}
                      onMouseLeave={() => setHoveredTooltipId(null)}
                      className="inline-flex cursor-help text-slate-400 hover:text-slate-600"
                    >
                      <HelpCircle size={12} strokeWidth={2} />
                    </span>
                  </p>
                  <p className="text-[18px] font-black text-slate-500 tabular-nums">{stats.settleFormatted}원</p>
                </div>
                <div className="px-4 py-3 flex flex-col items-end justify-center">
                  <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                    본사수익 합계(준비중)
                    <span
                      data-tooltip-id="deposit-stat-deposit-tooltip"
                      data-tooltip-html="월별 본사수익의 합계입니다."
                      onMouseEnter={() => !showAllOpen && setHoveredTooltipId('deposit-stat-deposit')}
                      onMouseLeave={() => setHoveredTooltipId(null)}
                      className="inline-flex cursor-help text-slate-400 hover:text-slate-600"
                    >
                      <HelpCircle size={12} strokeWidth={2} />
                    </span>
                  </p>
                  <p className="text-[18px] font-black text-[var(--brand-primary-hover)] tabular-nums">{stats.adminFeeFormatted ?? '0'}원</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 확정 모달 */}
      {isSettlementModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white w-[700px] rounded shadow-2xl overflow-hidden flex flex-col border border-[#e5e8eb]">
            <div className="px-10 py-8 flex items-center justify-between border-b border-[#f2f4f6]">
              <h2 className="text-[28px] font-black text-[#191f28]">정산예정목록</h2>
              <button
                onClick={() => {
                  setIsSettlementModalOpen(false);
                  setSelectedSettlementIds([]);
                  setSettlementOtpToken('');
                }}
                className="px-5 py-2 bg-[#e5e8eb] text-[#4e5968] rounded-xl font-bold text-[15px] hover:bg-[#d1d6db]"
              >
                닫기
              </button>
            </div>
            {/* <div className="px-8 pt-4 pb-3">
              <div className="flex items-center justify-between">
                <span className="text-[17px] font-extrabold text-[#f97316]">
                  정산예정 ({filteredPendingSettlements.length}건)
                </span>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => {
                      const allIds = filteredPendingSettlements.map((s) => s.id);
                      setSelectedSettlementIds(allIds);
                    }}
                    className="text-[14px] font-bold text-[#fb923c] hover:text-[#ea580c]"
                  >
                    전체선택
                  </button>
                  <button
                    onClick={() => setSelectedSettlementIds([])}
                    className="text-[14px] font-bold text-[#8b95a1] hover:text-[#4e5968]"
                  >
                    전체해제
                  </button>
                </div>
              </div>
            </div> */}
            <div className="flex-1 py-5 px-8 overflow-y-auto custom-scrollbar min-h-[320px] max-h-[480px]">
              <table className="w-full text-sm table-fixed border-t border-[#e5e8eb]">
                <colgroup>
                  <col className="w-10" />
                  <col className="w-[30%]" />
                  <col className="w-[20%]" />
                  <col className="w-[20%]" />
                  <col className="w-[25%]" />
                </colgroup>
                <thead>
                  <tr className="bg-[#f9fafb] text-[#8b95a1] border-b border-[#e5e8eb]">
                    <th className="py-2 pl-3 text-left text-[12px] font-bold">
                      <input
                        type="checkbox"
                        checked={allPendingSelected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            const allIds = filteredPendingSettlements.map((s) => s.id);
                            setSelectedSettlementIds(allIds);
                          } else {
                            setSelectedSettlementIds([]);
                          }
                        }}
                        className="w-5 h-5 border-[#fdba74] rounded focus:ring-[#fb923c] accent-[#f96600]"
                        style={{ accentColor: '#f96600' }}
                      />
                    </th>
                    <th className="py-2 text-left text-[12px] font-bold">가맹점명</th>
                    <th className="py-2 text-left text-[12px] font-bold">결제일</th>
                    <th className="py-2 text-left text-[12px] font-bold text-orange-600">정산예정일</th>
                    <th className="py-2 text-right text-[12px] font-bold pr-2">정산예정금액</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPendingSettlements.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-10 text-center text-[14px] text-[#8b95a1]">
                        정산예정 내역이 없습니다.
                      </td>
                    </tr>
                  ) : (
                    <>
                      {filteredPendingSettlements.map((s) => (
                        <tr
                          key={s.id}
                          className="border-b border-[#f2f4f6] hover:bg-[#f9fafb] cursor-pointer"
                          onClick={() => {
                            if (selectedSettlementIds.includes(s.id)) {
                              setSelectedSettlementIds(selectedSettlementIds.filter((id) => id !== s.id));
                            } else {
                              setSelectedSettlementIds([...selectedSettlementIds, s.id]);
                            }
                          }}
                        >
                          <td className="py-2 pl-3 align-middle">
                            <input
                              type="checkbox"
                              checked={selectedSettlementIds.includes(s.id)}
                              onChange={(e) => {
                                e.stopPropagation();
                                if (selectedSettlementIds.includes(s.id)) {
                                  setSelectedSettlementIds(selectedSettlementIds.filter((id) => id !== s.id));
                                } else {
                                  setSelectedSettlementIds([...selectedSettlementIds, s.id]);
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="w-5 h-5 border-[#fdba74] rounded focus:ring-[#fb923c] accent-[#f96600]"
                              style={{ accentColor: '#f96600' }}
                            />
                          </td>
                          <td className="py-2 align-middle">
                            <div className="flex flex-col">
                              <span className="font-bold text-[14px] text-[#191f28]">{s.merchant}</span>
                            </div>
                          </td>
                          <td className="py-2 align-middle text-[13px] text-[#4e5968]">
                            {s.approveDate || s.settleDate}
                          </td>
                          <td className="py-2 align-middle text-[13px] font-bold text-orange-600 tabular-nums">
                            {s.settleDate || "-"}
                          </td>
                          <td className="py-2 align-middle pr-2 text-right text-[14px] font-bold text-[#191f28]">
                            {s.settleAmount}원
                          </td>
                        </tr>
                      ))}
                      {(() => {
                        const selected = filteredPendingSettlements.filter((s) =>
                          selectedSettlementIds.includes(s.id)
                        );
                        const base = selectedSettlementIds.length > 0 ? selected : [];
                        const count = base.length;
                        const total = base.reduce((sum, s) => {
                          const num =
                            s.settleAmountNum ??
                            getSettleFromApprove(parseAmount(s.approveAmount));
                          return sum + Number(num || 0);
                        }, 0);
                        return (
                          <tr className="bg-[#fff7ed] font-bold text-[#f97316]">
                            <td className="py-2 pl-3 text-[13px]">합계</td>
                            <td className="py-2 text-[13px]" colSpan={3}>
                              {count}건
                            </td>
                            <td className="py-2 pr-2 text-right text-[14px]">
                              {total.toLocaleString()}원
                            </td>
                          </tr>
                        );
                      })()}
                    </>
                  )}
                </tbody>
              </table>
            </div>
            <div className="px-10 py-4 bg-[#f9fafb] border-t border-[#f2f4f6]">
              <div className="flex flex-col items-center gap-4">
                {/* OTP 토큰 입력 필드 (주석처리) */}
                {/* <div className="w-full max-w-[400px]">
                  <label className="block text-[14px] font-bold text-[#4e5968] mb-2">OTP 토큰</label>
                  <input
                    type="text"
                    value={settlementOtpToken}
                    onChange={(e) => setSettlementOtpToken(e.target.value)}
                    placeholder="OTP 토큰을 입력하세요"
                    className="w-full px-4 py-2.5 border border-[#d1d6db] rounded-lg text-[15px] focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] focus:border-transparent"
                  />
                </div> */}
                <button
                  onClick={async () => {
                    if (!selectedSettlementIds.length) {
                      showToast('확정할 항목을 선택해주세요.', 'error');
                      return;
                    }
                    // OTP 토큰 검증 (주석처리)
                    // if (!settlementOtpToken.trim()) {
                    //   showToast('OTP 토큰을 입력해주세요.');
                    //   return;
                    // }
                    const baseUrl = process.env.REACT_APP_API || '';
                    try {
                      await axios.post(
                        baseUrl + '/api/admin/cp-settlement/confirm',
                        { ids: selectedSettlementIds }, // otpToken: settlementOtpToken.trim() 제거
                        { withCredentials: true },
                      );
                      showToast('정산확정 처리가 완료되었습니다.');
                      setIsSettlementModalOpen(false);
                      setSelectedSettlementIds([]);
                      setSelectedIds((prev) => prev.filter((id) => !selectedSettlementIds.includes(id))); // 확정된 항목들을 selectedIds에서도 제거
                      setSettlementOtpToken('');
                      setReloadToken((t) => t + 1);
                    } catch (e) {
                      showToast(
                        e?.response?.data?.msg ||
                        '정산확정 처리 중 오류가 발생했습니다.',
                        'error'
                      );
                    }
                  }}
                  className={`w-[300px] py-4 rounded-2xl font-black text-[20px] shadow-lg transition-all ${selectedSettlementIds.length > 0 // && settlementOtpToken.trim() 제거
                    ? 'bg-[#f97316] text-white hover:bg-[#ea580c]'
                    : 'bg-[#fee2e2] text-[#fb923c]/60 cursor-not-allowed'
                    }`}
                >
                  정산 확정
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 입금 모달 */}
      {isDepositModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white w-[820px] rounded shadow-2xl overflow-hidden flex flex-col border border-[#e5e8eb]">
            <div className="px-10 py-8 flex items-center justify-between border-b border-[#f2f4f6]">
              <h2 className="text-[28px] font-black text-[#191f28]">입금대기목록</h2>
              <button
                onClick={() => {
                  setIsDepositModalOpen(false);
                  setSelectedIds([]);
                  setDepositOtpToken('');
                }}
                className="px-5 py-2 bg-[#e5e8eb] text-[#4e5968] rounded-xl font-bold text-[15px] hover:bg-[#d1d6db]"
              >
                닫기
              </button>
            </div>
            {/* <div className="px-8 py-5 border-b border-[#f2f4f6] bg-[#fcfdfe]">
              <div className="flex items-center justify-between">
                <span className="text-[17px] font-extrabold text-[var(--brand-primary)]">
                  정산확정 ({filteredSettlements.length}건)
                </span>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => {
                      if (!hasRealDepositRows) return;
                      const allIds = filteredSettlements.map((s) => s.id);
                      setSelectedIds(allIds);
                    }}
                    className="text-[14px] font-bold text-[var(--brand-primary)] hover:text-[var(--brand-primary-hover)]"
                  >
                    전체선택
                  </button>
                  <button
                    onClick={() => setSelectedIds([])}
                    className="text-[14px] font-bold text-[#8b95a1] hover:text-[#4e5968]"
                  >
                    전체해제
                  </button>
                </div>
              </div>
            </div> */}
            <div className="flex-1 py-5 px-8 overflow-y-auto custom-scrollbar min-h-[320px] max-h-[480px]">
              <table className="w-full text-sm table-fixed border-t border-[#e5e8eb]">
                <colgroup>
                  <col className="w-10" />
                  <col className="w-[26%]" />
                  <col className="w-[18%]" />
                  <col className="w-[18%]" />
                  <col className="w-[18%]" />
                  <col className="w-[20%]" />
                </colgroup>
                <thead>
                  <tr className="bg-[#f9fafb] text-[#8b95a1] border-b border-[#e5e8eb]">
                    <th className="py-2 pl-3 text-left text-[12px] font-bold">
                      <input
                        type="checkbox"
                        checked={allDepositSelected}
                        onChange={(e) => {
                          if (!hasRealDepositRows) return;
                          if (e.target.checked) {
                            const allIds = selectableDepositRows.map((s) => s.id);
                            setSelectedIds(allIds);
                          } else {
                            setSelectedIds([]);
                          }
                        }}
                        className="w-5 h-5 border-[#cbd5f5] rounded focus:ring-[var(--brand-primary-disabled)] accent-[var(--brand-primary)]"
                        style={{ accentColor: 'var(--brand-primary)' }}
                      />
                    </th>
                    <th className="py-2 text-left text-[12px] font-bold">가맹점명</th>
                    <th className="py-2 text-left text-[12px] font-bold">정산확정일</th>
                    <th className="py-2 text-left text-[12px] font-bold">결제일</th>
                    <th className="py-2 text-right text-[12px] font-bold pr-1">정산금액</th>
                    <th className="py-2 text-right text-[12px] font-bold pr-2">입금예정금액</th>
                  </tr>
                </thead>
                <tbody>
                  {depositModalRows.map((s) => {
                    const sSelectable = !externalSettlementMode && isPayoutSelectable(s);
                    const sTransferring = isPayoutTransferring(s);
                    return (
                    <tr
                      key={s.id}
                      className={`border-b border-[#f2f4f6] ${!sSelectable ? 'bg-slate-50 opacity-75 cursor-not-allowed' : 'hover:bg-[#f9fafb] cursor-pointer'}`}
                      onClick={() => {
                        if (!sSelectable || !hasRealDepositRows) return;
                        if (selectedIds.includes(s.id)) {
                          setSelectedIds(selectedIds.filter((id) => id !== s.id));
                        } else {
                          setSelectedIds([...selectedIds, s.id]);
                        }
                      }}
                    >
                      <td className="py-2 pl-3 align-middle">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(s.id)}
                          disabled={!hasRealDepositRows || !sSelectable}
                          onChange={(e) => {
                            if (!hasRealDepositRows || !sSelectable) return;
                            e.stopPropagation();
                            if (selectedIds.includes(s.id)) {
                              setSelectedIds(selectedIds.filter((id) => id !== s.id));
                            } else {
                              setSelectedIds([...selectedIds, s.id]);
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="w-5 h-5 border-[#cbd5f5] rounded focus:ring-[var(--brand-primary-disabled)] accent-[var(--brand-primary)] disabled:opacity-40"
                          style={{ accentColor: 'var(--brand-primary)' }}
                        />
                      </td>
                      <td className="py-2 align-middle">
                        <div className="flex w-full flex-col items-start gap-0.5">
                          <span className="font-bold text-[14px] text-[#191f28]">{s.merchant}</span>
                          {sTransferring && (
                            <span className="text-[10px] font-semibold text-blue-600">지급 요청 등록 · {getPayoutRequestDetail(s) || '처리 중'}</span>
                          )}
                          <PayoutDeadlineLine settlement={s} />
                                  <ExternalSettlementLine settlement={s} />
                        </div>
                      </td>
                      <td className="py-2 align-middle text-[13px] text-[#4e5968]">
                        {s.settleDate || '-'}
                      </td>
                      <td className="py-2 align-middle text-[13px] text-[#4e5968]">
                        {s.approveDate || '-'}
                      </td>
                      <td className="py-2 align-middle pr-1 text-right text-[14px] font-bold text-[#191f28]">
                        {s.settleAmount}원
                      </td>
                      <td className="py-2 align-middle pr-2 text-right text-[14px] font-bold text-[var(--brand-primary)]">
                        {(s.depositAmount || s.settleAmount) + '원'}
                      </td>
                    </tr>
                    );
                  })}
                  {depositModalRows.length > 0 &&
                    (() => {
                      const selected = depositModalRows.filter((s) => selectedIds.includes(s.id));
                      const base = selectedIds.length > 0 ? selected : [];
                      const count = base.length;
                      const totalSettle = base.reduce((sum, s) => {
                        const num =
                          s.settleAmountNum ??
                          getSettleFromApprove(parseAmount(s.settleAmount || s.approveAmount));
                        return sum + Number(num || 0);
                      }, 0);
                      const totalDeposit = base.reduce((sum, s) => {
                        const num =
                          s.depositAmountNum ??
                          s.settleAmountNum ??
                          getSettleFromApprove(parseAmount(s.settleAmount || s.approveAmount));
                        return sum + Number(num || 0);
                      }, 0);
                      return (
                        <tr className="bg-[#eff6ff] font-bold">
                          <td className="py-2 pl-3 text-[13px] text-[#1d4ed8]">합계</td>
                          <td className="py-2 text-[13px] text-[#1f2937]">{count}건</td>
                          <td className="py-2 text-[13px]" colSpan={2}></td>
                          <td className="py-2 pr-1 text-right text-[14px] text-[#111827]">
                            {totalSettle.toLocaleString()}원
                          </td>
                          <td className="py-2 pr-2 text-right text-[14px] text-[#1d4ed8]">
                            {totalDeposit.toLocaleString()}원
                          </td>
                        </tr>
                      );
                    })()}
                </tbody>
              </table>
            </div>
            <div className="px-10 py-4 bg-[#f9fafb] border-t border-[#f2f4f6]">
              <div className="flex flex-col items-center gap-4">
                {/* OTP 토큰 입력 필드 (주석처리) */}
                {/* <div className="w-full max-w-[400px]">
                  <label className="block text-[14px] font-bold text-[#4e5968] mb-2">OTP 토큰</label>
                  <input
                    type="text"
                    value={depositOtpToken}
                    onChange={(e) => setDepositOtpToken(e.target.value)}
                    placeholder="OTP 토큰을 입력하세요"
                    className="w-full px-4 py-2.5 border border-[#d1d6db] rounded-lg text-[15px] focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary-hover)] focus:border-transparent"
                  />
                </div> */}
                <button
                  onClick={handlePayoutBatchRequest}
                  disabled={!payoutActionEnabled}
                  className={`w-[300px] py-4 rounded-2xl font-black text-[20px] shadow-lg transition-all ${payoutActionEnabled
                    ? 'bg-[var(--brand-primary)] text-white hover:bg-[var(--brand-primary-hover)]'
                    : 'bg-[#e5e8eb] text-[#adb5bd] cursor-not-allowed'
                    }`}
                >
                  {payoutSubmitting ? '신청 중...' : ADMIN_CP_PAYOUT_TENANT_ENABLED && kgInicisPayoutRows.length > 0 ? '합계 지급 신청' : '입금 승인'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 정산확정 안내 모달 */}
      {confirmSettlementOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white w-[420px] rounded-2xl shadow-2xl overflow-hidden border border-[#e5e8eb]">
            <div className="px-6 py-5 border-b border-[#f2f4f6]">
              <h2 className="text-[20px] font-black text-[#191f28]">정산 확정</h2>
            </div>
            <div className="px-6 py-5 text-[14px] text-[#4e5968] whitespace-pre-line">
              {selectedSettlementIds.length > 0
                ? `선택된 ${selectedSettlementIds.length}건을 정산 확정 처리하시겠습니까?\n확정 후 목록은 입금예정에서 확인할 수 있습니다.`
                : '정산 확정할 항목이 선택되어 있지 않습니다.'}
            </div>
            <div className="px-6 py-4 bg-[#f9fafb] border-t border-[#f2f4f6] flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmSettlementOpen(false)}
                className="px-4 py-2 rounded-lg text-[14px] font-bold text-[#4e5968] bg-white border border-[#e5e8eb] hover:bg-[#f2f4f6]"
              >
                취소
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (!selectedSettlementIds.length) {
                    showToast('확정할 항목을 선택해주세요.', 'error');
                    setConfirmSettlementOpen(false);
                    return;
                  }
                  const baseUrl = process.env.REACT_APP_API || '';
                  try {
                    await axios.post(
                      baseUrl + '/api/admin/cp-settlement/confirm',
                      { ids: selectedSettlementIds },
                      { withCredentials: true },
                    );
                    showToast('정산확정 처리가 완료되었습니다.');
                    setConfirmSettlementOpen(false);
                    setSelectedSettlementIds([]);
                    setSelectedIds((prev) =>
                      prev.filter((id) => !selectedSettlementIds.includes(id))
                    );
                    setReloadToken((t) => t + 1);
                  } catch (e) {
                    showToast(
                      e?.response?.data?.msg ||
                      '정산확정 처리 중 오류가 발생했습니다.',
                      'error'
                    );
                  }
                }}
                className={`px-4 py-2 rounded-lg text-[14px] font-bold text-white ${selectedSettlementIds.length > 0
                  ? 'bg-[#f97316] hover:bg-[#ea580c]'
                  : 'bg-[#fdba74] cursor-not-allowed'
                  }`}
                disabled={!selectedSettlementIds.length}
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 입금 승인 안내 모달 */}
      {confirmDepositOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white w-[420px] rounded-2xl shadow-2xl overflow-hidden border border-[#e5e8eb]">
            <div className="px-6 py-5 border-b border-[#f2f4f6]">
              <h2 className="text-[20px] font-black text-[#191f28]">
                {ADMIN_CP_PAYOUT_TENANT_ENABLED && kgInicisPayoutRows.length > 0 ? '합계 지급 신청' : '입금 승인'}
              </h2>
            </div>
            <div className="px-6 py-5 text-[14px] text-[#4e5968] whitespace-pre-line">
              {selectedIds.length > 0
                ? ADMIN_CP_PAYOUT_TENANT_ENABLED && kgInicisPayoutRows.length > 0
                  ? `선택 정산: ${selectedPayoutRows.length}건\n합계 지급액: ${selectedPayoutAmount}원\n지급예정일: ${selectedPayoutDateLabel}\n\nKG이니시스 합산 지급을 신청하시겠습니까?\nACCEPTED는 입금 완료가 아닌 지급 신청 접수 상태입니다.`
                  : `선택된 ${selectedIds.length}건을 입금 승인 처리하시겠습니까?\n입금 후 목록은 입금완료내역에서 확인할 수 있습니다.`
                : '입금 승인할 항목이 선택되어 있지 않습니다.'}
            </div>
            <div className="px-6 py-4 bg-[#f9fafb] border-t border-[#f2f4f6] flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmDepositOpen(false)}
                className="px-4 py-2 rounded-lg text-[14px] font-bold text-[#4e5968] bg-white border border-[#e5e8eb] hover:bg-[#f2f4f6]"
              >
                취소
              </button>
              <button
                type="button"
                onClick={handlePayoutBatchRequest}
                className={`px-4 py-2 rounded-lg text-[14px] font-bold text-white ${payoutActionEnabled
                  ? 'bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)]'
                  : 'bg-[#9cbcff] cursor-not-allowed'
                  }`}
                disabled={!payoutActionEnabled}
              >
                {payoutSubmitting ? '신청 중...' : ADMIN_CP_PAYOUT_TENANT_ENABLED && kgInicisPayoutRows.length > 0 ? '합계 지급 신청' : '확인'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 지급 신청 그룹별 결과 */}
      {payoutBatchResult && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="flex max-h-[80vh] w-[620px] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
            <div className="border-b border-slate-100 px-6 py-5">
              <h2 className="text-[20px] font-black text-slate-900">합계 지급 신청 결과</h2>
              <p className="mt-1 text-[12px] text-slate-500">
                ACCEPTED는 입금 완료가 아니라 KG이니시스 지급 신청 접수 상태입니다.
              </p>
            </div>
            <div className="grid grid-cols-5 divide-x divide-slate-100 border-b border-slate-100 bg-slate-50">
              {[
                ['접수', payoutBatchResult.acceptedCount, 'text-amber-700'],
                ['완료', payoutBatchResult.successCount, 'text-green-700'],
                ['확인 필요', payoutBatchResult.unknownCount, 'text-orange-700'],
                ['실패', payoutBatchResult.failedCount, 'text-red-700'],
                ['제외', payoutBatchResult.skippedCount, 'text-slate-600'],
              ].map(([label, value, color]) => (
                <div key={label} className="px-3 py-3 text-center">
                  <p className="text-[10px] font-semibold text-slate-500">{label}</p>
                  <p className={`text-[17px] font-black ${color}`}>{value}건</p>
                </div>
              ))}
            </div>
            <div className="flex-1 space-y-2 overflow-y-auto px-5 py-4 custom-scrollbar">
              {payoutBatchResult.rows.map((row, index) => {
                const payoutBatch = normalizePayoutBatch(row?.payoutBatch || row?.payout_batch || {}, index);
                const statusMeta = getPayoutBatchStatus(row?.status || payoutBatch.status);
                const settlementIds = Array.isArray(row?.settlementIds)
                  ? row.settlementIds
                  : Array.isArray(row?.settlement_ids)
                    ? row.settlement_ids
                    : [];
                const message = row?.message || payoutBatch.providerMessage || '';
                return (
                  <div key={String(payoutBatch.batchId || index)} className="rounded-lg border border-slate-200 px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[13px] font-bold text-slate-800">
                          {payoutBatch.cpName || payoutBatch.idMall || `지급 그룹 ${index + 1}`}
                        </p>
                        <p className="mt-0.5 text-[10px] text-slate-500">
                          지급일 {formatPayoutBatchDate(payoutBatch.payDate)} · 정산 {settlementIds.length}건
                        </p>
                      </div>
                      <span className="text-[13px] font-black tabular-nums text-slate-900">
                        {formatPayoutAmount(payoutBatch.totalAmount)}원
                      </span>
                      <span className={`rounded-full border px-2 py-1 text-[10px] font-bold ${statusMeta.className}`}>
                        {statusMeta.label}
                      </span>
                    </div>
                    {message && <p className="mt-2 text-[11px] text-slate-600">{message}</p>}
                  </div>
                );
              })}
              {payoutBatchResult.rows.length === 0 && (
                <p className="py-8 text-center text-[12px] text-slate-400">{payoutBatchResult.message || '그룹별 결과가 없습니다.'}</p>
              )}
            </div>
            <div className="flex justify-end border-t border-slate-100 bg-slate-50 px-5 py-4">
              <button
                type="button"
                onClick={() => setPayoutBatchResult(null)}
                className="rounded-lg bg-slate-900 px-5 py-2 text-[13px] font-bold text-white hover:bg-black"
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Settlement;
